import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import { usePrintSettings } from "@/contexts/PrintSettingsContext";
import { compareByMonthYear } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  BookOpen,
  Calendar,
  ArrowLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Minus,
  DollarSign,
  AlertTriangle,
  Filter,
  X,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  CheckCircle,
  Printer,
  Edit,
  Trash2,
  List,
  Download,
  FileSpreadsheet,
  FileText,
  Package,
  Loader2,
  Users,
  Building2,
  UserCheck,
  Briefcase,
  Upload,
  Eye,
  CheckSquare,
  Square,
  CreditCard,
  Wallet,
  BarChart3,
  LayoutGrid,
  RotateCcw,
  Clock,
  Pencil,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY, toISO } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Deterministic color palette for advance-utilization rows. All parcels under
// the SAME advance_ref pick the SAME color from this palette so finance can
// visually trace which rows belong to a single customer advance.
const ADVANCE_ROW_PALETTE = [
  { row: 'bg-pink-50 border-pink-200',     chip: 'bg-pink-100 text-pink-700 border-pink-300' },
  { row: 'bg-sky-50 border-sky-200',       chip: 'bg-sky-100 text-sky-700 border-sky-300' },
  { row: 'bg-violet-50 border-violet-200', chip: 'bg-violet-100 text-violet-700 border-violet-300' },
  { row: 'bg-amber-50 border-amber-200',   chip: 'bg-amber-100 text-amber-700 border-amber-300' },
  { row: 'bg-teal-50 border-teal-200',     chip: 'bg-teal-100 text-teal-700 border-teal-300' },
  { row: 'bg-rose-50 border-rose-200',     chip: 'bg-rose-100 text-rose-700 border-rose-300' },
  { row: 'bg-lime-50 border-lime-200',     chip: 'bg-lime-100 text-lime-700 border-lime-300' },
  { row: 'bg-cyan-50 border-cyan-200',     chip: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { row: 'bg-fuchsia-50 border-fuchsia-200', chip: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300' },
  { row: 'bg-orange-50 border-orange-200', chip: 'bg-orange-100 text-orange-700 border-orange-300' },
];
const getAdvanceColor = (advanceRef) => {
  if (!advanceRef) return ADVANCE_ROW_PALETTE[0];
  let h = 0;
  for (let i = 0; i < advanceRef.length; i++) {
    h = (h * 31 + advanceRef.charCodeAt(i)) | 0;
  }
  return ADVANCE_ROW_PALETTE[Math.abs(h) % ADVANCE_ROW_PALETTE.length];
};

const LedgerReconciliationPage = () => {
  usePageTitle("Ledger Reconciliation");
  const { token } = useAuth();
  const { settings: printSettings } = usePrintSettings();
  const printRef = useRef(null);

  // Main view toggle: "ledger" (deep drill), "advances" (advance payments dashboard), or "account" (traditional ledger)
  const [mainView, setMainView] = useState("ledger");
  const [searchParams] = useSearchParams();
  
  // Read view from URL query param on mount
  useEffect(() => {
    const viewParam = searchParams.get("view");
    if (viewParam && ["ledger", "account", "advances"].includes(viewParam)) {
      setMainView(viewParam);
    }
  }, [searchParams]);
  
  // ⌘K palette deep-link: /ledger-reconciliation?tracking=XXX&client=YYY&ts=n
  // Jumps straight to the parcel row (drill into client → parcels view →
  // scroll + highlight + auto-expand) via the existing search navigation.
  const _handledTrackingJump = useRef("");
  useEffect(() => {
    const trackingParam = (searchParams.get("tracking") || "").trim();
    if (!trackingParam || !token) return;
    const jumpKey = `${trackingParam}|${searchParams.get("ts") || ""}`;
    if (_handledTrackingJump.current === jumpKey) return;
    _handledTrackingJump.current = jumpKey;
    setMainView("ledger");
    const clientParam = (searchParams.get("client") || "").trim().toUpperCase();
    setGlobalSearchQuery(trackingParam.toUpperCase());
    setSearchNavigated(true);
    navigateToSearchResult({
      type: "tracking",
      tracking: trackingParam.toUpperCase(),
      navigation: { client: clientParam || null }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);
  
  // Partner Ledger Account View state
  const [accountViewData, setAccountViewData] = useState(null);
  const [accountViewLoading, setAccountViewLoading] = useState(false);
  const [accountViewPartner, setAccountViewPartner] = useState("");
  const [accountViewDateFrom, setAccountViewDateFrom] = useState("");
  const [accountViewDateTo, setAccountViewDateTo] = useState("");
  const [expandedParcels, setExpandedParcels] = useState(new Set());
  const [accountViewMode, setAccountViewMode] = useState("parcel"); // "parcel" or "payment"
  const [expandedPaymentGroups, setExpandedPaymentGroups] = useState(new Set());
  const [accountViewSortConfig, setAccountViewSortConfig] = useState({ key: null, direction: 'asc' }); // Sorting state
  
  // Delete entries state
  const [selectedTrackings, setSelectedTrackings] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Restore entries state
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [deletedEntries, setDeletedEntries] = useState([]);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  
  // Table filter state (report category filter)
  // Multi-select filter — was a single string ('all' or one filter key).
  // Now an array of selected filter keys. An empty array == "all".
  // Multiple selections OR together (row matches if ANY selected filter matches).
  const [accountTableFilter, setAccountTableFilter] = useState([]);
  
  // Edit entry state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ sale_amount: '', cost_amount: '', payment_amount: '', payment_mode: '' });
  const [editPassword, setEditPassword] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // Manual Debit/Credit Entry Modal state (for Account View)
  const [showAccountViewManualEntry, setShowAccountViewManualEntry] = useState(false);
  const [accountViewManualEntryType, setAccountViewManualEntryType] = useState('debit'); // 'debit' or 'credit'
  const [accountViewManualEntryForm, setAccountViewManualEntryForm] = useState({
    amount: '',
    description: '',
    date: '',
    category: 'adjustment'
  });
  const [accountViewManualEntryLoading, setAccountViewManualEntryLoading] = useState(false);
  
  // Account View Reports state
  const [showAccountReportDialog, setShowAccountReportDialog] = useState(false);
  const [selectedAccountReports, setSelectedAccountReports] = useState([]);
  const [accountReportData, setAccountReportData] = useState(null);
  const [accountReportLoading, setAccountReportLoading] = useState(false);
  const [showFullScreenReport, setShowFullScreenReport] = useState(false);
  const [showLedgerPreview, setShowLedgerPreview] = useState(false); // NEW: Preview before export
  const [showFullDetails, setShowFullDetails] = useState(false); // Toggle for full details view
  const [exportColumns, setExportColumns] = useState({
    sno: true,
    date: true,
    tracking: true,
    sale: true,
    gp: true,
    coll: true,
    debit: true,
    credit: true,
    balance: true,
    grp_bal: true  // Only for payment-wise view
  }); // Column selection for export
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // Advance Payments Dashboard state
  const [advancePayments, setAdvancePayments] = useState([]);
  const [advancePaymentsLoading, setAdvancePaymentsLoading] = useState(false);
  const [selectedAdvanceRef, setSelectedAdvanceRef] = useState(null);
  // Per-partner advance trend (for the Customer Advances tile sparkline)
  const [advanceTrend, setAdvanceTrend] = useState(null);
  // Advance filters
  const [advanceDateFrom, setAdvanceDateFrom] = useState("");
  const [advanceDateTo, setAdvanceDateTo] = useState("");
  const [advanceStatusFilter, setAdvanceStatusFilter] = useState("all"); // all|fully_used|credit_remaining|debit_remaining
  const [advanceHideFullyUsed, setAdvanceHideFullyUsed] = useState(true);
  const [advanceSearchTerm, setAdvanceSearchTerm] = useState("");
  // Delete advance dialog
  const [deleteAdvanceTarget, setDeleteAdvanceTarget] = useState(null); // advance object
  const [deleteAdvancePassword, setDeleteAdvancePassword] = useState("");
  const [deleteAdvanceReason, setDeleteAdvanceReason] = useState("");
  const [deleteAdvanceLoading, setDeleteAdvanceLoading] = useState(false);

  // View levels: summary, year, month, day, parcels
  const [viewLevel, setViewLevel] = useState("summary");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [errorFilter, setErrorFilter] = useState("all");
  const [accountTypeFilter, setAccountTypeFilter] = useState("all"); // "all", "partner", "agent", "client"
  const [underFilter, setUnderFilter] = useState("all"); // Filter by manager/under field
  const [managers, setManagers] = useState([]); // List of unique manager codes
  const [minBalance, setMinBalance] = useState("");
  const [maxBalance, setMaxBalance] = useState("");
  const [hideSettled, setHideSettled] = useState(false); // Hide settled entries from drill-down

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: "sales_balance", direction: "desc" });

  // Manual entry dialog
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryType, setManualEntryType] = useState("take");
  const [manualEntryClientCode, setManualEntryClientCode] = useState("");
  const [manualEntryAmount, setManualEntryAmount] = useState("");
  const [manualEntryDate, setManualEntryDate] = useState("");
  const [manualEntryNarration, setManualEntryNarration] = useState("");
  const [manualEntryTracking, setManualEntryTracking] = useState("");
  const [editingEntryId, setEditingEntryId] = useState(null);
  
  // Separate Debit dialog state (to avoid conflicts)
  const [showDebitDialog, setShowDebitDialog] = useState(false);
  const [debitClientCode, setDebitClientCode] = useState("");
  const [debitAmount, setDebitAmount] = useState("");
  const [debitDate, setDebitDate] = useState("");
  const [debitNarration, setDebitNarration] = useState("");

  // Manual entries list for current client
  const [manualEntries, setManualEntries] = useState([]);
  const [showEntriesDialog, setShowEntriesDialog] = useState(false);

  // Print dialog states
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printIncludeDetails, setPrintIncludeDetails] = useState(false);
  const [printColumns, setPrintColumns] = useState({
    client_code: true,
    orders: true,
    sales: true,
    costing: true,
    gp_self: true,
    total_debit: true,
    total_credit: true,
    balance: true,
    parcels: true,
    sell_price: true,
    gp: true,
    payment: true,
    errors: true,
    tracking: true,
    date: true,
    status: true
  });

  // Export dialog states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState("excel"); // excel, pdf
  const [exportScope, setExportScope] = useState("current"); // current, all_clients, selected_client, date_range
  const [exportDateRange, setExportDateRange] = useState("all"); // all, today, week, month, year, custom
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportIncludeDetails, setExportIncludeDetails] = useState(false);
  const [exportSelectedClients, setExportSelectedClients] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  // PDF Preview Modal state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);

  // Account Print Column Dialog state
  const [showAccountPrintDialog, setShowAccountPrintDialog] = useState(false);
  // Persist Account print prefs in localStorage so finance can fine-tune the
  // ticked columns once and have them auto-applied next time. Defaults are
  // tuned for the team's preferred layout (hide Self share + Balance, hide
  // Opening / Closing / Percentages).
  const ACCOUNT_PRINT_PREF_KEY = "eximflow.ledgerRecon.accountPrintPrefs.v1";
  const ACCOUNT_PRINT_DEFAULTS = {
    columns: {
      date: true,
      tracking: true,
      customer: true,
      ref: true,
      sale: true,
      gp: true,
      gp_self: false,
      gp_partner: true,
      debit: true,
      credit: true,
      coll: true,
      balance: false
    },
    hideDetails: true,
    hideManual: false,
  };
  const _loadAccountPrintPrefs = () => {
    try {
      const raw = localStorage.getItem(ACCOUNT_PRINT_PREF_KEY);
      if (!raw) return ACCOUNT_PRINT_DEFAULTS;
      const parsed = JSON.parse(raw);
      return {
        columns: { ...ACCOUNT_PRINT_DEFAULTS.columns, ...(parsed.columns || {}) },
        hideDetails: parsed.hideDetails ?? ACCOUNT_PRINT_DEFAULTS.hideDetails,
        hideManual: parsed.hideManual ?? ACCOUNT_PRINT_DEFAULTS.hideManual,
      };
    } catch {
      return ACCOUNT_PRINT_DEFAULTS;
    }
  };
  const _initialAcctPrint = _loadAccountPrintPrefs();
  const [accountPrintHideDetails, setAccountPrintHideDetails] = useState(_initialAcctPrint.hideDetails);
  const [accountPrintHideManual, setAccountPrintHideManual] = useState(_initialAcctPrint.hideManual);
  const [accountPrintColumns, setAccountPrintColumns] = useState(_initialAcctPrint.columns);
  useEffect(() => {
    try {
      localStorage.setItem(
        ACCOUNT_PRINT_PREF_KEY,
        JSON.stringify({
          columns: accountPrintColumns,
          hideDetails: accountPrintHideDetails,
          hideManual: accountPrintHideManual,
        })
      );
    } catch {
      // localStorage might be full or unavailable (private mode) — silently ignore.
    }
  }, [accountPrintColumns, accountPrintHideDetails, accountPrintHideManual]);

  // Expanded rows for viewing products within a tracking
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadingProducts, setLoadingProducts] = useState({});
  const [trackingProducts, setTrackingProducts] = useState({});

  // Pagination state (for 100x data scale)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(150);
  const [totalCount, setTotalCount] = useState(0);
  const [usePagination, setUsePagination] = useState(true); // Enable server-side pagination
  
  // Mobile view mode
  const [mobileViewMode, setMobileViewMode] = useState("card"); // "card" or "table"

  // Global Search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchNavigated, setSearchNavigated] = useState(false); // Track if we navigated via search
  const [searchedTracking, setSearchedTracking] = useState(null); // Track the searched tracking number for filtering
  // In-ledger live filter — when a client's ledger has hundreds of parcels,
  // typing here narrows the visible rows in real time (matches tracking,
  // service, country, product columns). Independent of the global header
  // search which navigates between clients.
  const [parcelInPageFilter, setParcelInPageFilter] = useState("");

  // Partner Ledger Upload state
  const [showPartnerLedgerUpload, setShowPartnerLedgerUpload] = useState(false);
  const [partnerLedgerFile, setPartnerLedgerFile] = useState(null);
  const [partnerLedgerUploading, setPartnerLedgerUploading] = useState(false);
  const [partnerLedgerResults, setPartnerLedgerResults] = useState(null);
  const [partnerLedgerProcessing, setPartnerLedgerProcessing] = useState(false);
  const [selectedPartnerEntries, setSelectedPartnerEntries] = useState({});
  const [duplicateHandling, setDuplicateHandling] = useState("skip");
  const [expandedPartners, setExpandedPartners] = useState(new Set());
  const [partnerLedgerStatusFilter, setPartnerLedgerStatusFilter] = useState("all");
  
  // Partner Collections state (for information only - invoice payments to partner)
  const [showPartnerCollections, setShowPartnerCollections] = useState(false);
  const [partnerCollections, setPartnerCollections] = useState(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsFilter, setCollectionsFilter] = useState("all"); // all, received, matched, unmatched
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // {partnerCode, entryIndex, field}
  const [editingValue, setEditingValue] = useState("");
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState("");
  const [showAuditLogDialog, setShowAuditLogDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  
  // Workflow state
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [workflowFilter, setWorkflowFilter] = useState("all"); // all, pending, verified, approved, rejected
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Get user role from auth context
  const { user } = useAuth();
  const userRole = user?.role || "super_admin";
  const canEditAll = userRole === "super_admin" || userRole === "admin";
  const canEditLimited = userRole === "accounts" || canEditAll;
  const canVerify = userRole === "accounts" || canEditAll;
  const canApprove = canEditAll; // Only admin can approve

  // Predefined payment modes for quick selection
  const PAYMENT_MODES = [
    { value: "ESR", label: "ESR (Company)", isCompany: true },
    { value: "ESR BOB", label: "ESR BOB (Company)", isCompany: true },
    { value: "ESR AXIS", label: "ESR AXIS (Company)", isCompany: true },
    { value: "ESR KOTAK", label: "ESR KOTAK (Company)", isCompany: true },
    { value: "ESR WISE", label: "ESR WISE (Company)", isCompany: true },
    { value: "Maxwell Enterprises", label: "Maxwell (Company)", isCompany: true },
    { value: "UK Account", label: "UK Account (Company)", isCompany: true },
    { value: "Personal via BOB", label: "Personal via BOB", isCompany: false },
    { value: "Kartik (Personal)", label: "Kartik (Personal)", isCompany: false },
    { value: "Johny (Personal)", label: "Johny (Personal)", isCompany: false },
    { value: "GPay", label: "GPay (Personal)", isCompany: false },
    { value: "BTC", label: "BTC (Personal)", isCompany: false },
    { value: "USDT", label: "USDT (Personal)", isCompany: false },
    { value: "Cash", label: "Cash (Personal)", isCompany: false },
  ];

  // Calculate status counts from partner ledger results
  const partnerLedgerStatusCounts = useMemo(() => {
    if (!partnerLedgerResults?.partners) return { matched: 0, no_payment: 0, unknown_receiver: 0, unknown_tracking: 0, esr_reship: 0 };
    
    const counts = { matched: 0, no_payment: 0, unknown_receiver: 0, unknown_tracking: 0, esr_reship: 0 };
    partnerLedgerResults.partners.forEach(partner => {
      partner.entries?.forEach(entry => {
        if (entry.status === 'matched') counts.matched++;
        else if (entry.status === 'no_payment') counts.no_payment++;
        else if (entry.status === 'unknown_receiver') counts.unknown_receiver++;
        else if (entry.status === 'unknown_tracking') counts.unknown_tracking++;
        // ESR Reship is a flag, not a status — count separately so we can
        // surface free-reship parcels in their own bucket for month-end audit.
        if (entry.is_reship && (entry.payment_mode || "").toUpperCase() === "ESR") {
          counts.esr_reship++;
        }
      });
    });
    return counts;
  }, [partnerLedgerResults]);

  // Filter entries by status
  const getFilteredEntries = (entries) => {
    if (partnerLedgerStatusFilter === "all") return entries;
    if (partnerLedgerStatusFilter === "esr_reship") {
      return entries.filter(e =>
        e.is_reship && (e.payment_mode || "").toUpperCase() === "ESR"
      );
    }
    return entries.filter(entry => entry.status === partnerLedgerStatusFilter);
  };

  // All available columns per level
  const columnOptions = {
    summary: [
      { key: "client_code", label: "Account Code" },
      { key: "orders", label: "Orders" },
      { key: "sales", label: "Sales" },
      { key: "costing", label: "Costing" },
      { key: "gp_self", label: "GP Self" },
      { key: "total_debit", label: "Total Debit" },
      { key: "total_credit", label: "Total Credit" },
      { key: "balance", label: "Balance" }
    ],
    hierarchy: [
      { key: "parcels", label: "Parcels Count" },
      { key: "costing", label: "Costing" },
      { key: "sell_price", label: "Sell Price" },
      { key: "gp", label: "GP" },
      { key: "gp_self", label: "GP Self" },
      { key: "payment", label: "Payment" },
      { key: "errors", label: "Errors" }
    ],
    parcels: [
      { key: "tracking", label: "Tracking" },
      { key: "date", label: "Date" },
      { key: "costing", label: "Costing" },
      { key: "sell_price", label: "Sell Price" },
      { key: "gp", label: "GP" },
      { key: "gp_self", label: "GP Self" },
      { key: "payment", label: "Payment" },
      { key: "status", label: "Status" }
    ]
  };

  // Fetch summary data - Uses PAGINATED endpoint to prevent crashes with large datasets
  const fetchSummaryData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      // Use the paginated endpoint for better performance
      let url = `${BACKEND_URL}/api/ledger-reconciliation/summary/paginated`;
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      params.append("sort_by", sortConfig.key || "balance");
      params.append("sort_dir", sortConfig.direction || "desc");
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (accountTypeFilter && accountTypeFilter !== "all") {
        params.append("account_type", accountTypeFilter);
      }
      if (underFilter && underFilter !== "all") {
        params.append("under_filter", underFilter);
      }
      if (searchTerm) params.append("search", searchTerm);
      if (balanceFilter && balanceFilter !== "all") {
        params.append("balance_filter", balanceFilter);
      }
      if (minBalance) params.append("min_balance", minBalance);
      if (maxBalance) params.append("max_balance", maxBalance);
      url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        // Transform paginated response to match expected format
        setSummaryData({
          clients: data.clients,
          summary: data.summary
        });
        if (data.managers) setManagers(data.managers);
        setTotalCount(data.pagination?.total_count || 0);
        setCurrentPage(data.pagination?.page || 1);
      } else {
        toast.error(data.detail || "Failed to fetch reconciliation data");
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to fetch reconciliation data");
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, accountTypeFilter, underFilter, searchTerm, balanceFilter, minBalance, maxBalance, pageSize, sortConfig]);

  // Fetch client details
  const fetchClientDetails = useCallback(async (clientCode) => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/${clientCode}/details`;
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (hideSettled) params.append("hide_settled", "true");
      if (params.toString()) url += `?${params.toString()}`;

      // Fetch both details and account view in parallel
      let accountUrl = `${BACKEND_URL}/api/partner-ledger-account/${clientCode}`;
      const accountParams = new URLSearchParams();
      if (dateFrom) accountParams.append("date_from", dateFrom);
      if (dateTo) accountParams.append("date_to", dateTo);
      if (accountParams.toString()) accountUrl += `?${accountParams.toString()}`;

      const [detailsRes, accountRes] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(accountUrl, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const data = await detailsRes.json();
      if (detailsRes.ok) {
        // Merge account view totals into the details data for accurate summary
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          if (accountData.totals) {
            data.account_view_totals = accountData.totals;
            data.account_view_parcel_count = accountData.parcel_count;
          }
        }
        setClientDetails(data);
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      toast.error("Failed to fetch client details");
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, hideSettled]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchSummaryData(1);
  }, [token, dateFrom, dateTo, accountTypeFilter, underFilter, searchTerm, balanceFilter, minBalance, maxBalance, pageSize, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientDetails(selectedClient);
      fetchManualEntries(selectedClient);
    }
  }, [selectedClient, fetchClientDetails, hideSettled]);

  // Fetch advance payments when switching to advances view
  useEffect(() => {
    if (mainView === "advances") {
      fetchAdvancePayments();
    }
  }, [mainView]);

  // Fetch advance payments data
  const fetchAdvancePayments = async () => {
    setAdvancePaymentsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/advance-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdvancePayments(data.advances || []);
      }
    } catch (error) {
      console.error("Failed to fetch advance payments:", error);
    } finally {
      setAdvancePaymentsLoading(false);
    }
  };

  // Delete an advance payment (admin only, password-confirmed)
  const handleDeleteAdvance = async () => {
    if (!deleteAdvanceTarget?.ref) return;
    if (!deleteAdvancePassword) {
      toast.error("Please enter your password to confirm.");
      return;
    }
    setDeleteAdvanceLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/advance-payments/${encodeURIComponent(deleteAdvanceTarget.ref)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            password: deleteAdvancePassword,
            reason: deleteAdvanceReason || null,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || "Failed to delete advance");
      }
      toast.success(
        `Advance ${deleteAdvanceTarget.ref} deleted (${data.uploads_modified || 0} upload(s), ${data.ledger_entries_deleted || 0} ledger entry/ies)`
      );
      // Reset & refetch
      setDeleteAdvanceTarget(null);
      setDeleteAdvancePassword("");
      setDeleteAdvanceReason("");
      await fetchAdvancePayments();
    } catch (error) {
      toast.error(error.message || "Could not delete advance payment");
    } finally {
      setDeleteAdvanceLoading(false);
    }
  };

  // Fetch Partner Ledger Account View data
  const fetchAccountViewData = async (partnerCode) => {
    if (!partnerCode) {
      toast.error("Please select a partner");
      return;
    }
    
    setAccountViewLoading(true);
    setSelectedTrackings(new Set()); // Clear selection on new load
    try {
      let url = `${BACKEND_URL}/api/partner-ledger-account/${partnerCode}?view_mode=${accountViewMode}`;
      if (accountViewDateFrom) url += `&date_from=${accountViewDateFrom}`;
      if (accountViewDateTo) url += `&date_to=${accountViewDateTo}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccountViewData(data);
        const mode = accountViewMode === "payment" ? "payment groups" : "parcels";
        const count = accountViewMode === "payment" ? data.payment_group_count : data.parcel_count;
        toast.success(`Loaded ${count} ${mode} for ${partnerCode}`);
        // Fire-and-forget: fetch the advance trend for the tile sparkline.
        // We don't block the main load on this.
        try {
          const trendResp = await fetch(
            `${BACKEND_URL}/api/partner-ledger-account/${partnerCode}/advance-trend?months=6`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (trendResp.ok) {
            const trendData = await trendResp.json();
            setAdvanceTrend(trendData);
          } else {
            setAdvanceTrend(null);
          }
        } catch (_e) {
          setAdvanceTrend(null);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to fetch ledger account");
      }
    } catch (error) {
      console.error("Failed to fetch account view:", error);
      toast.error("Failed to fetch ledger account data");
    } finally {
      setAccountViewLoading(false);
    }
  };

  // Export Partner Ledger Account
  const handleExportAccountView = async (format) => {
    if (!accountViewPartner) {
      toast.error("Please select a partner first");
      return;
    }
    
    try {
      // Build columns parameter from selected columns
      const selectedColsList = Object.entries(exportColumns)
        .filter(([key, value]) => value && (key !== 'grp_bal' || accountViewMode === 'payment'))
        .map(([key]) => key)
        .join(',');
      
      let url = `${BACKEND_URL}/api/partner-ledger-account/${accountViewPartner}/export?format=${format}&view_mode=${accountViewMode}&full_details=${showFullDetails}&columns=${selectedColsList}`;
      if (accountViewDateFrom) url += `&date_from=${accountViewDateFrom}`;
      if (accountViewDateTo) url += `&date_to=${accountViewDateTo}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const detailsSuffix = showFullDetails ? '_FullDetails' : '';
        const filename = `LedgerAccount_${accountViewPartner}_${accountViewMode}${detailsSuffix}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        saveAs(blob, filename);
        toast.success(`${format.toUpperCase()} downloaded successfully`);
      } else {
        toast.error("Failed to export ledger account");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export ledger account");
    }
  };

  // Account View Print: Opens a new tab with selected columns, export buttons
  const handleAccountPrint = () => {
    if (!accountViewData) {
      toast.error("No data to print");
      return;
    }

    const cols = accountPrintColumns;
    const hideDetails = accountPrintHideDetails;
    const hideManual = accountPrintHideManual;
    const gpCols = accountViewData?.gp_columns || [];
    const partner = accountViewPartner;
    const mode = accountViewMode;
    let entries = mode === "payment"
      ? (accountViewData.payment_groups || [])
      : (getSortedAccountViewData.ledger_entries || accountViewData.ledger_entries || []);
    
    // Filter out manual entries if option selected
    if (hideManual && mode === "parcel") {
      entries = entries.filter(e => !e.is_manual_entry);
    }
    const totals = accountViewData.totals || {};
    const dateRange = accountViewData.date_range || {};
    const partnerName = accountViewData.partner_name || partner;
    const profitSharing = accountViewData.profit_sharing || {};
    const openBal = accountViewData.opening_balance || 0;
    const closeBal = accountViewData.closing_balance || 0;

    // Build header columns
    let headerHtml = '';
    if (cols.date) headerHtml += '<th>Date</th>';
    if (cols.tracking) headerHtml += '<th class="left">Particulars</th>';
    if (cols.customer) headerHtml += '<th class="left">Customer</th>';
    if (cols.ref) headerHtml += '<th>Ref</th>';
    if (cols.sale) headerHtml += '<th>Sale</th>';
    if (cols.gp) headerHtml += '<th>GP</th>';
    if (cols.gp_self) {
      const selfCol = gpCols.find(gc => gc.key === 'gp_self');
      headerHtml += `<th>${selfCol ? `Self (${selfCol.percentage}%)` : 'GP Self'}</th>`;
    }
    if (cols.gp_partner) {
      const partnerCol = gpCols.find(gc => gc.key === 'gp_to_partner');
      headerHtml += `<th>${partnerCol ? `${partnerCol.name} (${partnerCol.percentage}%)` : 'GP Partner'}</th>`;
    }
    if (cols.debit) headerHtml += '<th class="debit">Debit</th>';
    if (cols.credit) headerHtml += '<th class="credit">Credit</th>';
    if (cols.coll) headerHtml += '<th>Coll</th>';
    if (cols.balance) headerHtml += '<th>Balance</th>';

    // Build rows
    let rowsHtml = '';
    const fmt = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

    if (mode === "parcel") {
      entries.forEach((p, idx) => {
        let row = '';
        if (cols.date) row += `<td>${formatDMY(p.date)}</td>`;
        if (cols.tracking) row += `<td class="left bold">${p.is_manual_entry ? '' : 'PARCEL: '}${p.tracking}</td>`;
        if (cols.customer) row += `<td class="left">${p.customer || '-'}</td>`;
        if (cols.ref) row += `<td>${p.payment_ref || '-'}</td>`;
        if (cols.sale) row += `<td>${fmt(p.sale_amount)}</td>`;
        if (cols.gp) row += `<td class="${p.gp < 0 ? 'neg' : ''}">${fmt(p.gp)}</td>`;
        if (cols.gp_self) row += `<td>${fmt(p.gp_self)}</td>`;
        if (cols.gp_partner) row += `<td>${fmt(p.gp_to_partner)}</td>`;
        if (cols.debit) row += `<td class="debit">${fmt(p.parcel_summary?.total_debit)}</td>`;
        if (cols.credit) row += `<td class="credit">${fmt(p.parcel_summary?.total_credit)}</td>`;
        if (cols.coll) row += `<td>${fmt(p.parcel_summary?.collection)}</td>`;
        if (cols.balance) {
          const bal = p.running_balance ?? p.entries?.[p.entries.length - 1]?.balance ?? 0;
          row += `<td class="bal ${bal < 0 ? 'neg' : ''}">${fmt(bal)}</td>`;
        }
        rowsHtml += `<tr>${row}</tr>`;
      });
    } else {
      // Payment-wise view
      (entries || []).forEach((group) => {
        (group.parcels || []).forEach((p) => {
          let row = '';
          if (cols.date) row += `<td>${formatDMY(p.date)}</td>`;
          if (cols.tracking) row += `<td class="left bold">${p.is_manual_entry ? '' : 'PARCEL: '}${p.tracking}</td>`;
          if (cols.customer) row += `<td class="left">${p.customer || '-'}</td>`;
          if (cols.ref) row += `<td>-</td>`;
          if (cols.sale) row += `<td>${fmt(p.sale_amount)}</td>`;
          if (cols.gp) row += `<td class="${p.gp < 0 ? 'neg' : ''}">${fmt(p.gp)}</td>`;
          if (cols.gp_self) row += `<td>${fmt(p.gp_self)}</td>`;
          if (cols.gp_partner) row += `<td>${fmt(p.gp_to_partner)}</td>`;
          if (cols.debit) row += `<td class="debit">${fmt(p.parcel_summary?.total_debit)}</td>`;
          if (cols.credit) row += `<td class="credit">-</td>`;
          if (cols.coll) row += `<td>-</td>`;
          if (cols.balance) row += `<td>-</td>`;
          rowsHtml += `<tr>${row}</tr>`;
        });
        // Payment row
        if (group.payment_entry) {
          const pe = group.payment_entry;
          let payRow = '';
          const colCount = Object.values(cols).filter(v => v).length + (cols.gp_split ? gpCols.length - 1 : 0);
          const firstCols = (cols.date ? 1 : 0) + (cols.tracking ? 1 : 0) + (cols.customer ? 1 : 0) + (cols.ref ? 1 : 0);
          if (firstCols > 0) payRow += `<td colspan="${firstCols}" class="left pay-row">${pe.description || 'Payment'}</td>`;
          if (cols.sale) payRow += '<td class="pay-row">-</td>';
          if (cols.gp) payRow += '<td class="pay-row">-</td>';
          if (cols.gp_self) payRow += '<td class="pay-row">-</td>';
          if (cols.gp_partner) payRow += '<td class="pay-row">-</td>';
          if (cols.debit) payRow += '<td class="pay-row">-</td>';
          if (cols.credit) payRow += `<td class="credit pay-row">${fmt(pe.credit)}</td>`;
          if (cols.coll) payRow += `<td class="pay-row">${fmt(pe.collection)}</td>`;
          if (cols.balance) payRow += `<td class="bal pay-row">${fmt(pe.balance)}</td>`;
          rowsHtml += `<tr class="pay-group">${payRow}</tr>`;
        }
      });
    }

    // Totals row - recalculate from filtered entries when hideManual is active
    let totalsRow = '';
    let printTotals = totals;
    if (hideManual && mode === "parcel") {
      const tSale = entries.reduce((s, e) => s + (e.sale_amount || 0), 0);
      const tGp = entries.reduce((s, e) => s + (e.gp || 0), 0);
      const tDebit = entries.reduce((s, e) => s + (e.parcel_summary?.total_debit || 0), 0);
      const tCredit = entries.reduce((s, e) => s + (e.parcel_summary?.total_credit || 0), 0);
      const tColl = entries.reduce((s, e) => s + (e.parcel_summary?.collection || 0), 0);
      printTotals = { total_sale: tSale, total_gp: tGp, total_debit: tDebit, total_credit: tCredit, total_collection: tColl };
    }
    const gpSelfTotal = entries.reduce((s, e) => s + (e.gp_self || (e.parcels ? e.parcels.reduce((ps, p) => ps + (p.gp_self||0), 0) : 0)), 0);
    const gpPartnerTotal = entries.reduce((s, e) => s + (e.gp_to_partner || (e.parcels ? e.parcels.reduce((ps, p) => ps + (p.gp_to_partner||0), 0) : 0)), 0);
    const firstCols = (cols.date ? 1 : 0) + (cols.tracking ? 1 : 0) + (cols.customer ? 1 : 0) + (cols.ref ? 1 : 0);
    if (firstCols > 0) totalsRow += `<td colspan="${firstCols}" class="left bold">TOTALS</td>`;
    if (cols.sale) totalsRow += `<td class="bold">${fmt(printTotals.total_sale)}</td>`;
    if (cols.gp) totalsRow += `<td class="bold">${fmt(printTotals.total_gp)}</td>`;
    if (cols.gp_self) totalsRow += `<td class="bold">${fmt(gpSelfTotal)}</td>`;
    if (cols.gp_partner) totalsRow += `<td class="bold">${fmt(gpPartnerTotal)}</td>`;
    if (cols.debit) totalsRow += `<td class="bold debit">${fmt(printTotals.total_debit)}</td>`;
    if (cols.credit) totalsRow += `<td class="bold credit">${fmt(printTotals.total_credit)}</td>`;
    if (cols.coll) totalsRow += `<td class="bold">${fmt(printTotals.total_collection)}</td>`;
    if (cols.balance) totalsRow += `<td class="bold bal">${fmt(closeBal)}</td>`;

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<title>PLA - ${partner} | ${mode === 'payment' ? 'Payment-wise' : 'Parcel-wise'}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #f3f4f6; color: #1e293b; padding: 16px; }
  .toolbar { position: sticky; top: 0; z-index: 10; background: #fff; border-bottom: 2px solid #e2e8f0; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; margin: -16px -16px 16px -16px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .toolbar h2 { font-size: 16px; color: #0f172a; }
  .toolbar .sub { font-size: 12px; color: #64748b; }
  .toolbar .btns { display: flex; gap: 8px; }
  .toolbar .btns button { padding: 8px 16px; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.15s; }
  .btn-excel { background: #059669; color: #fff; } .btn-excel:hover { background: #047857; }
  .btn-pdf { background: #dc2626; color: #fff; } .btn-pdf:hover { background: #b91c1c; }
  .btn-print { background: #2563eb; color: #fff; } .btn-print:hover { background: #1d4ed8; }
  .header { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 20px; margin-bottom: 12px; }
  .header h1 { font-size: 18px; color: #0f172a; } .header .meta { font-size: 12px; color: #64748b; margin-top: 4px; }
  .header .bal-info { display: flex; gap: 24px; margin-top: 8px; font-size: 13px; }
  .header .bal-info span { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; font-size: 12px; }
  thead th { background: #1e293b; color: #fff; padding: 8px 10px; text-align: right; white-space: nowrap; font-size: 11px; }
  thead th.left { text-align: left; }
  tbody td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; text-align: right; }
  tbody td.left { text-align: left; } tbody td.bold { font-weight: 700; }
  tbody td.debit { color: #dc2626; } tbody td.credit { color: #059669; }
  tbody td.bal { font-weight: 700; } tbody td.neg { color: #dc2626; }
  tbody td.pay-row { background: #ecfdf5; font-weight: 600; }
  tr.pay-group td { border-top: 2px solid #059669; }
  tr.totals td { background: #f8fafc; border-top: 2px solid #334155; font-weight: 700; font-size: 12px; padding: 10px; }
  @media print { .toolbar { display: none; } body { background: #fff; padding: 8px; } }
</style>
<script src="https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js"><\/script>
</head><body>
<div class="toolbar">
  <div>
    <h2>PLA - ${partnerName} (${partner})</h2>
    <div class="sub">${mode === 'payment' ? 'Payment-wise' : 'Parcel-wise'} | ${entries.length} entries${dateRange.from ? ` | ${dateRange.from} to ${dateRange.to}` : ''}</div>
  </div>
  <div class="btns">
    <button class="btn-excel" onclick="exportExcel()">Export Excel</button>
    <button class="btn-pdf" onclick="window.print()">Print / Save PDF</button>
  </div>
</div>
<div class="header">
  <h1>${partnerName} (${partner})</h1>
  <div class="meta">${mode === 'payment' ? 'Payment-wise' : 'Parcel-wise'} View${dateRange.from ? ` | Period: ${dateRange.from} to ${dateRange.to}` : ''}</div>
  ${!hideDetails ? `<div class="bal-info">
    <div>Opening: <span>₹${Number(openBal).toLocaleString('en-IN')}</span></div>
    <div>Closing: <span style="color:${closeBal >= 0 ? '#059669':'#dc2626'}">₹${Number(closeBal).toLocaleString('en-IN')}</span></div>
    ${profitSharing.self_percentage ? `<div>Self: ${profitSharing.self_percentage}% | Partner: ${profitSharing.partner_percentage}%</div>` : ''}
  </div>` : ''}
</div>
<table id="printTable">
  <thead><tr>${headerHtml}</tr></thead>
  <tbody>${rowsHtml}<tr class="totals">${totalsRow}</tr></tbody>
</table>
<script>
function exportExcel() {
  try {
    var wb = XLSX.utils.table_to_book(document.getElementById('printTable'), {sheet: 'Ledger'});
    XLSX.writeFile(wb, 'LedgerAccount_${partner}_${mode}_${new Date().toISOString().split('T')[0]}.xlsx');
  } catch(e) { alert('Excel export failed: ' + e.message); }
}
<\/script>
</body></html>`;

    const newTab = window.open('', '_blank');
    if (!newTab) {
      toast.error('Please allow popups for this site to use the print feature.');
      return;
    }
    newTab.document.write(html);
    newTab.document.close();
    setShowAccountPrintDialog(false);
    toast.success('Print preview opened in new tab');
  };

  // Handle manual debit/credit entry submission (Account View)
  const handleAccountViewManualEntrySubmit = async () => {
    if (!accountViewPartner) {
      toast.error("Please select a partner first");
      return;
    }
    if (!accountViewManualEntryForm.amount || parseFloat(accountViewManualEntryForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!accountViewManualEntryForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!accountViewManualEntryForm.date) {
      toast.error("Please select a date");
      return;
    }

    setAccountViewManualEntryLoading(true);
    try {
      // Map debit/credit to take/give for existing API
      const apiEntryType = accountViewManualEntryType === 'debit' ? 'take' : 'give';
      
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/manual-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_code: accountViewPartner,
          entry_type: apiEntryType,
          amount: parseFloat(accountViewManualEntryForm.amount),
          narration: `[${accountViewManualEntryForm.category.toUpperCase()}] ${accountViewManualEntryForm.description}`,
          date: accountViewManualEntryForm.date
        })
      });

      if (response.ok) {
        toast.success(`${accountViewManualEntryType === 'debit' ? 'Debit' : 'Credit'} entry added successfully`);
        setShowAccountViewManualEntry(false);
        setAccountViewManualEntryForm({ amount: '', description: '', date: '', category: 'adjustment' });
        // Refresh the account view data
        fetchAccountViewData(accountViewPartner);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add entry");
      }
    } catch (error) {
      console.error("Manual entry error:", error);
      toast.error("Failed to add entry");
    } finally {
      setAccountViewManualEntryLoading(false);
    }
  };

  // Open manual entry modal (Account View)
  const openAccountViewManualEntry = (type) => {
    setAccountViewManualEntryType(type);
    setAccountViewManualEntryForm({ amount: '', description: '', date: '', category: 'adjustment' });
    setShowAccountViewManualEntry(true);
  };

  // Toggle payment group expansion
  const togglePaymentGroupExpansion = (paymentKey) => {
    setExpandedPaymentGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(paymentKey)) {
        newSet.delete(paymentKey);
      } else {
        newSet.add(paymentKey);
      }
      return newSet;
    });
  };

  // Toggle parcel expansion in account view
  const toggleParcelExpansion = (tracking) => {
    setExpandedParcels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tracking)) {
        newSet.delete(tracking);
      } else {
        newSet.add(tracking);
      }
      return newSet;
    });
  };


  // Toggle tracking selection for delete
  const toggleTrackingSelect = (tracking) => {
    setSelectedTrackings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tracking)) {
        newSet.delete(tracking);
      } else {
        newSet.add(tracking);
      }
      return newSet;
    });
  };

  // Select/deselect all trackings
  const toggleSelectAll = () => {
    if (!accountViewData) return;
    const allTrackings = accountViewMode === "parcel"
      ? (accountViewData.ledger_entries || []).map(e => e.tracking)
      : (accountViewData.payment_groups || []).flatMap(g => g.parcels?.map(p => p.tracking) || []);
    
    if (selectedTrackings.size === allTrackings.length) {
      setSelectedTrackings(new Set());
    } else {
      setSelectedTrackings(new Set(allTrackings));
    }
  };

  // Handle delete entries using XMLHttpRequest (avoids response body interception)
  const handleDeleteEntries = async () => {
    if (!deletePassword || selectedTrackings.size === 0) return;
    
    setDeleteLoading(true);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/partner-ledger-account/delete-entries`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = () => {
      setDeleteLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(`Deleted ${selectedTrackings.size} entries (${data.deleted_counts?.total || 0} records removed)`);
          setSelectedTrackings(new Set());
          setShowDeleteConfirm(false);
          setDeletePassword('');
          setDeleteReason('');
          fetchAccountViewData(accountViewPartner);
        } else {
          toast.error(data.detail || 'Delete failed');
        }
      } catch {
        toast.error('Error parsing response');
      }
    };
    
    xhr.onerror = () => {
      setDeleteLoading(false);
      toast.error('Network error during delete');
    };
    
    xhr.send(JSON.stringify({
      client_code: accountViewPartner,
      tracking_numbers: Array.from(selectedTrackings),
      admin_password: deletePassword,
      delete_reason: deleteReason.trim() || 'No reason provided'
    }));
  };


  // Fetch deleted entries for restore
  const fetchDeletedEntries = async () => {
    try {
      const clientParam = accountViewPartner ? `?client_code=${accountViewPartner}` : '';
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger-account/deleted-entries${clientParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setDeletedEntries(data.deleted_entries || []);
      setShowRestoreDialog(true);
    } catch (err) {
      toast.error('Error loading deleted entries');
    }
  };

  // Handle restore entries
  const handleRestoreEntries = (logId) => {
    if (!restorePassword) return;
    setRestoreLoading(true);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/partner-ledger-account/restore-entries`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = () => {
      setRestoreLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(`Restored ${data.tracking_numbers?.length || 0} entries (${Object.values(data.restored_counts || {}).reduce((a,b) => a+b, 0)} records)`);
          setRestorePassword('');
          fetchDeletedEntries(); // Refresh list
          if (accountViewPartner) fetchAccountViewData(accountViewPartner); // Reload account view
        } else {
          toast.error(data.detail || 'Restore failed');
        }
      } catch {
        toast.error('Error parsing response');
      }
    };
    
    xhr.onerror = () => { setRestoreLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ log_id: logId, admin_password: restorePassword }));
  };

  // Open edit dialog for a parcel
  const openEditDialog = (parcel) => {
    setEditEntry(parcel);
    // Convert display date (DD-MM-YY) to input date (YYYY-MM-DD)
    let dateValue = '';
    const dateStr = parcel.date || '';
    if (dateStr && dateStr.includes('-') && dateStr.length === 8) {
      const [dd, mm, yy] = dateStr.split('-');
      const fullYear = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      dateValue = `${fullYear}-${mm}-${dd}`;
    }
    setEditForm({
      sale_amount: parcel.sale_amount || '',
      cost_amount: parcel.parcel_summary?.total_debit || '',
      payment_amount: parcel.parcel_summary?.total_credit > 0 ? parcel.parcel_summary.total_credit : (parcel.parcel_summary?.collection || ''),
      payment_mode: parcel.payment_mode || '',
      entry_date: dateValue
    });
    setEditPassword('');
    setEditReason('');
    setShowEditDialog(true);
  };

  // Handle edit ledger parcel submission
  const handleEditLedgerParcel = () => {
    if (!editEntry || !editPassword || !editReason.trim()) return;
    setEditLoading(true);
    
    const body = {
      client_code: accountViewPartner,
      tracking_number: editEntry.tracking,
      admin_password: editPassword,
      edit_reason: editReason.trim()
    };
    
    // Only include changed fields
    const origSale = editEntry.sale_amount || 0;
    const origCost = editEntry.parcel_summary?.total_debit || 0;
    const origPayment = editEntry.parcel_summary?.total_credit > 0 ? editEntry.parcel_summary.total_credit : (editEntry.parcel_summary?.collection || 0);
    
    if (editForm.sale_amount !== '' && parseFloat(editForm.sale_amount) !== origSale) {
      body.sale_amount = parseFloat(editForm.sale_amount);
    }
    if (editForm.cost_amount !== '' && parseFloat(editForm.cost_amount) !== origCost) {
      body.cost_amount = parseFloat(editForm.cost_amount);
    }
    if (editForm.payment_amount !== '' && parseFloat(editForm.payment_amount) !== origPayment) {
      body.payment_amount = parseFloat(editForm.payment_amount);
    }
    if (editForm.payment_mode && editForm.payment_mode !== (editEntry.payment_mode || '')) {
      body.payment_mode = editForm.payment_mode;
    }
    if (editForm.entry_date) {
      // Convert parcel display date to YYYY-MM-DD for comparison
      const dateStr = editEntry.date || '';
      let origDate = '';
      if (dateStr && dateStr.includes('-') && dateStr.length === 8) {
        const [dd, mm, yy] = dateStr.split('-');
        const fullYear = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
        origDate = `${fullYear}-${mm}-${dd}`;
      }
      if (editForm.entry_date !== origDate) {
        body.entry_date = editForm.entry_date;
      }
    }
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/partner-ledger-account/edit-entry`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = () => {
      setEditLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status === 200) {
          toast.success(data.message || 'Entry updated successfully');
          setShowEditDialog(false);
          setEditEntry(null);
          setEditPassword('');
          setEditReason('');
          if (accountViewPartner) fetchAccountViewData(accountViewPartner);
        } else {
          toast.error(data.detail || 'Edit failed');
        }
      } catch {
        toast.error('Error parsing response');
      }
    };
    
    xhr.onerror = () => { setEditLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify(body));
  };



  // Account View Sorting functions
  const handleAccountViewSort = (key) => {
    setAccountViewSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getAccountViewSortIcon = (key) => {
    if (accountViewSortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return accountViewSortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-white" /> 
      : <ArrowDown className="w-3 h-3 text-white" />;
  };

  const sortAccountViewData = (data, key, direction) => {
    if (!key || !data) return data;
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      // Handle nested values
      if (key === 'total_debit') {
        aVal = a.parcel_summary?.total_debit || a.group_summary?.total_debit || 0;
        bVal = b.parcel_summary?.total_debit || b.group_summary?.total_debit || 0;
      } else if (key === 'total_credit') {
        aVal = a.parcel_summary?.total_credit || a.group_summary?.total_credit || a.payment_amount || 0;
        bVal = b.parcel_summary?.total_credit || b.group_summary?.total_credit || b.payment_amount || 0;
      } else if (key === 'running_balance') {
        aVal = a.running_balance || a.group_summary?.closing_balance || 0;
        bVal = b.running_balance || b.group_summary?.closing_balance || 0;
      } else if (key === 'payment_ref') {
        // Sort by payment reference (PMT-001, PMT-002, etc.)
        aVal = a.payment_ref || 'ZZZ';  // Put empty refs at end
        bVal = b.payment_ref || 'ZZZ';
      } else {
        aVal = a[key] || 0;
        bVal = b[key] || 0;
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter entries by report category.
  // accountTableFilter is now an ARRAY of selected keys (empty == "all").
  // Multiple selections OR together.
  const filterByCategory = useCallback((entries, isPaymentMode) => {
    if (!accountTableFilter || accountTableFilter.length === 0 || !entries) return entries;

    const selected = new Set(accountTableFilter);
    const paymentGroupKeys = new Set([
      'company_payments', 'personal_collections', 'multi_parcel',
      'pending_payment', 'high_value',
    ]);

    const parcelMatches = (p, key) => {
      switch (key) {
        case 'negative_gp': return p.has_negative_gp;
        case 'no_invoice': return p.has_no_invoice;
        case 'high_value': return (p.sale_amount || 0) >= 10000;
        case 'company_payments': return p.entries?.some(e => e.category === 'esr_payment');
        case 'personal_collections': return p.entries?.some(e => e.category === 'collection');
        case 'pending_payment': return !p.entries?.some(e => e.category === 'esr_payment' || e.category === 'collection');
        case 'no_sales': return !p.sale_amount || p.sale_amount === 0;
        case 'dispute_adjustment': {
          const saleAmt = p.sale_amount || 0;
          const costAmt = p.cost_amount || 0;
          const pctDiff = costAmt > 0 ? (Math.abs(saleAmt - costAmt) / costAmt) * 100 : 0;
          return p.has_negative_gp || pctDiff > 50;
        }
        default: return false;
      }
    };

    const groupMatches = (g, key) => {
      switch (key) {
        case 'company_payments': return g.is_company_payment;
        case 'personal_collections': return !g.is_company_payment && g.payment_amount > 0;
        case 'multi_parcel': return g.parcel_count > 1;
        case 'pending_payment': return g.payment_amount === 0;
        case 'high_value': return g.payment_amount >= 10000;
        default: return false;
      }
    };

    if (isPaymentMode) {
      const groupKeys = [...selected].filter(k => paymentGroupKeys.has(k));
      const onlyParcelKeys = [...selected].filter(k => !paymentGroupKeys.has(k));
      let filtered = entries;
      if (groupKeys.length > 0) {
        filtered = entries.filter(g => groupKeys.some(k => groupMatches(g, k)));
      }
      if (onlyParcelKeys.length > 0) {
        filtered = filtered.map(g => {
          const fp = (g.parcels || []).filter(p => onlyParcelKeys.some(k => parcelMatches(p, k)));
          if (!fp.length) return null;
          return { ...g, parcels: fp, parcel_count: fp.length };
        }).filter(Boolean);
      }
      return filtered;
    }
    return entries.filter(p => [...selected].some(k => parcelMatches(p, k)));
  }, [accountTableFilter]);

  // Get sorted data for Account View
  const getSortedAccountViewData = useMemo(() => {
    if (!accountViewData) return { ledger_entries: [], payment_groups: [] };
    
    const { key, direction } = accountViewSortConfig;
    
    const filteredLedger = filterByCategory(accountViewData.ledger_entries, false);
    const filteredPayment = filterByCategory(accountViewData.payment_groups, true);
    
    return {
      ledger_entries: sortAccountViewData(filteredLedger, key, direction),
      payment_groups: sortAccountViewData(filteredPayment, key, direction)
    };
  }, [accountViewData, accountViewSortConfig, filterByCategory]);

  // Get partners list for dropdown - include ALL clients that have transactions
  const partnersList = useMemo(() => {
    if (!summaryData?.clients) return [];
    // Include all clients (not just partners) since new clients may not have role set yet
    return summaryData.clients
      .map(c => c.client_code)
      .filter(code => code) // Filter out empty codes
      .sort();
  }, [summaryData]);

  // Account View Report Types
  const accountReportTypes = [
    { id: 'outstanding_balance', name: 'Outstanding Balance Report', description: 'Partners with high outstanding balances', color: 'red' },
    { id: 'negative_gp', name: 'Negative GP Report', description: 'Loss-making transactions', color: 'red' },
    { id: 'no_invoice', name: 'No Invoice Report', description: 'Parcels without invoice amount', color: 'orange' },
    { id: 'high_value', name: 'High Value Transactions (≥₹10K)', description: 'Large transactions for audit', color: 'blue' },
    { id: 'company_payments', name: 'Company Payments Report', description: 'ESR/Bank payments received', color: 'green' },
    { id: 'personal_collections', name: 'Personal Collections Report', description: 'Neutral payments (no balance effect)', color: 'yellow' },
    { id: 'multi_parcel', name: 'Multi-Parcel Payments', description: 'Payments covering multiple parcels', color: 'purple' },
    { id: 'pending_payment', name: 'Pending Payment Report', description: 'Parcels awaiting payment', color: 'orange' },
    { id: 'profit_summary', name: 'Profit Share Summary', description: 'GP distribution by partner', color: 'emerald' },
    { id: 'dormant_account', name: 'Dormant Account Report', description: 'No transactions in last 30 days', color: 'slate' },
    { id: 'dispute_adjustment', name: 'Dispute/Adjustment Report', description: 'Parcels with price adjustments', color: 'rose' },
    { id: 'settlement_pending', name: 'Settlement Pending Report', description: 'Unsettled profit share with 3rd party', color: 'indigo' },
    { id: 'month_end_summary', name: 'Month-End Summary', description: 'Comprehensive monthly balance summary', color: 'cyan' },
  ];

  // Generate Account Reports
  const generateAccountReports = async () => {
    if (selectedAccountReports.length === 0) {
      toast.error("Please select at least one report type");
      return;
    }
    
    if (!accountViewData) {
      toast.error("Please load account data first");
      return;
    }
    
    setAccountReportLoading(true);
    
    try {
      // Process reports locally from loaded data
      const reports = {};
      const data = accountViewMode === "payment" ? accountViewData.payment_groups : accountViewData.ledger_entries;
      
      selectedAccountReports.forEach(reportId => {
        switch(reportId) {
          case 'outstanding_balance':
            // Partners with non-zero balance
            reports.outstanding_balance = {
              title: 'Outstanding Balance Report',
              balance: accountViewData.closing_balance,
              status: accountViewData.closing_balance > 0 ? 'Partner owes you' : accountViewData.closing_balance < 0 ? 'You owe partner' : 'Settled',
              total_debit: accountViewData.totals?.total_debit,
              total_credit: accountViewData.totals?.total_credit
            };
            break;
            
          case 'negative_gp':
            // Parcels with negative GP
            if (accountViewMode === "payment") {
              const negGpParcels = [];
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  if (p.has_negative_gp) negGpParcels.push(p);
                });
              });
              reports.negative_gp = { title: 'Negative GP Report', items: negGpParcels, count: negGpParcels.length };
            } else {
              const negGp = data?.filter(p => p.has_negative_gp) || [];
              reports.negative_gp = { title: 'Negative GP Report', items: negGp, count: negGp.length };
            }
            break;
            
          case 'no_invoice':
            // Parcels without invoice
            if (accountViewMode === "payment") {
              const noInvParcels = [];
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  if (p.has_no_invoice) noInvParcels.push(p);
                });
              });
              reports.no_invoice = { title: 'No Invoice Report', items: noInvParcels, count: noInvParcels.length };
            } else {
              const noInv = data?.filter(p => p.has_no_invoice) || [];
              reports.no_invoice = { title: 'No Invoice Report', items: noInv, count: noInv.length };
            }
            break;
            
          case 'high_value':
            // Transactions ≥ ₹10K
            if (accountViewMode === "payment") {
              const highVal = data?.filter(g => g.payment_amount >= 10000) || [];
              reports.high_value = { title: 'High Value Transactions (≥₹10K)', items: highVal, count: highVal.length };
            } else {
              const highVal = data?.filter(p => (p.sale_amount || 0) >= 10000) || [];
              reports.high_value = { title: 'High Value Transactions (≥₹10K)', items: highVal, count: highVal.length };
            }
            break;
            
          case 'company_payments':
            // ESR/Company payments
            if (accountViewMode === "payment") {
              const companyPay = data?.filter(g => g.is_company_payment) || [];
              const totalCompany = companyPay.reduce((sum, g) => sum + (g.payment_amount || 0), 0);
              reports.company_payments = { title: 'Company Payments Report', items: companyPay, count: companyPay.length, total: totalCompany };
            } else {
              const companyPay = data?.filter(p => p.entries?.some(e => e.category === 'esr_payment')) || [];
              reports.company_payments = { title: 'Company Payments Report', items: companyPay, count: companyPay.length };
            }
            break;
            
          case 'personal_collections':
            // Personal collections (neutral)
            if (accountViewMode === "payment") {
              const personal = data?.filter(g => !g.is_company_payment && g.payment_amount > 0) || [];
              const totalPersonal = personal.reduce((sum, g) => sum + (g.payment_amount || 0), 0);
              reports.personal_collections = { title: 'Personal Collections Report', items: personal, count: personal.length, total: totalPersonal };
            } else {
              const personal = data?.filter(p => p.entries?.some(e => e.category === 'personal')) || [];
              reports.personal_collections = { title: 'Personal Collections Report', items: personal, count: personal.length };
            }
            break;
            
          case 'multi_parcel':
            // Multi-parcel payments
            if (accountViewMode === "payment") {
              const multiParcel = data?.filter(g => g.parcel_count > 1) || [];
              reports.multi_parcel = { title: 'Multi-Parcel Payments', items: multiParcel, count: multiParcel.length };
            } else {
              reports.multi_parcel = { title: 'Multi-Parcel Payments', items: [], count: 0, note: 'Switch to Payment-wise view for this report' };
            }
            break;
            
          case 'pending_payment':
            // Parcels without any payment
            if (accountViewMode === "payment") {
              // In payment-wise view, extract parcels from groups with no payment
              const pendingGroups = data?.filter(g => g.payment_amount === 0) || [];
              const pendingParcels = [];
              let totalPendingAmount = 0;
              
              pendingGroups.forEach(group => {
                if (group.parcels && group.parcels.length > 0) {
                  group.parcels.forEach(parcel => {
                    pendingParcels.push({
                      tracking: parcel.tracking,
                      customer: parcel.customer,
                      sale_amount: parcel.sale_amount || 0,
                      date: parcel.date,
                    });
                    totalPendingAmount += parcel.sale_amount || 0;
                  });
                }
              });
              
              reports.pending_payment = { 
                title: 'Pending Payment Report', 
                items: pendingParcels, 
                count: pendingParcels.length,
                total_pending: totalPendingAmount
              };
            } else {
              const pending = data?.filter(p => !p.entries?.some(e => e.category === 'esr_payment' || e.category === 'personal')) || [];
              const totalPendingAmount = pending.reduce((sum, p) => sum + (p.sale_amount || 0), 0);
              reports.pending_payment = { 
                title: 'Pending Payment Report', 
                items: pending, 
                count: pending.length,
                total_pending: totalPendingAmount
              };
            }
            break;
            
          case 'profit_summary':
            // Profit share summary
            let totalGpSelf = 0, totalGpPartner = 0;
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  totalGpSelf += p.gp_self || 0;
                  totalGpPartner += p.gp_to_partner || 0;
                });
              });
            } else {
              data?.forEach(p => {
                totalGpSelf += p.gp_self || 0;
                totalGpPartner += p.gp_to_partner || 0;
              });
            }
            reports.profit_summary = {
              title: 'Profit Share Summary',
              self_share: totalGpSelf,
              partner_share: totalGpPartner,
              total_gp: totalGpSelf + totalGpPartner
            };
            break;
            
          case 'dormant_account':
            // Check if no transactions in last 30 days
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            let latestTxnDate = null;
            let dormantParcels = [];
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const txnDate = p.date ? new Date(p.date) : null;
                  if (txnDate && (!latestTxnDate || txnDate > latestTxnDate)) {
                    latestTxnDate = txnDate;
                  }
                  if (txnDate && txnDate < thirtyDaysAgo) {
                    dormantParcels.push(p);
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const txnDate = p.date ? new Date(p.date) : null;
                if (txnDate && (!latestTxnDate || txnDate > latestTxnDate)) {
                  latestTxnDate = txnDate;
                }
                if (txnDate && txnDate < thirtyDaysAgo) {
                  dormantParcels.push(p);
                }
              });
            }
            
            const daysSinceLastTxn = latestTxnDate ? Math.floor((new Date() - latestTxnDate) / (1000 * 60 * 60 * 24)) : null;
            const isDormant = daysSinceLastTxn === null || daysSinceLastTxn > 30;
            
            reports.dormant_account = {
              title: 'Dormant Account Report',
              is_dormant: isDormant,
              days_since_last_transaction: daysSinceLastTxn,
              last_transaction_date: latestTxnDate?.toLocaleDateString() || 'N/A',
              old_parcels: dormantParcels,
              old_parcels_count: dormantParcels.length,
              status: isDormant ? 'Dormant - No recent activity' : 'Active'
            };
            break;
            
          case 'dispute_adjustment':
            // Parcels with price adjustments (where sale differs significantly from costing)
            let adjustedParcels = [];
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const saleAmt = p.sale_amount || 0;
                  const costAmt = p.cost_amount || 0;
                  const diff = Math.abs(saleAmt - costAmt);
                  const pctDiff = costAmt > 0 ? (diff / costAmt) * 100 : 0;
                  // Flag if GP margin is unusual (negative or > 50%)
                  if (p.has_negative_gp || pctDiff > 50) {
                    adjustedParcels.push({
                      ...p,
                      adjustment_amount: saleAmt - costAmt,
                      adjustment_pct: pctDiff.toFixed(1)
                    });
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const saleAmt = p.sale_amount || 0;
                const costAmt = p.cost_amount || 0;
                const diff = Math.abs(saleAmt - costAmt);
                const pctDiff = costAmt > 0 ? (diff / costAmt) * 100 : 0;
                if (p.has_negative_gp || pctDiff > 50) {
                  adjustedParcels.push({
                    ...p,
                    adjustment_amount: saleAmt - costAmt,
                    adjustment_pct: pctDiff.toFixed(1)
                  });
                }
              });
            }
            
            reports.dispute_adjustment = {
              title: 'Dispute/Adjustment Report',
              items: adjustedParcels,
              count: adjustedParcels.length,
              total_adjustment: adjustedParcels.reduce((sum, p) => sum + (p.adjustment_amount || 0), 0)
            };
            break;
            
          case 'settlement_pending':
            // Parcels with 3rd party profit share pending settlement
            let pendingSettlements = [];
            const hasThirdParty = accountViewData?.profit_sharing?.third_percentage > 0;
            
            if (hasThirdParty) {
              if (accountViewMode === "payment") {
                data?.forEach(group => {
                  group.parcels?.forEach(p => {
                    if (p.third_party_share > 0) {
                      pendingSettlements.push({
                        tracking: p.tracking,
                        date: p.date,
                        sale_amount: p.sale_amount,
                        third_party_share: p.third_party_share,
                        third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                      });
                    }
                  });
                });
              } else {
                data?.forEach(p => {
                  if (p.third_party_share > 0) {
                    pendingSettlements.push({
                      tracking: p.tracking,
                      date: p.date,
                      sale_amount: p.sale_amount,
                      third_party_share: p.third_party_share,
                      third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                    });
                  }
                });
              }
            }
            
            // Also include third_partner_referrals if available
            if (accountViewData?.third_partner_referrals?.length > 0) {
              accountViewData.third_partner_referrals.forEach(ref => {
                pendingSettlements.push({
                  tracking: ref.tracking,
                  date: ref.date,
                  sale_amount: ref.sale_amount,
                  third_party_share: ref.amount,
                  third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                });
              });
            }
            
            reports.settlement_pending = {
              title: 'Settlement Pending Report',
              items: pendingSettlements,
              count: pendingSettlements.length,
              third_party_name: accountViewData?.profit_sharing?.third_name || 'Third Party',
              total_pending: pendingSettlements.reduce((sum, p) => sum + (p.third_party_share || 0), 0),
              has_third_party: hasThirdParty || accountViewData?.third_partner_referrals?.length > 0
            };
            break;
            
          case 'month_end_summary':
            // Comprehensive month-end summary
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            
            let monthlyDebits = 0, monthlyCredits = 0, monthlyParcels = 0;
            let prevMonthDebits = 0, prevMonthCredits = 0;
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const txnDate = p.date ? new Date(p.date) : null;
                  if (txnDate) {
                    const txnMonth = txnDate.getMonth();
                    const txnYear = txnDate.getFullYear();
                    
                    if (txnMonth === currentMonth && txnYear === currentYear) {
                      monthlyParcels++;
                      p.entries?.forEach(e => {
                        monthlyDebits += e.debit || 0;
                        monthlyCredits += e.credit || 0;
                      });
                    } else if ((txnMonth === currentMonth - 1 && txnYear === currentYear) || 
                               (currentMonth === 0 && txnMonth === 11 && txnYear === currentYear - 1)) {
                      p.entries?.forEach(e => {
                        prevMonthDebits += e.debit || 0;
                        prevMonthCredits += e.credit || 0;
                      });
                    }
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const txnDate = p.date ? new Date(p.date) : null;
                if (txnDate) {
                  const txnMonth = txnDate.getMonth();
                  const txnYear = txnDate.getFullYear();
                  
                  if (txnMonth === currentMonth && txnYear === currentYear) {
                    monthlyParcels++;
                    p.entries?.forEach(e => {
                      monthlyDebits += e.debit || 0;
                      monthlyCredits += e.credit || 0;
                    });
                  } else if ((txnMonth === currentMonth - 1 && txnYear === currentYear) || 
                             (currentMonth === 0 && txnMonth === 11 && txnYear === currentYear - 1)) {
                    p.entries?.forEach(e => {
                      prevMonthDebits += e.debit || 0;
                      prevMonthCredits += e.credit || 0;
                    });
                  }
                }
              });
            }
            
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                               'July', 'August', 'September', 'October', 'November', 'December'];
            
            reports.month_end_summary = {
              title: 'Month-End Summary',
              current_month: monthNames[currentMonth],
              current_year: currentYear,
              opening_balance: accountViewData?.opening_balance || 0,
              closing_balance: accountViewData?.closing_balance || 0,
              monthly_debits: monthlyDebits,
              monthly_credits: monthlyCredits,
              monthly_parcels: monthlyParcels,
              net_movement: monthlyDebits - monthlyCredits,
              prev_month_debits: prevMonthDebits,
              prev_month_credits: prevMonthCredits,
              month_over_month_change: monthlyDebits - prevMonthDebits
            };
            break;
        }
      });
      
      setAccountReportData(reports);
      setShowAccountReportDialog(false); // Close selection dialog
      setShowFullScreenReport(true); // Open full screen view
      toast.success(`Generated ${Object.keys(reports).length} reports - Opening full screen view`);
    } catch (error) {
      console.error("Report generation error:", error);
      toast.error("Failed to generate reports");
    } finally {
      setAccountReportLoading(false);
    }
  };

  // Export generated reports to Excel
  const exportReportsToExcel = () => {
    if (!accountReportData) return;
    
    const wb = XLSX.utils.book_new();
    
    // Outstanding Balance Sheet
    if (accountReportData.outstanding_balance) {
      const data = [
        ['Outstanding Balance Report'],
        ['Partner', accountViewPartner],
        [''],
        ['Metric', 'Value'],
        ['Total Debit', accountReportData.outstanding_balance.total_debit],
        ['Total Credit', accountReportData.outstanding_balance.total_credit],
        ['Net Balance', accountReportData.outstanding_balance.balance],
        ['Status', accountReportData.outstanding_balance.status]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Outstanding Balance');
    }
    
    // Profit Summary Sheet
    if (accountReportData.profit_summary) {
      const data = [
        ['Profit Share Summary'],
        ['Partner', accountViewPartner],
        [''],
        ['Category', 'Amount'],
        [`Self Share (${accountViewData?.profit_sharing?.self_percentage}%)`, accountReportData.profit_summary.self_share],
        [`Partner Share (${accountViewData?.profit_sharing?.partner_percentage}%)`, accountReportData.profit_summary.partner_share],
        ['Total GP', accountReportData.profit_summary.total_gp]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Profit Summary');
    }
    
    // Company Payments Sheet
    if (accountReportData.company_payments && accountReportData.company_payments.items?.length > 0) {
      const data = [
        ['Company Payments Report'],
        ['Total', accountReportData.company_payments.total || ''],
        ['Count', accountReportData.company_payments.count],
        [''],
        ['Payment Mode / Tracking', 'Amount', 'Parcel Count']
      ];
      accountReportData.company_payments.items.forEach(item => {
        data.push([
          item.payment_mode || item.tracking,
          item.payment_amount || item.sale_amount,
          item.parcel_count || 1
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Company Payments');
    }
    
    // Personal Collections Sheet
    if (accountReportData.personal_collections && accountReportData.personal_collections.items?.length > 0) {
      const data = [
        ['Personal Collections Report (Neutral)'],
        ['Total', accountReportData.personal_collections.total || ''],
        ['Count', accountReportData.personal_collections.count],
        [''],
        ['Payment Mode / Tracking', 'Amount']
      ];
      accountReportData.personal_collections.items.forEach(item => {
        data.push([item.payment_mode || item.tracking, item.payment_amount || item.sale_amount]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Personal Collections');
    }
    
    // Multi-Parcel Payments Sheet
    if (accountReportData.multi_parcel && accountReportData.multi_parcel.items?.length > 0) {
      const data = [
        ['Multi-Parcel Payments'],
        ['Count', accountReportData.multi_parcel.count],
        [''],
        ['Payment Mode', 'Amount', 'Parcel Count']
      ];
      accountReportData.multi_parcel.items.forEach(item => {
        data.push([item.payment_mode, item.payment_amount, item.parcel_count]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Multi-Parcel Payments');
    }
    
    // Negative GP Sheet
    if (accountReportData.negative_gp && accountReportData.negative_gp.items?.length > 0) {
      const data = [
        ['Negative GP Report (Loss)'],
        ['Count', accountReportData.negative_gp.count],
        [''],
        ['Tracking', 'GP', 'Date']
      ];
      accountReportData.negative_gp.items.forEach(item => {
        data.push([item.tracking, item.gp, item.date]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Negative GP');
    }
    
    // No Invoice Sheet
    if (accountReportData.no_invoice && accountReportData.no_invoice.items?.length > 0) {
      const data = [
        ['No Invoice Report'],
        ['Count', accountReportData.no_invoice.count],
        [''],
        ['Tracking', 'Date']
      ];
      accountReportData.no_invoice.items.forEach(item => {
        data.push([item.tracking, item.date]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'No Invoice');
    }
    
    // High Value Sheet
    if (accountReportData.high_value && accountReportData.high_value.items?.length > 0) {
      const data = [
        ['High Value Transactions (≥₹10K)'],
        ['Count', accountReportData.high_value.count],
        [''],
        ['Tracking / Payment', 'Amount']
      ];
      accountReportData.high_value.items.forEach(item => {
        data.push([item.tracking || item.payment_mode, item.payment_amount || item.sale_amount]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'High Value');
    }
    
    // Pending Payment Sheet
    if (accountReportData.pending_payment && accountReportData.pending_payment.items?.length > 0) {
      const data = [
        ['Pending Payment Report'],
        ['Count', accountReportData.pending_payment.count],
        [''],
        ['Tracking / Description', 'Parcel Count']
      ];
      accountReportData.pending_payment.items.forEach(item => {
        data.push([item.tracking || item.payment_mode || 'No Payment', item.parcel_count || 1]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Pending Payment');
    }
    
    // Dormant Account Sheet
    if (accountReportData.dormant_account) {
      const data = [
        ['Dormant Account Report'],
        ['Partner', accountViewPartner],
        [''],
        ['Metric', 'Value'],
        ['Account Status', accountReportData.dormant_account.status],
        ['Days Since Last Transaction', accountReportData.dormant_account.days_since_last_transaction || 'N/A'],
        ['Last Transaction Date', accountReportData.dormant_account.last_transaction_date],
        ['Old Parcels (>30 days)', accountReportData.dormant_account.old_parcels_count]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Dormant Account');
    }
    
    // Dispute/Adjustment Sheet
    if (accountReportData.dispute_adjustment && accountReportData.dispute_adjustment.items?.length > 0) {
      const data = [
        ['Dispute/Adjustment Report'],
        ['Total Adjustments', accountReportData.dispute_adjustment.total_adjustment],
        ['Count', accountReportData.dispute_adjustment.count],
        [''],
        ['Tracking', 'Sale Amount', 'Cost Amount', 'Adjustment', 'Adjustment %', 'Date']
      ];
      accountReportData.dispute_adjustment.items.forEach(item => {
        data.push([
          item.tracking,
          item.sale_amount || 0,
          item.cost_amount || 0,
          item.adjustment_amount || 0,
          item.adjustment_pct + '%',
          item.date || ''
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Disputes');
    }
    
    // Settlement Pending Sheet
    if (accountReportData.settlement_pending && accountReportData.settlement_pending.items?.length > 0) {
      const data = [
        ['Settlement Pending Report'],
        ['Third Party', accountReportData.settlement_pending.third_party_name],
        ['Total Pending', accountReportData.settlement_pending.total_pending],
        ['Count', accountReportData.settlement_pending.count],
        [''],
        ['Tracking', 'Sale Amount', 'Third Party Share', 'Date']
      ];
      accountReportData.settlement_pending.items.forEach(item => {
        data.push([
          item.tracking,
          item.sale_amount || 0,
          item.third_party_share || 0,
          item.date || ''
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Settlement Pending');
    }
    
    // Month-End Summary Sheet
    if (accountReportData.month_end_summary) {
      const data = [
        ['Month-End Summary Report'],
        ['Partner', accountViewPartner],
        ['Period', `${accountReportData.month_end_summary.current_month} ${accountReportData.month_end_summary.current_year}`],
        [''],
        ['Metric', 'Value'],
        ['Opening Balance', accountReportData.month_end_summary.opening_balance],
        ['Monthly Debits', accountReportData.month_end_summary.monthly_debits],
        ['Monthly Credits', accountReportData.month_end_summary.monthly_credits],
        ['Net Movement', accountReportData.month_end_summary.net_movement],
        ['Monthly Parcels', accountReportData.month_end_summary.monthly_parcels],
        ['Closing Balance', accountReportData.month_end_summary.closing_balance],
        [''],
        ['Previous Month Comparison'],
        ['Prev Month Debits', accountReportData.month_end_summary.prev_month_debits],
        ['Month-over-Month Change', accountReportData.month_end_summary.month_over_month_change]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Month-End Summary');
    }
    
    // Download
    const filename = `AccountReports_${accountViewPartner}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Excel report downloaded');
  };

  // Print reports (opens print dialog)
  const printReports = () => {
    window.print();
  };

  // Toggle report selection
  const toggleAccountReportSelection = (reportId) => {
    setSelectedAccountReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  // Get report count for a type
  const getAccountReportCount = (reportId) => {
    if (!accountViewData) return 0;
    const data = accountViewMode === "payment" ? accountViewData.payment_groups : accountViewData.ledger_entries;
    if (!data) return 0;
    
    switch(reportId) {
      case 'negative_gp':
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => { if (p.has_negative_gp) count++; }));
          return count;
        }
        return data.filter(p => p.has_negative_gp).length;
      case 'no_invoice':
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => { if (p.has_no_invoice) count++; }));
          return count;
        }
        return data.filter(p => p.has_no_invoice).length;
      case 'high_value':
        if (accountViewMode === "payment") {
          return data.filter(g => g.payment_amount >= 10000).length;
        }
        return data.filter(p => (p.sale_amount || 0) >= 10000).length;
      case 'company_payments':
        if (accountViewMode === "payment") {
          return data.filter(g => g.is_company_payment).length;
        }
        return data.filter(p => p.entries?.some(e => e.category === 'esr_payment')).length;
      case 'personal_collections':
        if (accountViewMode === "payment") {
          return data.filter(g => !g.is_company_payment && g.payment_amount > 0).length;
        }
        return data.filter(p => p.entries?.some(e => e.category === 'personal')).length;
      case 'multi_parcel':
        return accountViewMode === "payment" ? data.filter(g => g.parcel_count > 1).length : 0;
      case 'pending_payment':
        if (accountViewMode === "payment") {
          return data.filter(g => g.payment_amount === 0).length;
        }
        return data.filter(p => !p.entries?.some(e => e.category === 'esr_payment' || e.category === 'personal')).length;
      case 'dormant_account':
        // Return 1 if account is dormant (for selection purposes)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        let latestDate = null;
        if (accountViewMode === "payment") {
          data.forEach(g => g.parcels?.forEach(p => {
            const d = p.date ? new Date(p.date) : null;
            if (d && (!latestDate || d > latestDate)) latestDate = d;
          }));
        } else {
          data.forEach(p => {
            const d = p.date ? new Date(p.date) : null;
            if (d && (!latestDate || d > latestDate)) latestDate = d;
          });
        }
        return (!latestDate || latestDate < thirtyDaysAgo) ? 1 : 0;
      case 'dispute_adjustment':
        // Parcels with unusual GP margins
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => {
            const saleAmt = p.sale_amount || 0;
            const costAmt = p.cost_amount || 0;
            const pctDiff = costAmt > 0 ? (Math.abs(saleAmt - costAmt) / costAmt) * 100 : 0;
            if (p.has_negative_gp || pctDiff > 50) count++;
          }));
          return count;
        }
        return data.filter(p => {
          const saleAmt = p.sale_amount || 0;
          const costAmt = p.cost_amount || 0;
          const pctDiff = costAmt > 0 ? (Math.abs(saleAmt - costAmt) / costAmt) * 100 : 0;
          return p.has_negative_gp || pctDiff > 50;
        }).length;
      case 'settlement_pending':
        // Count of parcels with 3rd party share pending
        const hasThird = accountViewData?.profit_sharing?.third_percentage > 0;
        const thirdReferrals = accountViewData?.third_partner_referrals?.length || 0;
        if (!hasThird && thirdReferrals === 0) return 0;
        
        let pendingCount = thirdReferrals;
        if (hasThird) {
          if (accountViewMode === "payment") {
            data.forEach(g => g.parcels?.forEach(p => {
              if (p.third_party_share > 0) pendingCount++;
            }));
          } else {
            pendingCount += data.filter(p => p.third_party_share > 0).length;
          }
        }
        return pendingCount;
      case 'month_end_summary':
        // Always return 1 as it's a summary report
        return 1;
      default:
        return 0;
    }
  };

  // Fetch manual entries for a client
  const fetchManualEntries = async (clientCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/${clientCode}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setManualEntries(data.entries || []);
      }
    } catch (error) {
      console.error("Error fetching manual entries:", error);
    }
  };

  // Create or Update manual entry
  const handleCreateManualEntry = async () => {
    const clientCode = manualEntryClientCode || selectedClient;
    
    if (!clientCode) {
      toast.error("Please select a client code");
      return;
    }
    if (!manualEntryAmount || !manualEntryDate || !manualEntryNarration) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/manual-entry`;
      let method = "POST";
      
      if (editingEntryId) {
        url = `${BACKEND_URL}/api/ledger-reconciliation/manual-entry/${editingEntryId}`;
        method = "PUT";
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_code: clientCode.toUpperCase(),
          entry_type: manualEntryType,
          amount: parseFloat(manualEntryAmount),
          date: manualEntryDate,
          narration: manualEntryNarration,
          tracking_number: manualEntryTracking || null
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(editingEntryId ? "Entry updated successfully" : data.message);
        setShowManualEntryDialog(false);
        resetManualEntryForm();
        if (selectedClient) {
          fetchClientDetails(selectedClient);
          fetchManualEntries(selectedClient);
        }
        fetchSummaryData(currentPage);
      } else {
        toast.error(data.detail || "Failed to save entry");
      }
    } catch (error) {
      console.error("Error saving manual entry:", error);
      toast.error("Failed to save manual entry");
    }
  };

  // Delete manual entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/manual-entry/${entryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Entry deleted successfully");
        if (selectedClient) {
          fetchClientDetails(selectedClient);
          fetchManualEntries(selectedClient);
        }
        fetchSummaryData(currentPage);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete entry");
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    }
  };

  // Edit entry - populate form
  const handleEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setManualEntryType(entry.entry_type);
    setManualEntryClientCode(entry.client_code);
    setManualEntryAmount(entry.amount.toString());
    setManualEntryDate(entry.date);
    setManualEntryNarration(entry.narration);
    setManualEntryTracking(entry.tracking_number || "");
    setShowEntriesDialog(false);
    setShowManualEntryDialog(true);
  };

  const resetManualEntryForm = () => {
    setManualEntryClientCode("");
    setManualEntryAmount("");
    setManualEntryDate("");
    setManualEntryNarration("");
    setManualEntryTracking("");
    setEditingEntryId(null);
  };

  // Simple Debit Save Handler
  const handleSaveDebit = async () => {
    const clientCode = debitClientCode || selectedClient;
    if (!clientCode) {
      toast.error("Please select a client code");
      return;
    }
    if (!debitAmount || parseFloat(debitAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/manual-entry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_code: clientCode.toUpperCase(),
          entry_type: "take",
          amount: parseFloat(debitAmount),
          date: debitDate || new Date().toISOString().split("T")[0],
          narration: debitNarration || "Manual debit entry"
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success("Debit entry added successfully");
        setShowDebitDialog(false);
        // Store the client code before clearing for refresh
        const savedClientCode = clientCode.toUpperCase();
        setDebitClientCode("");
        setDebitAmount("");
        setDebitDate("");
        setDebitNarration("");
        // Always refresh summary data
        fetchSummaryData(currentPage);
        // Refresh client details if we're viewing the affected client's drill-down
        if (selectedClient && selectedClient.toUpperCase() === savedClientCode) {
          fetchClientDetails(selectedClient);
          fetchManualEntries(selectedClient);
        } else if (selectedClient) {
          // Also refresh current view if different client is selected
          fetchClientDetails(selectedClient);
        }
      } else {
        toast.error(data.detail || "Failed to add debit entry");
      }
    } catch (error) {
      console.error("Error saving debit:", error);
      toast.error("Failed to save debit entry");
    }
  };

  // Fetch Partner Collections (invoice payments received by partner from customers)
  const fetchPartnerCollections = async (clientCode = null) => {
    setCollectionsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/partner-collections`;
      const params = new URLSearchParams();
      if (clientCode) params.append("client_code", clientCode);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (collectionsFilter && collectionsFilter !== "all") params.append("status", collectionsFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setPartnerCollections(data);
      } else {
        toast.error(data.detail || "Failed to fetch partner collections");
      }
    } catch (error) {
      toast.error("Failed to fetch partner collections");
      console.error(error);
    } finally {
      setCollectionsLoading(false);
    }
  };

  // Export Partner Collections to Excel
  const exportPartnerCollectionsToExcel = () => {
    if (!partnerCollections?.collections?.length) {
      toast.error("No collections data to export");
      return;
    }

    try {
      // Prepare data for export
      const exportData = partnerCollections.collections.map(coll => ({
        "Tracking": coll.tracking,
        "Partner Code": coll.partner_code,
        "Customer": coll.customer_name || "-",
        "Order Date": coll.order_date || "-",
        "Collection Date": coll.collection_date || "-",
        "Invoice Amount (₹)": coll.invoice_amount || 0,
        "Costing (₹)": coll.costing || 0,
        "GP (₹)": coll.gp || 0,
        "Payment to Company (₹)": coll.payment_to_company || 0,
        "Match Status": coll.matched_status === "matched" ? "Matched" : "Unmatched",
        "Payment Mode": coll.payment_mode || "-"
      }));

      // Add summary row
      const summary = partnerCollections.summary;
      exportData.push({});  // Empty row
      exportData.push({
        "Tracking": "SUMMARY",
        "Partner Code": "",
        "Customer": "",
        "Order Date": "",
        "Collection Date": "",
        "Invoice Amount (₹)": summary.total_invoice_amount,
        "Costing (₹)": "",
        "GP (₹)": "",
        "Payment to Company (₹)": summary.total_payment_to_company,
        "Match Status": `${summary.matched_count} Matched / ${summary.unmatched_count} Unmatched`,
        "Payment Mode": ""
      });
      exportData.push({
        "Tracking": "Pending Settlement",
        "Invoice Amount (₹)": summary.pending_settlement
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Partner Collections");

      // Auto-size columns
      const maxWidths = {};
      exportData.forEach(row => {
        Object.keys(row).forEach(key => {
          const value = String(row[key] || "");
          maxWidths[key] = Math.max(maxWidths[key] || key.length, value.length);
        });
      });
      ws["!cols"] = Object.keys(maxWidths).map(key => ({ wch: Math.min(maxWidths[key] + 2, 30) }));

      // Generate filename with date
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `Partner_Collections_${selectedClient || "All"}_${dateStr}.xlsx`;

      // Save file
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);

      toast.success(`Exported ${partnerCollections.collections.length} records to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  // Partner Ledger Upload handlers
  const handlePartnerLedgerUpload = async () => {
    if (!partnerLedgerFile) {
      toast.error("Please select an Excel file");
      return;
    }

    setPartnerLedgerUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", partnerLedgerFile);
      formData.append("duplicate_handling", duplicateHandling);

      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setPartnerLedgerResults(data);
        // Initialize selection state - all entries selected by default
        const selections = {};
        data.partners.forEach(partner => {
          selections[partner.partner_code] = {};
          partner.entries.forEach((entry, idx) => {
            selections[partner.partner_code][idx] = true;
          });
        });
        setSelectedPartnerEntries(selections);
        // Expand first partner by default
        if (data.partners.length > 0) {
          setExpandedPartners(new Set([data.partners[0].partner_code]));
        }
        // Load workflow summary
        if (data.upload_id) {
          setTimeout(() => loadWorkflowSummary(), 500);
        }
        toast.success(`Parsed ${data.sheets_processed} sheets, ${data.total_payments_detected} payments detected`);
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Partner ledger upload error:", error);
      toast.error("Upload failed");
    } finally {
      setPartnerLedgerUploading(false);
    }
  };

  const handleProcessPartnerLedger = async () => {
    if (!partnerLedgerResults?.upload_id) {
      toast.error("No upload to process");
      return;
    }

    // Build selected entries list
    const selectedEntries = [];
    partnerLedgerResults.partners.forEach(partner => {
      partner.entries.forEach((entry, idx) => {
        if (selectedPartnerEntries[partner.partner_code]?.[idx]) {
          selectedEntries.push({
            ...entry,
            partner_code: partner.partner_code,
            gp_ratios: partner.gp_ratios
          });
        }
      });
    });

    if (selectedEntries.length === 0) {
      toast.error("Please select at least one entry to process");
      return;
    }

    setPartnerLedgerProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/process/${partnerLedgerResults.upload_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(selectedEntries)
      });

      const data = await response.json();
      if (response.ok) {
        const salesMsg = data.sales_updated > 0 ? `, ${data.sales_updated} sales updated` : "";
        toast.success(`Created ${data.entries_created} entries, ${data.gp_entries_created} GP splits${salesMsg}`);
        setShowPartnerLedgerUpload(false);
        setPartnerLedgerResults(null);
        setPartnerLedgerFile(null);
        setSelectedPartnerEntries({});
        // Refresh data
        fetchSummaryData(currentPage);
      } else {
        toast.error(data.detail || "Processing failed");
      }
    } catch (error) {
      console.error("Partner ledger process error:", error);
      toast.error("Processing failed");
    } finally {
      setPartnerLedgerProcessing(false);
    }
  };

  const togglePartnerExpand = (partnerCode) => {
    const newExpanded = new Set(expandedPartners);
    if (newExpanded.has(partnerCode)) {
      newExpanded.delete(partnerCode);
    } else {
      newExpanded.add(partnerCode);
    }
    setExpandedPartners(newExpanded);
  };

  const toggleEntrySelection = (partnerCode, entryIdx) => {
    setSelectedPartnerEntries(prev => ({
      ...prev,
      [partnerCode]: {
        ...prev[partnerCode],
        [entryIdx]: !prev[partnerCode]?.[entryIdx]
      }
    }));
  };

  const toggleAllEntries = (partnerCode, entries, selectAll) => {
    setSelectedPartnerEntries(prev => ({
      ...prev,
      [partnerCode]: entries.reduce((acc, _, idx) => {
        acc[idx] = selectAll;
        return acc;
      }, {})
    }));
  };

  const getSelectedCount = (partnerCode, entries) => {
    if (!selectedPartnerEntries[partnerCode]) return 0;
    return entries.filter((_, idx) => selectedPartnerEntries[partnerCode][idx]).length;
  };

  // Inline editing handlers
  const handleCellDoubleClick = (partnerCode, entryIndex, field, currentValue) => {
    // Check if user can edit this field
    const adminOnlyFields = ["amount", "entry_type", "tracking", "date"];
    if (!canEditAll && adminOnlyFields.includes(field)) {
      toast.error(`Only admins can edit '${field}'`);
      return;
    }
    if (!canEditLimited) {
      toast.error("You don't have permission to edit entries");
      return;
    }
    
    setEditingCell({ partnerCode, entryIndex, field });
    setEditingValue(currentValue || "");
  };

  const handleCellEditSave = async () => {
    if (!editingCell || !partnerLedgerResults?.upload_id) return;
    
    const { partnerCode, entryIndex, field } = editingCell;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/entry`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          partner_code: partnerCode,
          entry_index: entryIndex,
          field: field,
          new_value: editingValue
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        // Update local state
        setPartnerLedgerResults(prev => {
          const newResults = { ...prev };
          const partnerIdx = newResults.partners.findIndex(p => p.partner_code === partnerCode);
          if (partnerIdx !== -1) {
            newResults.partners[partnerIdx].entries[entryIndex][field] = editingValue;
            newResults.partners[partnerIdx].entries[entryIndex].edited = true;
            newResults.partners[partnerIdx].entries[entryIndex].edited_by = user?.username || "admin";
            
            // Auto-update related fields for payment_mode
            if (field === "payment_mode") {
              const companyModes = ["ESR", "Maxwell", "NEFT", "RTGS", "IMPS", "Wire", "UK Account", "Bank Transfer"];
              const isCompany = companyModes.some(cm => editingValue?.toLowerCase()?.includes(cm.toLowerCase()));
              newResults.partners[partnerIdx].entries[entryIndex].is_company_payment = isCompany;
              newResults.partners[partnerIdx].entries[entryIndex].entry_type = isCompany ? "give" : "take";
              
              if (editingValue && newResults.partners[partnerIdx].entries[entryIndex].status === "unknown_receiver") {
                newResults.partners[partnerIdx].entries[entryIndex].status = "matched";
              }
            }
          }
          return newResults;
        });
        
        toast.success(`Updated ${field}: ${data.old_value || "empty"} → ${editingValue}`);
        setEditingCell(null);
        setEditingValue("");
      } else {
        toast.error(data.detail || "Update failed");
      }
    } catch (error) {
      console.error("Cell edit error:", error);
      toast.error("Update failed");
    }
  };

  const handleCellEditCancel = () => {
    setEditingCell(null);
    setEditingValue("");
  };

  const handleBulkEdit = async () => {
    if (!bulkEditMode || !partnerLedgerResults?.upload_id) {
      toast.error("Please select a payment mode");
      return;
    }
    
    // Get all selected entries
    const entriesToEdit = [];
    partnerLedgerResults.partners.forEach(partner => {
      partner.entries.forEach((entry, idx) => {
        if (selectedPartnerEntries[partner.partner_code]?.[idx]) {
          entriesToEdit.push({
            partner_code: partner.partner_code,
            entry_index: idx
          });
        }
      });
    });
    
    if (entriesToEdit.length === 0) {
      toast.error("Please select at least one entry");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/entries/bulk`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          entries: entriesToEdit,
          field: "payment_mode",
          new_value: bulkEditMode
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        // Refresh the data
        const refreshResponse = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          // Preserve upload_id when refreshing - merge it with results
          const resultsWithId = {
            ...(refreshData.results || refreshData),
            upload_id: refreshData.upload_id || partnerLedgerResults?.upload_id
          };
          setPartnerLedgerResults(resultsWithId);
        }
        
        toast.success(`Updated ${data.updated_count} entries to "${bulkEditMode}"`);
        setShowBulkEditDialog(false);
        setBulkEditMode("");
      } else {
        toast.error(data.detail || "Bulk update failed");
      }
    } catch (error) {
      console.error("Bulk edit error:", error);
      toast.error("Bulk update failed");
    }
  };

  const loadAuditLogs = async () => {
    if (!partnerLedgerResults?.upload_id) return;
    
    setAuditLogsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Load audit logs error:", error);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  // Load workflow summary
  const loadWorkflowSummary = async () => {
    if (!partnerLedgerResults?.upload_id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/workflow-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkflowSummary(data);
      }
    } catch (error) {
      console.error("Load workflow summary error:", error);
    }
  };

  // Verify selected entries
  const handleVerifyEntries = async (action = "verify") => {
    if (!partnerLedgerResults?.upload_id) return;
    
    // Get all selected entries
    const entriesToVerify = [];
    partnerLedgerResults.partners.forEach(partner => {
      partner.entries.forEach((entry, idx) => {
        if (selectedPartnerEntries[partner.partner_code]?.[idx]) {
          entriesToVerify.push({
            partner_code: partner.partner_code,
            entry_index: idx
          });
        }
      });
    });
    
    if (entriesToVerify.length === 0) {
      toast.error("Please select at least one entry");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/verify`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          entries: entriesToVerify,
          action: action
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        // Refresh the data
        const refreshResponse = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          // Preserve upload_id when refreshing
          const resultsWithId = {
            ...(refreshData.results || refreshData),
            upload_id: refreshData.upload_id || partnerLedgerResults?.upload_id
          };
          setPartnerLedgerResults(resultsWithId);
        }
        loadWorkflowSummary();
        
        toast.success(`${action === "verify" ? "Verified" : "Unverified"} ${data.updated_count} entries`);
      } else {
        toast.error(data.detail || `${action} failed`);
      }
    } catch (error) {
      console.error("Verify error:", error);
      toast.error(`${action} failed`);
    }
  };

  // Approve selected entries
  const handleApproveEntries = async (action = "approve") => {
    if (!partnerLedgerResults?.upload_id) return;
    
    // Get all selected entries
    const entriesToApprove = [];
    partnerLedgerResults.partners.forEach(partner => {
      partner.entries.forEach((entry, idx) => {
        if (selectedPartnerEntries[partner.partner_code]?.[idx]) {
          entriesToApprove.push({
            partner_code: partner.partner_code,
            entry_index: idx
          });
        }
      });
    });
    
    if (entriesToApprove.length === 0) {
      toast.error("Please select at least one entry");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/approve`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          entries: entriesToApprove,
          action: action,
          reject_reason: action === "reject" ? rejectReason : ""
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        // Refresh the data
        const refreshResponse = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          // Preserve upload_id when refreshing
          const resultsWithId = {
            ...(refreshData.results || refreshData),
            upload_id: refreshData.upload_id || partnerLedgerResults?.upload_id
          };
          setPartnerLedgerResults(resultsWithId);
        }
        loadWorkflowSummary();
        
        toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${data.updated_count} entries`);
        setShowRejectDialog(false);
        setRejectReason("");
      } else {
        toast.error(data.detail || `${action} failed`);
      }
    } catch (error) {
      console.error("Approve error:", error);
      toast.error(`${action} failed`);
    }
  };

  // Sync approved entries to Sales & Profit
  const handleSyncToSales = async () => {
    if (!partnerLedgerResults?.upload_id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/upload/${partnerLedgerResults.upload_id}/sync-to-sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(`Synced ${data.updated_count} records to Sales & Profit`);
        if (data.not_found_count > 0) {
          toast.warning(`${data.not_found_count} trackings not found in Sales`);
        }
      } else {
        toast.error(data.detail || "Sync failed");
      }
    } catch (error) {
      console.error("Sync to sales error:", error);
      toast.error("Sync failed");
    }
  };

  // Filter entries by workflow status
  const getWorkflowFilteredEntries = (entries) => {
    let filtered = getFilteredEntries(entries);
    if (workflowFilter === "all") return filtered;
    return filtered.filter(e => (e.workflow_status || "pending") === workflowFilter);
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 text-blue-600" /> 
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  // Sort function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Use monthly sorting for date fields (month-year grouping)
      const dateFields = ['date', 'order_date', 'sale_date', 'payment_date', 'created_at', 'year', 'month', 'day'];
      if (dateFields.includes(key)) {
        return compareByMonthYear(aVal, bVal, direction);
      }
      
      // Numeric comparison for number fields
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison for other fields
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Process client data - server-side filtering is handled, just sort locally if needed
  const filteredClients = useMemo(() => {
    if (!summaryData?.clients) return [];
    // Server already handles filtering, just return the data
    // The sortConfig is also sent to server, but we can still apply client-side sorting as backup
    return sortData([...summaryData.clients], sortConfig.key, sortConfig.direction);
  }, [summaryData, sortConfig]);

  // Process tracking data by date hierarchy
  const processedTrackingData = useMemo(() => {
    if (!clientDetails?.tracking_verification) return { years: {}, flat: [], notifications: [] };
    
    const trackings = clientDetails.tracking_verification;
    const years = {};
    const notifications = []; // For shifted, cancelled, and replaced orders ONLY (zero cost items)
    
    trackings.forEach(track => {
      const dateStr = track.date || "";
      let year = "Unknown";
      let month = "Unknown";
      let day = "Unknown";
      
      // Check if this is a special status order (shifted, cancelled, replaced)
      // NOTE: Orders with just date issues are NOT special - they are valid orders!
      const isShifted = track.is_shifted || track.status === "SHIFTED ORDER";
      const isCancelled = track.is_cancelled || track.status === "CANCELLED";
      const isReplaced = track.is_strikethrough || track.tracking_replaced;
      
      // Only truly special status orders (with zero cost) go to notifications
      const isNotification = (isShifted || isCancelled || isReplaced) && (track.costing === 0 || !track.costing);
      
      // Parse date if available
      if (dateStr && dateStr.length === 6) {
        day = dateStr.substring(0, 2);
        month = dateStr.substring(2, 4);
        year = "20" + dateStr.substring(4, 6);
      } else if (dateStr && dateStr.includes("-")) {
        const parts = dateStr.split("-");
        year = parts[0];
        month = parts[1];
        day = parts[2];
      }
      
      // Add notification type to track
      if (isShifted && track.costing === 0) {
        track.notification_type = "shifted";
        track.notification_label = `Shifted to ${track.shifted_to_service || "other service"}`;
      } else if (isCancelled && track.costing === 0) {
        track.notification_type = "cancelled";
        track.notification_label = "Cancelled order";
      } else if (isReplaced && track.costing === 0) {
        track.notification_type = "replaced";
        track.notification_label = `Tracking replaced → ${track.superseded_by || "new tracking"}`;
      }
      
      // Only add to notifications list if it's a true notification (zero cost special status)
      if (isNotification) {
        notifications.push(track);
      }
      
      if (!years[year]) {
        years[year] = { year, months: {}, total_costing: 0, total_sell: 0, total_gp: 0, total_gp_self: 0, total_payment: 0, total_debit: 0, total_credit: 0, balance: 0, count: 0, error_count: 0, notification_count: 0 };
      }
      
      if (!years[year].months[month]) {
        years[year].months[month] = { month, days: {}, total_costing: 0, total_sell: 0, total_gp: 0, total_gp_self: 0, total_payment: 0, total_debit: 0, total_credit: 0, balance: 0, count: 0, error_count: 0, notification_count: 0 };
      }
      
      if (!years[year].months[month].days[day]) {
        years[year].months[month].days[day] = { day, parcels: [], total_costing: 0, total_sell: 0, total_gp: 0, total_gp_self: 0, total_payment: 0, total_debit: 0, total_credit: 0, balance: 0, count: 0, error_count: 0, notification_count: 0 };
      }
      
      years[year].months[month].days[day].parcels.push(track);
      
      // For notification items (shifted/cancelled/replaced with zero cost), don't add to financial totals
      const shouldExcludeFromTotals = isNotification;
      
      const costing = shouldExcludeFromTotals ? 0 : (track.costing || 0);
      const sell = shouldExcludeFromTotals ? 0 : (track.sell_price || 0);
      const gp = shouldExcludeFromTotals ? 0 : (track.gp || 0);
      const gpSelf = shouldExcludeFromTotals ? 0 : (track.gp_self || 0);
      // Only count payment_received if this is the first parcel in a payment group
      // Non-first parcels have payment_ref set and payment_received = 0
      const isFirstInPaymentGroup = track.is_first_in_payment_group !== false;
      const payment = shouldExcludeFromTotals ? 0 : (isFirstInPaymentGroup ? (track.payment_received || 0) : 0);
      const isError = (track.status === "error" ? 1 : 0) + (track.error_count || 0);
      const isNotificationItem = isNotification ? 1 : 0;
      
      // Calculate debit (costing + gp_self + manual debits) and credit (payment + manual credits) for balance
      // Manual entries contribute to debit/credit but NOT to costing
      const manualDebit = track.manual_debit_amount || 0;
      const manualCredit = track.manual_credit_amount || 0;
      const debit = costing + gpSelf + manualDebit;
      const credit = payment + manualCredit;
      
      years[year].months[month].days[day].total_costing += costing;
      years[year].months[month].days[day].total_sell += sell;
      years[year].months[month].days[day].total_gp += gp;
      years[year].months[month].days[day].total_gp_self += gpSelf;
      years[year].months[month].days[day].total_payment += payment;
      years[year].months[month].days[day].total_debit += debit;
      years[year].months[month].days[day].total_credit += credit;
      years[year].months[month].days[day].count += track.is_manual_entry ? 0 : 1;
      years[year].months[month].days[day].error_count += isError;
      years[year].months[month].days[day].notification_count += isNotificationItem;
      
      years[year].months[month].total_costing += costing;
      years[year].months[month].total_sell += sell;
      years[year].months[month].total_gp += gp;
      years[year].months[month].total_gp_self += gpSelf;
      years[year].months[month].total_payment += payment;
      years[year].months[month].total_debit += debit;
      years[year].months[month].total_credit += credit;
      years[year].months[month].count += track.is_manual_entry ? 0 : 1;
      years[year].months[month].error_count += isError;
      years[year].months[month].notification_count += isNotificationItem;
      
      years[year].total_costing += costing;
      years[year].total_sell += sell;
      years[year].total_gp += gp;
      years[year].total_gp_self += gpSelf;
      years[year].total_payment += payment;
      years[year].total_debit += debit;
      years[year].total_credit += credit;
      years[year].count += track.is_manual_entry ? 0 : 1;
      years[year].error_count += isError;
      years[year].notification_count += isNotificationItem;
    });
    
    // Ledger (Sales): balance runs on SALES − CREDIT (user rule).
    Object.values(years).forEach(year => {
      year.balance = (year.total_sell || 0) - year.total_credit;
      Object.values(year.months).forEach(month => {
        month.balance = (month.total_sell || 0) - month.total_credit;
        Object.values(month.days).forEach(day => {
          day.balance = (day.total_sell || 0) - day.total_credit;
        });
      });
    });
    
    return { years, flat: trackings, notifications };
  }, [clientDetails]);

  const filterByError = (items) => {
    if (errorFilter === "all") return items;
    if (errorFilter === "errors") return items.filter(i => i.error_count > 0 || i.status === "error");
    if (errorFilter === "no-errors") return items.filter(i => (i.error_count === 0 || i.error_count === undefined) && i.status !== "error");
    return items;
  };

  // Navigation functions
  const goToClient = (clientCode) => {
    setSelectedClient(clientCode);
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setViewLevel("year");
  };

  const goToYear = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    setViewLevel("month");
  };

  const goToMonth = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    setViewLevel("day");
  };

  const goToDay = (day) => {
    setSelectedDay(day);
    setViewLevel("parcels");
  };

  // Toggle expanded row to show products for a tracking
  const toggleProductsExpand = async (tracking) => {
    const newExpanded = new Set(expandedRows);
    
    if (newExpanded.has(tracking)) {
      newExpanded.delete(tracking);
      setExpandedRows(newExpanded);
      return;
    }
    
    // Fetch products if not already loaded
    if (!trackingProducts[tracking]) {
      setLoadingProducts(prev => ({ ...prev, [tracking]: true }));
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/ledger-reconciliation/tracking/${encodeURIComponent(tracking)}/products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          setTrackingProducts(prev => ({ ...prev, [tracking]: data }));
        } else {
          toast.error("Failed to load product details");
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Error loading products");
      } finally {
        setLoadingProducts(prev => ({ ...prev, [tracking]: false }));
      }
    }
    
    newExpanded.add(tracking);
    setExpandedRows(newExpanded);
  };

  const goBack = () => {
    // Clear search navigation state when going back
    if (searchNavigated) {
      setSearchNavigated(false);
      setGlobalSearchQuery("");
      setGlobalSearchResults(null);
      setSearchedTracking(null);
    }
    
    if (viewLevel === "parcels") {
      // If we were in search mode, go back to summary
      if (searchedTracking) {
        setSearchedTracking(null);
        setSelectedClient(null);
        setSelectedYear(null);
        setSelectedMonth(null);
        setSelectedDay(null);
        setClientDetails(null);
        setViewLevel("summary");
      } else {
        setSelectedDay(null);
        setViewLevel("day");
      }
      // Clear expanded rows when navigating back
      setExpandedRows(new Set());
    } else if (viewLevel === "day") {
      setSelectedMonth(null);
      setViewLevel("month");
    } else if (viewLevel === "month") {
      setSelectedYear(null);
      setViewLevel("year");
    } else if (viewLevel === "year") {
      setSelectedClient(null);
      setClientDetails(null);
      setViewLevel("summary");
    }
  };

  // Global search function
  const performGlobalSearch = async (query) => {
    if (!query || query.length < 2) {
      setGlobalSearchResults(null);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    setShowSearchResults(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ledger-reconciliation/search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setGlobalSearchResults(data);
      } else {
        toast.error("Search failed");
        setGlobalSearchResults(null);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search error");
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (globalSearchQuery.length >= 2) {
        performGlobalSearch(globalSearchQuery);
      } else {
        setGlobalSearchResults(null);
        setShowSearchResults(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [globalSearchQuery]);

  // Navigate to search result
  const navigateToSearchResult = async (result) => {
    setShowSearchResults(false);
    setSearchNavigated(true);
    
    const nav = result.navigation;
    
    if (result.type === "client") {
      // Navigate to client's year view
      await fetchClientDetails(nav.client);
      setSelectedClient(nav.client);
      setSearchedTracking(null);
      setViewLevel("year");
      toast.success(`Navigated to ${nav.client}`);
    } else if (result.type === "tracking") {
      // Navigate to parcels view with tracking highlighted
      if (nav.client) {
        // Store the searched tracking FIRST
        setSearchedTracking(result.tracking);
        
        // Set the client - this will trigger fetchClientDetails via useEffect
        setSelectedClient(nav.client);
        
        // Manually fetch client details and wait for it
        setLoading(true);
        try {
          let url = `${BACKEND_URL}/api/ledger-reconciliation/${nav.client}/details`;
          const params = new URLSearchParams();
          if (dateFrom) params.append("date_from", dateFrom);
          if (dateTo) params.append("date_to", dateTo);
          if (params.toString()) url += `?${params.toString()}`;

          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();
          
          if (response.ok) {
            setClientDetails(data);
            
            // Now set view level to parcels AFTER data is loaded
            setViewLevel("parcels");
            
            // Auto-expand the tracking row after data is loaded
            setTimeout(() => {
              toggleProductsExpand(result.tracking);
            }, 500);
            
            toast.success(`Found tracking ${result.tracking} for ${nav.client}`);
          } else {
            toast.error("Failed to load client data");
          }
        } catch (error) {
          console.error("Error fetching client details:", error);
          toast.error("Error loading client data");
        } finally {
          setLoading(false);
        }
      } else {
        toast.info(`Tracking found: ${result.tracking} - No client info available`);
      }
    }
  };

  // Handle Enter key on search input
  const handleSearchKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      
      // If results are already loaded, navigate to first result
      if (globalSearchResults && globalSearchResults.total_matches > 0) {
        if (globalSearchResults.tracking_matches?.length > 0) {
          navigateToSearchResult(globalSearchResults.tracking_matches[0]);
        } else if (globalSearchResults.client_matches?.length > 0) {
          navigateToSearchResult(globalSearchResults.client_matches[0]);
        }
      } else if (globalSearchQuery.length >= 2) {
        // If no results yet but query is valid, perform search immediately and then navigate
        setIsSearching(true);
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/ledger-reconciliation/search?q=${encodeURIComponent(globalSearchQuery)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.ok) {
            const data = await response.json();
            setGlobalSearchResults(data);
            
            // Navigate to first result
            if (data.total_matches > 0) {
              if (data.tracking_matches?.length > 0) {
                navigateToSearchResult(data.tracking_matches[0]);
              } else if (data.client_matches?.length > 0) {
                navigateToSearchResult(data.client_matches[0]);
              }
            } else {
              toast.info("No results found");
            }
          }
        } catch (error) {
          console.error("Search error:", error);
          toast.error("Search failed");
        } finally {
          setIsSearching(false);
        }
      } else {
        toast.info("Please enter at least 2 characters to search");
      }
    } else if (e.key === "Escape") {
      setShowSearchResults(false);
    }
  };

  // Clear search and reset
  const clearGlobalSearch = () => {
    setGlobalSearchQuery("");
    setGlobalSearchResults(null);
    setShowSearchResults(false);
    setSearchedTracking(null);
    if (searchNavigated) {
      setSearchNavigated(false);
      setSelectedClient(null);
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDay(null);
      setClientDetails(null);
      setViewLevel("summary");
      setExpandedRows(new Set());
    }
  };

  // Get current level data
  const getCurrentData = () => {
    if (viewLevel === "notifications") {
      // Return notifications list
      return filterByError(sortData(processedTrackingData.notifications || [], sortConfig.key, sortConfig.direction));
    }
    if (viewLevel === "year") {
      const yearsList = Object.values(processedTrackingData.years).sort((a, b) => String(a.year).localeCompare(String(b.year)));
      const hasKey = yearsList.length > 0 && yearsList[0][sortConfig.key] !== undefined;
      return filterByError(hasKey ? sortData(yearsList, sortConfig.key === "year" ? "year" : sortConfig.key, sortConfig.direction) : yearsList);
    }
    if (viewLevel === "month" && selectedYear) {
      const monthsList = Object.values(processedTrackingData.years[selectedYear]?.months || {}).sort((a, b) => (parseInt(a.month) || 0) - (parseInt(b.month) || 0));
      const hasKey = monthsList.length > 0 && monthsList[0][sortConfig.key] !== undefined;
      return filterByError(hasKey ? sortData(monthsList, sortConfig.key === "month" ? "month" : sortConfig.key, sortConfig.direction) : monthsList);
    }
    if (viewLevel === "day" && selectedYear && selectedMonth) {
      const daysList = Object.values(processedTrackingData.years[selectedYear]?.months[selectedMonth]?.days || {}).sort((a, b) => (parseInt(a.day) || 0) - (parseInt(b.day) || 0));
      const hasKey = daysList.length > 0 && daysList[0][sortConfig.key] !== undefined;
      return filterByError(hasKey ? sortData(daysList, sortConfig.key === "day" ? "day" : sortConfig.key, sortConfig.direction) : daysList);
    }
    if (viewLevel === "parcels") {
      // If we have a searched tracking, show all parcels that match from the entire client data
      if (searchedTracking && clientDetails?.tracking_verification) {
        const matchingParcels = clientDetails.tracking_verification.filter(p => 
          p.tracking && p.tracking.toUpperCase().includes(searchedTracking.toUpperCase())
        );
        return filterByError(sortData(matchingParcels, sortConfig.key, sortConfig.direction));
      }
      // Otherwise show parcels for the selected day
      if (selectedYear && selectedMonth && selectedDay) {
        const parcels = processedTrackingData.years[selectedYear]?.months[selectedMonth]?.days[selectedDay]?.parcels || [];
        return filterByError(sortData(parcels, sortConfig.key, sortConfig.direction));
      }
    }
    return [];
  };

  // Get all parcels for current client (for detailed print)
  const getAllParcelsForPrint = () => {
    if (!clientDetails?.tracking_verification) return [];
    return clientDetails.tracking_verification;
  };

  // Breadcrumb
  const getBreadcrumb = () => {
    const crumbs = [{ label: "Sales Team", level: "summary" }];
    if (selectedClient) crumbs.push({ label: selectedClient, level: "year" });
    if (selectedYear) crumbs.push({ label: selectedYear, level: "month" });
    if (selectedMonth) crumbs.push({ label: `Month ${selectedMonth}`, level: "day" });
    if (selectedDay) crumbs.push({ label: `Day ${selectedDay}`, level: "parcels" });
    return crumbs;
  };

  const getMonthName = (monthNum) => {
    const months = ["", "January", "February", "March", "April", "May", "June", 
                    "July", "August", "September", "October", "November", "December"];
    return months[parseInt(monthNum)] || `Month ${monthNum}`;
  };

  // Get print title
  const getPrintTitle = () => {
    if (viewLevel === "summary") return "Ledger Reconciliation - Sales Team";
    if (viewLevel === "notifications") return `Ledger Reconciliation - ${selectedClient} - Notifications`;
    if (viewLevel === "year") return `Ledger Reconciliation - ${selectedClient} - Yearly`;
    if (viewLevel === "month") return `Ledger Reconciliation - ${selectedClient} - ${selectedYear}`;
    if (viewLevel === "day") return `Ledger Reconciliation - ${selectedClient} - ${getMonthName(selectedMonth)} ${selectedYear}`;
    if (viewLevel === "parcels") return `Ledger Reconciliation - ${selectedClient} - ${selectedDay}/${selectedMonth}/${selectedYear}`;
    return "Ledger Reconciliation";
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    // Check if popup was blocked
    if (!printWindow) {
      alert('Please allow popups for this site to use the print feature.');
      return;
    }
    
    const currentData = viewLevel === "summary" ? filteredClients : getCurrentData();
    const summaryInfo = viewLevel === "summary" ? summaryData?.summary : clientDetails?.summary;
    const allParcels = printIncludeDetails && viewLevel !== "parcels" ? getAllParcelsForPrint() : [];
    
    let tableHeaders = '';
    let tableRows = '';
    
    if (viewLevel === "summary") {
      // Summary table
      const cols = columnOptions.summary.filter(c => printColumns[c.key]);
      tableHeaders = cols.map(c => `<th>${c.label}</th>`).join('');
      tableRows = currentData.map(client => {
        return `<tr>
          ${printColumns.client_code ? `<td class="left">${client.client_code}</td>` : ''}
          ${printColumns.orders ? `<td class="center">${client.order_count}</td>` : ''}
          ${printColumns.sales ? `<td class="center">${client.sales_count}</td>` : ''}
          ${printColumns.costing ? `<td class="right">₹${client.total_costing?.toLocaleString()}</td>` : ''}
          ${printColumns.gp_self ? `<td class="right">₹${client.total_gp_self?.toLocaleString()}</td>` : ''}
          ${printColumns.total_debit ? `<td class="right">₹${client.total_debit?.toLocaleString()}</td>` : ''}
          ${printColumns.total_credit ? `<td class="right">₹${client.total_credit?.toLocaleString()}</td>` : ''}
          ${printColumns.balance ? `<td class="right bold ${(client.sales_balance ?? client.balance) > 0 ? 'red' : 'green'}">₹${(client.sales_balance ?? client.balance)?.toLocaleString()}</td>` : ''}
        </tr>`;
      }).join('');
    } else if (viewLevel === "parcels") {
      // Parcels table
      const cols = columnOptions.parcels.filter(c => printColumns[c.key]);
      tableHeaders = cols.map(c => `<th>${c.label}</th>`).join('');
      tableRows = currentData.map(parcel => {
        return `<tr class="${parcel.status === 'error' ? 'error-row' : ''}">
          ${printColumns.tracking ? `<td class="left mono">${parcel.tracking}</td>` : ''}
          ${printColumns.date ? `<td class="center">${formatDMY(parcel.date)}</td>` : ''}
          ${printColumns.costing ? `<td class="right">₹${parcel.costing?.toLocaleString()}</td>` : ''}
          ${printColumns.sell_price ? `<td class="right">₹${parcel.sell_price?.toLocaleString()}</td>` : ''}
          ${printColumns.gp ? `<td class="right ${parcel.gp < 0 ? 'red' : 'green'}">₹${parcel.gp?.toLocaleString()}</td>` : ''}
          ${printColumns.gp_self ? `<td class="right">₹${parcel.gp_self?.toLocaleString()}</td>` : ''}
          ${printColumns.payment ? `<td class="right">₹${parcel.payment_received?.toLocaleString()}</td>` : ''}
          ${printColumns.status ? `<td class="center">${parcel.status}</td>` : ''}
        </tr>`;
      }).join('');
    } else {
      // Hierarchy table (year/month/day)
      const labelKey = viewLevel === "year" ? "Year" : viewLevel === "month" ? "Month" : "Day";
      const cols = [{ key: 'label', label: labelKey }, ...columnOptions.hierarchy.filter(c => printColumns[c.key])];
      tableHeaders = cols.map(c => `<th>${c.label}</th>`).join('');
      tableRows = currentData.map(item => {
        const label = viewLevel === "year" ? item.year : viewLevel === "month" ? getMonthName(item.month) : `Day ${item.day}`;
        return `<tr>
          <td class="left bold">${label}</td>
          ${printColumns.parcels ? `<td class="center">${item.count}</td>` : ''}
          ${printColumns.costing ? `<td class="right">₹${item.total_costing?.toLocaleString()}</td>` : ''}
          ${printColumns.sell_price ? `<td class="right">₹${item.total_sell?.toLocaleString()}</td>` : ''}
          ${printColumns.gp ? `<td class="right ${item.total_gp < 0 ? 'red' : 'green'}">₹${item.total_gp?.toLocaleString()}</td>` : ''}
          ${printColumns.gp_self ? `<td class="right">₹${item.total_gp_self?.toLocaleString()}</td>` : ''}
          ${printColumns.payment ? `<td class="right">₹${item.total_payment?.toLocaleString()}</td>` : ''}
          ${printColumns.errors ? `<td class="center">${item.error_count > 0 ? `⚠️ ${item.error_count}` : '✓'}</td>` : ''}
        </tr>`;
      }).join('');
    }
    
    // Detailed parcels section for partner print
    let detailedParcelsHtml = '';
    if (printIncludeDetails && viewLevel !== "parcels" && allParcels.length > 0) {
      const parcelCols = columnOptions.parcels.filter(c => printColumns[c.key]);
      detailedParcelsHtml = `
        <div class="section-break"></div>
        <h2>Detailed Parcel List (${allParcels.length} parcels)</h2>
        <table>
          <thead>
            <tr>${parcelCols.map(c => `<th>${c.label}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${allParcels.map(parcel => `
              <tr class="${parcel.status === 'error' ? 'error-row' : ''}">
                ${printColumns.tracking ? `<td class="left mono">${parcel.tracking}</td>` : ''}
                ${printColumns.date ? `<td class="center">${formatDMY(parcel.date)}</td>` : ''}
                ${printColumns.costing ? `<td class="right">₹${parcel.costing?.toLocaleString()}</td>` : ''}
                ${printColumns.sell_price ? `<td class="right">₹${parcel.sell_price?.toLocaleString()}</td>` : ''}
                ${printColumns.gp ? `<td class="right ${parcel.gp < 0 ? 'red' : 'green'}">₹${parcel.gp?.toLocaleString()}</td>` : ''}
                ${printColumns.gp_self ? `<td class="right">₹${parcel.gp_self?.toLocaleString()}</td>` : ''}
                ${printColumns.payment ? `<td class="right">₹${parcel.payment_received?.toLocaleString()}</td>` : ''}
                ${printColumns.status ? `<td class="center">${parcel.status}</td>` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }
    
    // Document styles
    const documentStyles = `
      body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
      h1 { text-align: center; color: #1e40af; margin-bottom: 5px; }
      h2 { color: #374151; margin-top: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
      .subtitle { text-align: center; color: #6b7280; margin-bottom: 20px; }
      .summary-cards { display: flex; justify-content: space-around; margin-bottom: 20px; flex-wrap: wrap; }
      .summary-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 20px; text-align: center; min-width: 120px; margin: 5px; }
      .summary-card.debit { background: #fef2f2; border-color: #fecaca; }
      .summary-card.credit { background: #f0fdf4; border-color: #bbf7d0; }
      .summary-card.balance { background: #fffbeb; border-color: #fde68a; }
      .summary-card .label { font-size: 10px; color: #6b7280; }
      .summary-card .value { font-size: 16px; font-weight: bold; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th { background: #f1f5f9; padding: 10px 8px; text-align: center; border: 1px solid #e2e8f0; font-weight: 600; }
      td { padding: 8px; border: 1px solid #e2e8f0; }
      .left { text-align: left; }
      .center { text-align: center; }
      .right { text-align: right; }
      .bold { font-weight: bold; }
      .red { color: #dc2626; }
      .green { color: #16a34a; }
      .mono { font-family: monospace; font-size: 11px; }
      .error-row { background: #fef2f2; }
      .section-break { margin-top: 40px; border-top: 2px solid #e5e7eb; }
      .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #9ca3af; }
    `;

    // Document content
    const documentContent = `
      <h1>${printSettings.companyName} - ${getPrintTitle()}</h1>
      <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>
      
      <div class="summary-cards">
        <div class="summary-card debit">
          <div class="label">Total Debit</div>
          <div class="value">₹${(summaryInfo?.total_debit || 0).toLocaleString()}</div>
        </div>
        <div class="summary-card credit">
          <div class="label">Total Credit</div>
          <div class="value">₹${(summaryInfo?.total_credit || 0).toLocaleString()}</div>
        </div>
        <div class="summary-card balance">
          <div class="label">Balance</div>
          <div class="value">₹${(summaryInfo?.total_sales_balance ?? summaryInfo?.total_balance ?? summaryInfo?.balance ?? 0).toLocaleString()}</div>
        </div>
      </div>
      
      <table>
        <thead>
          <tr>${tableHeaders}</tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      
      ${detailedParcelsHtml}
      
      <div class="footer">
        ${printSettings.companyName} Business Management System • Printed by Admin
      </div>
    `;

    // Print preview with toolbar (preview-first approach)
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Preview - ${getPrintTitle()}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f1f5f9; min-height: 100vh; }
          
          /* Preview Toolbar */
          .preview-toolbar {
            position: sticky;
            top: 0;
            z-index: 100;
            background: linear-gradient(to right, ${printSettings.headerColor}, ${printSettings.headerColor}99);
            padding: 12px 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .preview-toolbar h1 {
            color: white;
            font-size: 18px;
            font-weight: 600;
          }
          .preview-toolbar .subtitle {
            color: rgba(255,255,255,0.8);
            font-size: 12px;
            margin-top: 2px;
          }
          .toolbar-buttons {
            display: flex;
            gap: 10px;
          }
          .toolbar-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .btn-print {
            background: white;
            color: #1e40af;
          }
          .btn-print:hover {
            background: #f0f9ff;
            transform: translateY(-1px);
          }
          .btn-pdf {
            background: #059669;
            color: white;
          }
          .btn-pdf:hover {
            background: #047857;
            transform: translateY(-1px);
          }
          .btn-close {
            background: rgba(255,255,255,0.2);
            color: white;
          }
          .btn-close:hover {
            background: rgba(255,255,255,0.3);
          }
          
          /* Preview Container */
          .preview-container {
            padding: 24px;
            display: flex;
            justify-content: center;
          }
          .preview-page {
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            border-radius: 8px;
            max-width: 1100px;
            width: 100%;
            overflow: hidden;
          }
          .preview-content {
            padding: 40px;
          }
          
          /* Print tip */
          .print-tip {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            color: #92400e;
            padding: 10px 16px;
            margin: 16px 24px;
            border-radius: 8px;
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          /* Document Styles */
          .preview-content { ${documentStyles} }
          
          /* Hide toolbar when printing */
          @media print {
            .preview-toolbar, .print-tip { display: none !important; }
            .preview-container { padding: 0; }
            .preview-page { box-shadow: none; border-radius: 0; }
            body { background: white; margin: 10px; }
            .summary-cards { page-break-inside: avoid; }
          }
        </style>
        <script>
          // Print function that keeps window open after print/cancel
          function handlePrint() {
            window.print();
            // Window stays open - user must manually close it
          }
        </script>
      </head>
      <body>
        <div class="preview-toolbar">
          <div>
            <h1>🖨️ Print Preview</h1>
            <div class="subtitle">${getPrintTitle()}</div>
          </div>
          <div class="toolbar-buttons">
            <button class="toolbar-btn btn-close" onclick="window.close()">
              ✕ Close
            </button>
            <button class="toolbar-btn btn-pdf" onclick="handlePrint()">
              📄 Save as PDF
            </button>
            <button class="toolbar-btn btn-print" onclick="handlePrint()">
              🖨️ Print
            </button>
          </div>
        </div>
        
        <div class="print-tip">
          💡 <strong>Tip:</strong> Review the content below. Click "Print" to send to printer or "Save as PDF" to download. The preview will stay open after printing.
        </div>
        
        <div class="preview-container">
          <div class="preview-page">
            <div class="preview-content">
              ${documentContent}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setShowPrintDialog(false);
  };

  // Toggle all columns
  const toggleAllColumns = (checked) => {
    const newColumns = { ...printColumns };
    Object.keys(newColumns).forEach(key => {
      newColumns[key] = checked;
    });
    setPrintColumns(newColumns);
  };

  // Get date range based on selection
  const getDateRangeForExport = () => {
    const today = new Date();
    let fromDate = null;
    let toDate = new Date().toISOString().split('T')[0];
    
    switch (exportDateRange) {
      case "today":
        fromDate = toDate;
        break;
      case "week":
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        fromDate = weekAgo.toISOString().split('T')[0];
        break;
      case "month":
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        fromDate = monthAgo.toISOString().split('T')[0];
        break;
      case "year":
        const yearAgo = new Date(today);
        yearAgo.setFullYear(today.getFullYear() - 1);
        fromDate = yearAgo.toISOString().split('T')[0];
        break;
      case "custom":
        fromDate = exportDateFrom || null;
        toDate = exportDateTo || toDate;
        break;
      default:
        fromDate = null;
        toDate = null;
    }
    return { fromDate, toDate };
  };

  // Export to Excel
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const workbook = XLSX.utils.book_new();
      const { fromDate, toDate } = getDateRangeForExport();
      const dateRangeText = fromDate && toDate ? `${fromDate} to ${toDate}` : "All Time";
      
      if (exportScope === "current") {
        // Export current view
        const currentData = viewLevel === "summary" ? filteredClients : getCurrentData();
        const sheetData = formatDataForExcel(currentData, viewLevel);
        const ws = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, ws, getPrintTitle().substring(0, 31));
      } else if (exportScope === "all_clients") {
        // Export all clients summary
        const clientsData = filteredClients.map(client => ({
          "Client Code": client.client_code,
          "Orders": client.order_count,
          "Sales": client.sales_count,
          "Costing (₹)": client.total_costing,
          "GP Self (₹)": client.total_gp_self,
          "Total Debit (₹)": client.total_debit,
          "Total Credit (₹)": client.total_credit,
          "Balance (₹)": client.sales_balance ?? client.balance
        }));
        const ws = XLSX.utils.json_to_sheet(clientsData);
        XLSX.utils.book_append_sheet(workbook, ws, "Sales Team Summary");
        
        // Add summary totals row
        const summaryRow = [{
          "Client Code": "TOTAL",
          "Orders": filteredClients.reduce((sum, c) => sum + (c.order_count || 0), 0),
          "Sales": filteredClients.reduce((sum, c) => sum + (c.sales_count || 0), 0),
          "Costing (₹)": filteredClients.reduce((sum, c) => sum + (c.total_costing || 0), 0),
          "GP Self (₹)": filteredClients.reduce((sum, c) => sum + (c.total_gp_self || 0), 0),
          "Total Debit (₹)": summaryData?.summary?.total_debit || 0,
          "Total Credit (₹)": summaryData?.summary?.total_credit || 0,
          "Balance (₹)": summaryData?.summary?.total_sales_balance ?? summaryData?.summary?.total_balance ?? 0
        }];
        XLSX.utils.sheet_add_json(ws, summaryRow, { origin: -1 });
      } else if (exportScope === "selected_client" && selectedClient) {
        // Export selected client with all hierarchy levels
        
        // Sheet 1: Yearly summary
        const yearlyData = Object.values(processedTrackingData.years).map(y => ({
          "Year": y.year,
          "Parcels": y.count,
          "Costing (₹)": y.total_costing,
          "Sell Price (₹)": y.total_sell,
          "GP (₹)": y.total_gp,
          "GP Self (₹)": y.total_gp_self,
          "Payment (₹)": y.total_payment,
          "Errors": y.error_count
        }));
        if (yearlyData.length > 0) {
          const wsYearly = XLSX.utils.json_to_sheet(yearlyData);
          XLSX.utils.book_append_sheet(workbook, wsYearly, `${selectedClient} - Yearly`);
        }
        
        // Sheet 2: Monthly breakdown
        const monthlyData = [];
        Object.values(processedTrackingData.years).forEach(year => {
          Object.values(year.months).forEach(month => {
            monthlyData.push({
              "Year": year.year,
              "Month": getMonthName(month.month),
              "Parcels": month.count,
              "Costing (₹)": month.total_costing,
              "Sell Price (₹)": month.total_sell,
              "GP (₹)": month.total_gp,
              "GP Self (₹)": month.total_gp_self,
              "Payment (₹)": month.total_payment,
              "Errors": month.error_count
            });
          });
        });
        if (monthlyData.length > 0) {
          const wsMonthly = XLSX.utils.json_to_sheet(monthlyData);
          XLSX.utils.book_append_sheet(workbook, wsMonthly, `${selectedClient} - Monthly`);
        }
        
        // Sheet 3: Daily breakdown
        const dailyData = [];
        Object.values(processedTrackingData.years).forEach(year => {
          Object.values(year.months).forEach(month => {
            Object.values(month.days).forEach(day => {
              dailyData.push({
                "Date": `${day.day}/${month.month}/${year.year}`,
                "Parcels": day.count,
                "Costing (₹)": day.total_costing,
                "Sell Price (₹)": day.total_sell,
                "GP (₹)": day.total_gp,
                "GP Self (₹)": day.total_gp_self,
                "Payment (₹)": day.total_payment,
                "Errors": day.error_count
              });
            });
          });
        });
        if (dailyData.length > 0) {
          const wsDaily = XLSX.utils.json_to_sheet(dailyData);
          XLSX.utils.book_append_sheet(workbook, wsDaily, `${selectedClient} - Daily`);
        }
        
        // Sheet 4: All parcels detail
        if (exportIncludeDetails && clientDetails?.tracking_verification) {
          const parcelsData = clientDetails.tracking_verification.map(p => ({
            "Tracking": p.tracking,
            "Date": p.date || "-",
            "Costing (₹)": p.costing,
            "Sell Price (₹)": p.sell_price,
            "GP (₹)": p.gp,
            "GP Self (₹)": p.gp_self,
            "Payment (₹)": p.payment_received,
            "Status": p.status
          }));
          const wsParcels = XLSX.utils.json_to_sheet(parcelsData);
          XLSX.utils.book_append_sheet(workbook, wsParcels, `${selectedClient} - Parcels`);
        }
        
        // Add client summary sheet
        const clientSummary = [{
          "Client Code": selectedClient,
          "Total Debit (₹)": clientDetails?.summary?.total_debit || 0,
          "Total Credit (₹)": clientDetails?.summary?.total_credit || 0,
          "Balance (₹)": clientDetails?.account_view_totals?.sales_balance ?? clientDetails?.summary?.balance ?? 0,
          "Total Costing (₹)": clientDetails?.summary?.total_costing || 0,
          "Total GP Self (₹)": clientDetails?.summary?.total_gp_self || 0,
          "Total Parcels": clientDetails?.tracking_verification?.length || 0,
          "Report Date": formatDMY(new Date())
        }];
        const wsSummary = XLSX.utils.json_to_sheet(clientSummary);
        XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");
      }
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      let filename = `LedgerReconciliation_${timestamp}`;
      if (exportScope === "selected_client" && selectedClient) {
        filename = `Ledger_${selectedClient}_${timestamp}`;
      } else if (exportScope === "all_clients") {
        filename = `Ledger_AllClients_${timestamp}`;
      }
      
      // Save file
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `${filename}.xlsx`);
      
      toast.success("Excel file downloaded successfully!");
      setShowExportDialog(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setExporting(false);
    }
  };

  // Format data for Excel based on view level
  const formatDataForExcel = (data, level) => {
    if (level === "summary") {
      return data.map(client => ({
        "Client Code": client.client_code,
        "Orders": client.order_count,
        "Sales": client.sales_count,
        "Costing (₹)": client.total_costing,
        "GP Self (₹)": client.total_gp_self,
        "Total Debit (₹)": client.total_debit,
        "Total Credit (₹)": client.total_credit,
        "Balance (₹)": client.sales_balance ?? client.balance
      }));
    } else if (level === "parcels") {
      return data.map(p => ({
        "Tracking": p.tracking,
        "Date": p.date || "-",
        "Costing (₹)": p.costing,
        "Sell Price (₹)": p.sell_price,
        "GP (₹)": p.gp,
        "GP Self (₹)": p.gp_self,
        "Payment (₹)": p.payment_received,
        "Status": p.status
      }));
    } else {
      // Year/Month/Day view
      const labelKey = level === "year" ? "Year" : level === "month" ? "Month" : "Day";
      return data.map(item => ({
        [labelKey]: level === "year" ? item.year : level === "month" ? getMonthName(item.month) : `Day ${item.day}`,
        "Parcels": item.count,
        "Costing (₹)": item.total_costing,
        "Sell Price (₹)": item.total_sell,
        "GP (₹)": item.total_gp,
        "GP Self (₹)": item.total_gp_self,
        "Payment (₹)": item.total_payment,
        "Errors": item.error_count
      }));
    }
  };

  // Export to PDF
  // Professional PDF Export with Screen Preview Modal
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const currentData = viewLevel === "summary" ? filteredClients : getCurrentData();
      const summaryInfo = viewLevel === "summary" ? summaryData?.summary : clientDetails?.summary;
      
      // Store data for preview modal
      setPdfPreviewData({ data: currentData, summaryInfo });
      setShowPdfPreview(true);
      setShowExportDialog(false);
      
      toast.success("PDF preview ready - review and download");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF preview: " + error.message);
    } finally {
      setExporting(false);
    }
  };
  
  // Download PDF directly
  const handleDownloadPDF = () => {
    if (!pdfPreviewData) return;
    
    try {
      generateDirectPDF(pdfPreviewData.data, pdfPreviewData.summaryInfo);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to download PDF: " + error.message);
    }
  };
  
  // Print from preview
  const handlePrintFromPreview = () => {
    if (!pdfPreviewData) return;
    
    const htmlContent = generateProfessionalPDFHTML(pdfPreviewData.data, pdfPreviewData.summaryInfo);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups for printing");
      return;
    }
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 300);
    };
  };

  // Generate Professional HTML for PDF
  const generateProfessionalPDFHTML = (data, summaryInfo) => {
    const companyName = printSettings?.companyName || "EAC";
    const headerColor = printSettings?.headerColor || "#1e40af";
    const timestamp = new Date().toLocaleString('en-IN', { 
      dateStyle: 'full', 
      timeStyle: 'short' 
    });
    
    // Calculate totals for summary table
    const totals = data.reduce((acc, item) => ({
      order_count: (acc.order_count || 0) + (item.order_count || 0),
      sales_count: (acc.sales_count || 0) + (item.sales_count || 0),
      total_costing: (acc.total_costing || 0) + (item.total_costing || item.costing || 0),
      total_gp_self: (acc.total_gp_self || 0) + (item.total_gp_self || item.gp_self || 0),
      total_debit: (acc.total_debit || 0) + (item.total_debit || 0),
      total_credit: (acc.total_credit || 0) + (item.total_credit || 0),
      balance: (acc.balance || 0) + (item.sales_balance ?? item.balance ?? 0),
    }), {});

    // Determine table columns based on view level
    let tableHeaders = '';
    let tableRows = '';
    
    if (viewLevel === "summary") {
      tableHeaders = `
        <th>Client Code</th>
        <th class="center">Orders</th>
        <th class="center">Sales</th>
        <th class="right">Costing</th>
        <th class="right">GP Self</th>
        <th class="right debit">Debit</th>
        <th class="right credit">Credit</th>
        <th class="right balance">Balance</th>
      `;
      tableRows = data.map((client, idx) => `
        <tr class="${idx % 2 === 0 ? 'even' : 'odd'}">
          <td class="client-code">${client.client_code || '-'}</td>
          <td class="center">${client.order_count || 0}</td>
          <td class="center">${client.sales_count || 0}</td>
          <td class="right">₹${(client.total_costing || 0).toLocaleString('en-IN')}</td>
          <td class="right gp-self">₹${(client.total_gp_self || 0).toLocaleString('en-IN')}</td>
          <td class="right debit">₹${(client.total_debit || 0).toLocaleString('en-IN')}</td>
          <td class="right credit">₹${(client.total_credit || 0).toLocaleString('en-IN')}</td>
          <td class="right balance ${(client.sales_balance ?? client.balance) > 0 ? 'pending' : (client.sales_balance ?? client.balance) < 0 ? 'overpaid' : 'settled'}">
            ₹${(client.sales_balance ?? client.balance ?? 0).toLocaleString('en-IN')}
          </td>
        </tr>
      `).join('');
    } else if (viewLevel === "parcels") {
      tableHeaders = `
        <th>Tracking Number</th>
        <th class="center">Date</th>
        <th class="right">Costing</th>
        <th class="right">Sell Price</th>
        <th class="right">GP</th>
        <th class="right">GP Self</th>
        <th class="right">Payment</th>
        <th class="center">Status</th>
      `;
      tableRows = data.map((parcel, idx) => `
        <tr class="${idx % 2 === 0 ? 'even' : 'odd'}">
          <td class="tracking">${parcel.tracking || '-'}</td>
          <td class="center">${formatDMY(parcel.date)}</td>
          <td class="right">₹${(parcel.costing || 0).toLocaleString('en-IN')}</td>
          <td class="right">₹${(parcel.sell_price || 0).toLocaleString('en-IN')}</td>
          <td class="right ${parcel.gp < 0 ? 'negative' : 'positive'}">₹${(parcel.gp || 0).toLocaleString('en-IN')}</td>
          <td class="right gp-self">₹${(parcel.gp_self || 0).toLocaleString('en-IN')}</td>
          <td class="right credit">₹${(parcel.payment_received || 0).toLocaleString('en-IN')}</td>
          <td class="center status-${parcel.status || 'pending'}">${parcel.status || 'Pending'}</td>
        </tr>
      `).join('');
    } else {
      // Year/Month/Day view
      const labelText = viewLevel === "year" ? "Year" : viewLevel === "month" ? "Month" : "Day";
      tableHeaders = `
        <th>${labelText}</th>
        <th class="center">Parcels</th>
        <th class="right">Costing</th>
        <th class="right">Sell Price</th>
        <th class="right">GP</th>
        <th class="right">GP Self</th>
        <th class="right">Payment</th>
        <th class="center">Errors</th>
      `;
      tableRows = data.map((item, idx) => {
        const label = viewLevel === "year" ? item.year : viewLevel === "month" ? getMonthName(item.month) : `Day ${item.day}`;
        return `
          <tr class="${idx % 2 === 0 ? 'even' : 'odd'}">
            <td class="label">${label}</td>
            <td class="center">${item.count || 0}</td>
            <td class="right">₹${(item.total_costing || 0).toLocaleString('en-IN')}</td>
            <td class="right">₹${(item.total_sell || 0).toLocaleString('en-IN')}</td>
            <td class="right ${item.total_gp < 0 ? 'negative' : 'positive'}">₹${(item.total_gp || 0).toLocaleString('en-IN')}</td>
            <td class="right gp-self">₹${(item.total_gp_self || 0).toLocaleString('en-IN')}</td>
            <td class="right credit">₹${(item.total_payment || 0).toLocaleString('en-IN')}</td>
            <td class="center ${item.error_count > 0 ? 'error' : 'success'}">${item.error_count || '✓'}</td>
          </tr>
        `;
      }).join('');
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${companyName} - Ledger Report</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 15mm;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #1f2937;
            background: #fff;
            padding: 20px;
          }
          
          .print-toolbar {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, ${headerColor}, ${headerColor}dd);
            color: white;
            padding: 12px 24px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          
          .print-toolbar h3 {
            font-size: 16px;
            font-weight: 600;
          }
          
          .print-toolbar button {
            padding: 8px 20px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 13px;
            margin-left: 10px;
            transition: all 0.2s;
          }
          
          .btn-print {
            background: white;
            color: ${headerColor};
          }
          
          .btn-print:hover {
            background: #f0f0f0;
            transform: translateY(-1px);
          }
          
          .btn-close {
            background: rgba(255,255,255,0.2);
            color: white;
            border: 1px solid rgba(255,255,255,0.3) !important;
          }
          
          .btn-close:hover {
            background: rgba(255,255,255,0.3);
          }
          
          .content {
            margin-top: 70px;
          }
          
          /* Header Section */
          .report-header {
            text-align: center;
            margin-bottom: 25px;
            padding-bottom: 20px;
            border-bottom: 3px solid ${headerColor};
          }
          
          .company-name {
            font-size: 28px;
            font-weight: 700;
            color: ${headerColor};
            margin-bottom: 5px;
            letter-spacing: 1px;
          }
          
          .report-title {
            font-size: 18px;
            color: #4b5563;
            font-weight: 500;
            margin-bottom: 8px;
          }
          
          .report-subtitle {
            font-size: 12px;
            color: #6b7280;
          }
          
          /* Summary Cards */
          .summary-section {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-bottom: 25px;
            flex-wrap: wrap;
          }
          
          .summary-card {
            background: linear-gradient(135deg, #f8fafc, #f1f5f9);
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 15px 25px;
            text-align: center;
            min-width: 140px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          }
          
          .summary-card.debit {
            background: linear-gradient(135deg, #fef2f2, #fee2e2);
            border-color: #fecaca;
          }
          
          .summary-card.credit {
            background: linear-gradient(135deg, #f0fdf4, #dcfce7);
            border-color: #bbf7d0;
          }
          
          .summary-card.balance {
            background: linear-gradient(135deg, #fffbeb, #fef3c7);
            border-color: #fde68a;
          }
          
          .summary-card .label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #64748b;
            margin-bottom: 5px;
            font-weight: 600;
          }
          
          .summary-card .value {
            font-size: 18px;
            font-weight: 700;
            color: #1e293b;
          }
          
          .summary-card.debit .value { color: #dc2626; }
          .summary-card.credit .value { color: #16a34a; }
          .summary-card.balance .value { color: #d97706; }
          
          /* Table */
          .table-container {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.08);
            border: 1px solid #e5e7eb;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
          }
          
          thead {
            background: linear-gradient(135deg, ${headerColor}, ${headerColor}dd);
          }
          
          th {
            padding: 14px 12px;
            text-align: left;
            font-weight: 600;
            color: white;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 2px solid ${headerColor}88;
          }
          
          th.center { text-align: center; }
          th.right { text-align: right; }
          
          td {
            padding: 12px;
            border-bottom: 1px solid #f1f5f9;
            font-size: 11px;
          }
          
          tr.even { background: #fafbfc; }
          tr.odd { background: white; }
          tr:hover { background: #f0f9ff; }
          
          td.center { text-align: center; }
          td.right { text-align: right; font-family: 'Courier New', monospace; }
          
          td.client-code, td.tracking, td.label {
            font-weight: 600;
            color: ${headerColor};
          }
          
          td.debit { color: #dc2626; font-weight: 600; }
          td.credit { color: #16a34a; font-weight: 600; }
          td.gp-self { color: #7c3aed; font-weight: 600; }
          
          td.balance.pending { color: #d97706; font-weight: 700; }
          td.balance.overpaid { color: #16a34a; font-weight: 700; }
          td.balance.settled { color: #64748b; }
          
          td.positive { color: #16a34a; }
          td.negative { color: #dc2626; }
          td.error { color: #dc2626; font-weight: 600; }
          td.success { color: #16a34a; }
          
          td.status-error { background: #fef2f2; color: #dc2626; font-weight: 600; border-radius: 4px; }
          td.status-settled { background: #f0fdf4; color: #16a34a; font-weight: 600; border-radius: 4px; }
          td.status-pending { background: #fffbeb; color: #d97706; font-weight: 600; border-radius: 4px; }
          
          /* Totals Row */
          tfoot {
            background: linear-gradient(135deg, #1e293b, #334155);
          }
          
          tfoot td {
            padding: 14px 12px;
            font-weight: 700;
            color: white;
            border: none;
            font-size: 12px;
          }
          
          tfoot td.debit { color: #fca5a5; }
          tfoot td.credit { color: #86efac; }
          tfoot td.balance { color: #fcd34d; }
          
          /* Footer */
          .report-footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #9ca3af;
          }
          
          .page-info {
            text-align: center;
            flex: 1;
          }
          
          @media print {
            .print-toolbar { display: none !important; }
            .content { margin-top: 0; }
            body { padding: 0; }
            .table-container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="print-toolbar">
          <h3>📄 PDF Preview - ${companyName}</h3>
          <div>
            <button class="btn-print" onclick="window.print()">🖨️ Print / Save PDF</button>
            <button class="btn-close" onclick="window.close()">✕ Close</button>
          </div>
        </div>
        
        <div class="content">
          <div class="report-header">
            <div class="company-name">${companyName}</div>
            <div class="report-title">Ledger Reconciliation Report</div>
            <div class="report-subtitle">
              ${viewLevel === "summary" ? "Sales Team Summary" : selectedClient ? `Account: ${selectedClient}` : ""}
              ${selectedYear ? ` | Year: ${selectedYear}` : ""}
              ${selectedMonth ? ` | Month: ${getMonthName(selectedMonth)}` : ""}
              | Generated: ${timestamp}
            </div>
          </div>
          
          <div class="summary-section">
            <div class="summary-card">
              <div class="label">Total Records</div>
              <div class="value">${data.length}</div>
            </div>
            <div class="summary-card debit">
              <div class="label">Total Debit</div>
              <div class="value">₹${(summaryInfo?.total_debit || totals.total_debit || 0).toLocaleString('en-IN')}</div>
            </div>
            <div class="summary-card credit">
              <div class="label">Total Credit</div>
              <div class="value">₹${(summaryInfo?.total_credit || totals.total_credit || 0).toLocaleString('en-IN')}</div>
            </div>
            <div class="summary-card balance">
              <div class="label">Balance</div>
              <div class="value">₹${(summaryInfo?.total_balance || summaryInfo?.balance || totals.balance || 0).toLocaleString('en-IN')}</div>
            </div>
          </div>
          
          <div class="table-container">
            <table>
              <thead>
                <tr>${tableHeaders}</tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL (${data.length} records)</strong></td>
                  ${viewLevel === "summary" ? `
                    <td class="center">${totals.order_count?.toLocaleString('en-IN') || '-'}</td>
                    <td class="center">${totals.sales_count?.toLocaleString('en-IN') || '-'}</td>
                    <td class="right">₹${(totals.total_costing || 0).toLocaleString('en-IN')}</td>
                    <td class="right">₹${(totals.total_gp_self || 0).toLocaleString('en-IN')}</td>
                    <td class="right debit">₹${(totals.total_debit || 0).toLocaleString('en-IN')}</td>
                    <td class="right credit">₹${(totals.total_credit || 0).toLocaleString('en-IN')}</td>
                    <td class="right balance">₹${(totals.balance || 0).toLocaleString('en-IN')}</td>
                  ` : `
                    <td colspan="7" class="right">See Summary Above</td>
                  `}
                </tr>
              </tfoot>
            </table>
          </div>
          
          <div class="report-footer">
            <div>Confidential - For Internal Use Only</div>
            <div class="page-info">${companyName} Business Management System</div>
            <div>Page 1 of 1</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Direct PDF Download using jsPDF (for "Download PDF" button in preview)
  const generateDirectPDF = (data, summaryInfo) => {
    const companyName = printSettings?.companyName || "EAC";
    const headerColor = printSettings?.headerColor || "#1e40af";
    
    // Convert hex to RGB for jsPDF
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ] : [30, 64, 175];
    };
    const primaryRgb = hexToRgb(headerColor);
    
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Header with company branding
    doc.setFillColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    doc.rect(0, 0, pageWidth, 28, 'F');
    
    // Company name
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, 14, 14);
    
    // Report title
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text('Ledger Reconciliation Report', 14, 22);
    
    // Date on right side
    const timestamp = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    doc.setFontSize(10);
    doc.text(`Generated: ${timestamp}`, pageWidth - 14, 14, { align: 'right' });
    
    // Context info
    let contextText = viewLevel === "summary" ? "Sales Team Summary" : "";
    if (selectedClient) contextText = `Client: ${selectedClient}`;
    if (selectedYear) contextText += ` | Year: ${selectedYear}`;
    if (selectedMonth) contextText += ` | Month: ${getMonthName(selectedMonth)}`;
    doc.text(contextText, pageWidth - 14, 22, { align: 'right' });
    
    // Summary cards section
    let yPos = 35;
    
    // Calculate totals
    const totals = data.reduce((acc, item) => ({
      order_count: (acc.order_count || 0) + (item.order_count || 0),
      sales_count: (acc.sales_count || 0) + (item.sales_count || 0),
      total_costing: (acc.total_costing || 0) + (item.total_costing || item.costing || 0),
      total_debit: (acc.total_debit || 0) + (item.total_debit || 0),
      total_credit: (acc.total_credit || 0) + (item.total_credit || 0),
      balance: (acc.balance || 0) + (item.sales_balance ?? item.balance ?? 0),
    }), {});
    
    // Draw summary cards
    const cardWidth = 55;
    const cardHeight = 18;
    const cards = [
      { label: 'Total Records', value: data.length.toString(), color: [241, 245, 249] },
      { label: 'Total Debit', value: `₹${(summaryInfo?.total_debit || totals.total_debit || 0).toLocaleString('en-IN')}`, color: [254, 242, 242] },
      { label: 'Total Credit', value: `₹${(summaryInfo?.total_credit || totals.total_credit || 0).toLocaleString('en-IN')}`, color: [240, 253, 244] },
      { label: 'Balance', value: `₹${(summaryInfo?.total_balance || summaryInfo?.balance || totals.balance || 0).toLocaleString('en-IN')}`, color: [255, 251, 235] },
    ];
    
    let cardX = (pageWidth - (cards.length * cardWidth + (cards.length - 1) * 8)) / 2;
    cards.forEach((card, idx) => {
      doc.setFillColor(card.color[0], card.color[1], card.color[2]);
      doc.roundedRect(cardX, yPos, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(cardX, yPos, cardWidth, cardHeight, 3, 3, 'S');
      
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(card.label.toUpperCase(), cardX + cardWidth/2, yPos + 6, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.setFont(undefined, 'bold');
      doc.text(card.value, cardX + cardWidth/2, yPos + 14, { align: 'center' });
      doc.setFont(undefined, 'normal');
      
      cardX += cardWidth + 8;
    });
    
    yPos += cardHeight + 10;
    
    // Generate table data based on view level
    const tableData = formatDataForPDF(data, viewLevel);
    
    // Draw the main table with professional styling
    autoTable(doc, {
      head: [tableData.headers],
      body: tableData.rows,
      startY: yPos,
      styles: { 
        fontSize: 9, 
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: { 
        fillColor: primaryRgb, 
        textColor: [255, 255, 255], 
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 5,
      },
      alternateRowStyles: { 
        fillColor: [248, 250, 252] 
      },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: primaryRgb },
      },
      footStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      foot: [[
        `TOTAL (${data.length})`,
        ...tableData.rows.reduce((acc, row) => {
          return row.slice(1).map((val, i) => {
            if (typeof val === 'string' && val.startsWith('₹')) {
              const num = parseFloat(val.replace(/[₹,]/g, '')) || 0;
              const accNum = parseFloat((acc[i] || '₹0').replace(/[₹,]/g, '')) || 0;
              return `₹${(num + accNum).toLocaleString('en-IN')}`;
            }
            const numVal = parseInt(val) || 0;
            const accVal = parseInt(acc[i]) || 0;
            return (numVal + accVal).toString();
          });
        }, tableData.headers.slice(1).map(() => '0'))
      ]],
      didDrawPage: (data) => {
        // Footer on each page
        const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175);
        doc.text(
          `${companyName} Business Management System  |  Page ${pageNum}`,
          pageWidth / 2, 
          pageHeight - 8, 
          { align: 'center' }
        );
        doc.text('Confidential - For Internal Use Only', 14, pageHeight - 8);
      }
    });
    
    // Generate filename
    const dateStr = new Date().toISOString().split('T')[0];
    let filename = `LedgerReconciliation_${dateStr}`;
    if (selectedClient) filename = `Ledger_${selectedClient}_${dateStr}`;
    
    doc.save(`${filename}.pdf`);
    return true;
  };

  // Format data for PDF table
  const formatDataForPDF = (data, level) => {
    if (level === "summary") {
      return {
        headers: ["Account Code", "Orders", "Sales", "Costing", "GP Self", "Total Debit", "Total Credit", "Balance"],
        rows: data.map(client => [
          client.client_code,
          client.order_count,
          client.sales_count,
          `₹${client.total_costing?.toLocaleString()}`,
          `₹${client.total_gp_self?.toLocaleString()}`,
          `₹${client.total_debit?.toLocaleString()}`,
          `₹${client.total_credit?.toLocaleString()}`,
          `₹${(client.sales_balance ?? client.balance)?.toLocaleString()}`
        ])
      };
    } else if (level === "parcels") {
      return {
        headers: ["Tracking", "Date", "Costing", "Sell Price", "GP", "GP Self", "Payment", "Status"],
        rows: data.map(p => [
          p.tracking,
          p.date || "-",
          `₹${p.costing?.toLocaleString()}`,
          `₹${p.sell_price?.toLocaleString()}`,
          `₹${p.gp?.toLocaleString()}`,
          `₹${p.gp_self?.toLocaleString()}`,
          `₹${p.payment_received?.toLocaleString()}`,
          p.status
        ])
      };
    } else {
      const labelKey = level === "year" ? "Year" : level === "month" ? "Month" : "Day";
      return {
        headers: [labelKey, "Parcels", "Costing", "Sell Price", "GP", "GP Self", "Payment", "Errors"],
        rows: data.map(item => [
          level === "year" ? item.year : level === "month" ? getMonthName(item.month) : `Day ${item.day}`,
          item.count,
          `₹${item.total_costing?.toLocaleString()}`,
          `₹${item.total_sell?.toLocaleString()}`,
          `₹${item.total_gp?.toLocaleString()}`,
          `₹${item.total_gp_self?.toLocaleString()}`,
          `₹${item.total_payment?.toLocaleString()}`,
          item.error_count
        ])
      };
    }
  };

  // Handle export based on format
  const handleExport = () => {
    if (exportFormat === "excel") {
      handleExportExcel();
    } else {
      handleExportPDF();
    }
  };

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = "" }) => (
    <th
      className={`px-3 py-2.5 font-semibold cursor-pointer hover:bg-slate-200 select-none text-center text-sm ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center justify-center gap-1.5">
        {label}
        {getSortIcon(sortKey)}
      </div>
    </th>
  );

  // Summary Cards Component - Compact Horizontal Bar with readable fonts
  const SummaryCards = ({ data }) => (
    <div className="flex flex-wrap items-center gap-3 py-2.5 px-4 bg-slate-50 rounded-lg border border-slate-200 mb-3" data-testid="ledger-summary-bar">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-50 border border-red-200">
        <span className="text-sm text-red-600">Debit:</span>
        <span className="text-base font-bold text-red-700">₹{(data?.total_debit || 0).toLocaleString('en-IN')}</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-green-50 border border-green-200">
        <span className="text-sm text-green-600">Credit:</span>
        <span className="text-base font-bold text-green-700">₹{(data?.total_credit || 0).toLocaleString('en-IN')}</span>
      </div>
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${(data?.total_sales_balance ?? data?.total_balance ?? data?.balance ?? 0) > 0 ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <span className={`text-sm ${(data?.total_sales_balance ?? data?.total_balance ?? data?.balance ?? 0) > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>Balance:</span>
        <span className={`text-base font-bold ${(data?.total_sales_balance ?? data?.total_balance ?? data?.balance ?? 0) > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
          ₹{(data?.total_sales_balance ?? data?.total_balance ?? data?.balance ?? 0).toLocaleString('en-IN')}
        </span>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200">
        <span className="text-sm text-blue-600">Costing:</span>
        <span className="text-base font-bold text-blue-700">₹{(data?.total_costing || 0).toLocaleString('en-IN')}</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-purple-50 border border-purple-200">
        <span className="text-sm text-purple-600">GP Self:</span>
        <span className="text-base font-bold text-purple-700">₹{(data?.total_gp_self || 0).toLocaleString('en-IN')}</span>
      </div>
    </div>
  );

  // Render filter panel - Compact single row
  const renderFilterPanel = () => (
    <div className={`bg-slate-50 border rounded-lg p-3 mb-3 ${showFilters ? '' : 'hidden'}`} data-testid="ledger-filter-panel">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search client..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-8 w-40 h-9" 
          />
        </div>
        
        {/* Date Range */}
        <div className="flex items-center gap-2">
          <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 h-9" placeholder="From" />
          <span className="text-slate-400">-</span>
          <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 h-9" placeholder="To" />
        </div>
        
        {/* Balance Status */}
        <Select value={balanceFilter} onValueChange={setBalanceFilter}>
          <SelectTrigger className="w-28 h-9"><SelectValue placeholder="Balance" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="settled">Settled</SelectItem>
            <SelectItem value="overpaid">Overpaid</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Error Status */}
        <Select value={errorFilter} onValueChange={setErrorFilter}>
          <SelectTrigger className="w-28 h-9"><SelectValue placeholder="Errors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="errors">With Errors</SelectItem>
            <SelectItem value="no-errors">No Errors</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Clear Button */}
        <Button variant="ghost" size="sm" onClick={() => {
          setSearchTerm("");
          setDateFrom("");
          setDateTo("");
          setBalanceFilter("all");
          setErrorFilter("all");
          setAccountTypeFilter("all");
          setUnderFilter("all");
          setMinBalance("");
          setMaxBalance("");
        }} className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50">
          <X className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  // Render breadcrumb
  const renderBreadcrumb = () => (
    <div className="flex items-center gap-2 mb-4">
      {getBreadcrumb().map((crumb, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {idx > 0 && <ChevronRight className="w-4 h-4 text-slate-400" />}
          <button
            onClick={() => {
              if (crumb.level === "summary") {
                setSelectedClient(null);
                setSelectedYear(null);
                setSelectedMonth(null);
                setSelectedDay(null);
                setViewLevel("summary");
              } else if (crumb.level === "year") {
                setSelectedYear(null);
                setSelectedMonth(null);
                setSelectedDay(null);
                setViewLevel("year");
              } else if (crumb.level === "month") {
                setSelectedMonth(null);
                setSelectedDay(null);
                setViewLevel("month");
              } else if (crumb.level === "day") {
                setSelectedDay(null);
                setViewLevel("day");
              }
            }}
            className={`text-sm ${idx === getBreadcrumb().length - 1 ? 'font-semibold text-blue-600' : 'text-slate-500 hover:text-blue-600'}`}
          >
            {crumb.label}
          </button>
        </div>
      ))}
    </div>
  );

  // Render action buttons
  const renderActionButtons = () => (
    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
      {/* Sync Button */}
      <Button 
        variant="outline" 
        size="sm"
        onClick={async () => {
          try {
            toast.info("Syncing costs from shipment orders...");
            const response = await fetch(`${BACKEND_URL}/api/sync/bulk`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({ 
                sync_type: "all",
                client_code: selectedClient || undefined
              })
            });
            const data = await response.json();
            if (response.ok) {
              toast.success(`Synced: ${data.sales_updated} sales, ${data.ledger_updated} ledger`);
              try {
                const ledgerSyncRes = await fetch(`${BACKEND_URL}/api/ledgers/sync-from-orders`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` }
                });
                const ledgerSyncData = await ledgerSyncRes.json();
                if (ledgerSyncRes.ok && ledgerSyncData.created > 0) {
                  toast.success(`Added ${ledgerSyncData.created} new clients to ledger`);
                }
              } catch (e) {
                console.log("Ledger sync skipped:", e);
              }
              fetchSummaryData(currentPage);
              if (selectedClient) fetchClientDetails(selectedClient);
            } else {
              toast.error(data.detail || "Sync failed");
            }
          } catch (error) {
            toast.error("Sync failed");
          }
        }}
        className="gap-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 h-8 px-2 sm:px-3"
        title="Sync costs from Shipment Costing to Sales & Ledger"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Sync</span>
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => {
          setShowPartnerLedgerUpload(true);
          setPartnerLedgerResults(null);
          setPartnerLedgerFile(null);
          setSelectedPartnerEntries({});
        }}
        className="gap-1 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 h-8 px-2 sm:px-3"
        title="Upload Partner Ledger Excel"
      >
        <Upload className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Ledger</span>
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => window.open('/partner-collections', '_blank')}
        className="gap-1 bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100 h-8 px-2 sm:px-3"
        title="View Partner Collections"
      >
        <Wallet className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Collections</span>
      </Button>
      <Button variant="outline" size="sm" onClick={() => setShowExportDialog(true)} className="gap-1 bg-green-50 border-green-200 text-green-700 hover:bg-green-100 h-8 px-2 sm:px-3">
        <Download className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Export</span>
      </Button>
      <Button variant="outline" size="sm" onClick={() => setShowPrintDialog(true)} className="gap-1 h-8 px-2 sm:px-3">
        <Printer className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Print</span>
      </Button>
      {selectedClient && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchManualEntries(selectedClient);
            setShowEntriesDialog(true);
          }}
          className="gap-1 h-8 px-2 sm:px-3"
        >
          <List className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-xs">Entries ({manualEntries.length})</span>
        </Button>
      )}
      <Button
        variant="outline"
        onClick={() => {
          console.log("Opening simple debit dialog");
          setDebitClientCode(selectedClient || "");
          setDebitAmount("");
          setDebitDate(new Date().toISOString().split("T")[0]);
          setDebitNarration("");
          setShowDebitDialog(true);
        }}
        className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
        data-testid="add-debit-btn-recon"
      >
        <Plus className="w-4 h-4" />
        Add Debit
      </Button>
      <Button
        variant="outline"
        onClick={() => {
          resetManualEntryForm();
          setManualEntryType("give");
          setManualEntryClientCode(selectedClient || "");
          setManualEntryDate(selectedDay && selectedMonth && selectedYear ? `${selectedYear}-${selectedMonth}-${selectedDay}` : "");
          setShowManualEntryDialog(true);
        }}
        className="gap-2 text-green-600 border-green-200 hover:bg-green-50"
      >
        <Plus className="w-4 h-4" />
        Add Credit
      </Button>
    </div>
  );

  // Render summary table (client list) - Full table without scroll, with totals
  const renderSummaryTable = () => {
    // Calculate totals
    const totals = filteredClients.reduce((acc, client) => ({
      order_count: (acc.order_count || 0) + (client.order_count || 0),
      sales_count: (acc.sales_count || 0) + (client.sales_count || 0),
      total_sales: (acc.total_sales || 0) + (client.total_sales || 0),
      error_count: (acc.error_count || 0) + (client.error_count || 0),
      total_costing: (acc.total_costing || 0) + (client.total_costing || 0),
      total_gp_self: (acc.total_gp_self || 0) + (client.total_gp_self || 0),
      total_debit: (acc.total_debit || 0) + (client.total_debit || 0),
      total_credit: (acc.total_credit || 0) + (client.total_credit || 0),
      balance: (acc.balance || 0) + (client.sales_balance ?? client.balance ?? 0),
    }), {});

    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto" data-testid="ledger-summary-table">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs sm:text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("client_code")}>
                    <div className="flex items-center gap-1">Account {getSortIcon("client_code")}</div>
                  </th>
                  <th className="px-1.5 sm:px-3 py-2 text-center text-xs sm:text-sm font-semibold text-white">Type</th>
                  <th className="px-1.5 sm:px-3 py-2 text-center text-xs sm:text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("order_count")}>
                    <div className="flex items-center justify-center gap-1">Orders {getSortIcon("order_count")}</div>
                  </th>
                  <th className="px-1.5 sm:px-3 py-2 text-center text-xs sm:text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_sales")}>
                    <div className="flex items-center justify-center gap-1">Sales {getSortIcon("total_sales")}</div>
                  </th>
                  <th className="px-1.5 sm:px-3 py-2 text-center text-xs sm:text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("error_count")}>
                    <div className="flex items-center justify-center gap-1">Errors {getSortIcon("error_count")}</div>
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_costing")}>
                    <div className="flex items-center justify-end gap-1">Costing {getSortIcon("total_costing")}</div>
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-purple-200 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_gp_self")}>
                    <div className="flex items-center justify-end gap-1">GP Self {getSortIcon("total_gp_self")}</div>
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-red-300 cursor-pointer hover:bg-slate-600 bg-red-900/30" onClick={() => handleSort("total_debit")}>
                    <div className="flex items-center justify-end gap-1">Debit {getSortIcon("total_debit")}</div>
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-green-300 cursor-pointer hover:bg-slate-600 bg-green-900/30" onClick={() => handleSort("total_credit")}>
                    <div className="flex items-center justify-end gap-1">Credit {getSortIcon("total_credit")}</div>
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-orange-300 cursor-pointer hover:bg-slate-600 bg-orange-900/30" onClick={() => handleSort("sales_balance")}>
                    <div className="flex items-center justify-end gap-1">Balance {getSortIcon("sales_balance")}</div>
                  </th>
                  <th className="px-1.5 sm:px-3 py-2 text-center text-xs sm:text-sm font-semibold text-white w-16 sm:w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client, idx) => (
                  <tr key={client.client_code || idx} className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => goToClient(client.client_code)}>
                    <td className="px-3 py-2.5">
                      <span className="font-semibold text-blue-600 text-sm">{client.client_code}</span>
                      {client.under && (
                        <span className="block text-xs text-slate-400">under: {client.under}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {client.account_type === "partner" && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 gap-1 text-xs">
                          <Building2 className="w-3 h-3" /> Partner
                        </Badge>
                      )}
                      {client.account_type === "agent" && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 gap-1 text-xs">
                          <UserCheck className="w-3 h-3" /> Agent
                        </Badge>
                      )}
                      {client.account_type === "client" && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700 gap-1 text-xs">
                          <Users className="w-3 h-3" /> DP CLIENT
                        </Badge>
                      )}
                      {!client.account_type && (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center text-sm font-medium">{client.order_count}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-medium text-blue-700" data-testid={`summary-sales-${client.client_code}`}>₹{(client.total_sales || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2.5 text-center text-sm" data-testid={`summary-errors-${client.client_code}`}>
                      {(client.error_count || 0) > 0 ? (
                        <span className="text-red-600 font-semibold">⚠ {client.error_count}</span>
                      ) : (
                        <span className="text-green-600">✓</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm text-slate-700">₹{client.total_costing?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right text-sm text-purple-600 font-medium">₹{client.total_gp_self?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-semibold text-red-600 bg-red-50/50">₹{client.total_debit?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-semibold text-green-600 bg-green-50/50">₹{client.total_credit?.toLocaleString()}</td>
                    <td className={`px-3 py-2.5 text-right text-sm font-bold ${(client.sales_balance ?? client.balance) > 0 ? 'text-orange-600 bg-orange-50/50' : (client.sales_balance ?? client.balance) < 0 ? 'text-green-600 bg-green-50/50' : 'text-slate-500'}`} data-testid={`summary-balance-${client.client_code}`}>
                      ₹{(client.sales_balance ?? client.balance)?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <Button variant="ghost" size="sm" className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-100">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals Footer */}
              <tfoot className="bg-gradient-to-r from-slate-800 to-slate-900 sticky bottom-0">
                <tr>
                  <td className="px-3 py-3 text-left text-sm font-bold text-white" colSpan={2}>
                    TOTAL ({filteredClients.length} accounts)
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold text-white">{totals.order_count?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-blue-300">₹{totals.total_sales?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-center text-sm font-bold text-red-300">{totals.error_count?.toLocaleString() || 0}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white">₹{totals.total_costing?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-purple-300">₹{totals.total_gp_self?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-red-300 bg-red-900/30">₹{totals.total_debit?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-green-300 bg-green-900/30">₹{totals.total_credit?.toLocaleString()}</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold bg-orange-900/30 ${totals.balance > 0 ? 'text-orange-300' : 'text-green-300'}`}>
                    ₹{totals.balance?.toLocaleString('en-IN')}
                  </td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render year/month/day table - Full table without scroll, with totals
  const renderHierarchyTable = () => {
    const data = getCurrentData();
    const isYearView = viewLevel === "year";
    const isMonthView = viewLevel === "month";
    const labelKey = isYearView ? "year" : isMonthView ? "month" : "day";
    const labelText = isYearView ? "Year" : isMonthView ? "Month" : "Day";

    // Calculate totals
    const totals = data.reduce((acc, item) => ({
      count: (acc.count || 0) + (item.count || 0),
      total_costing: (acc.total_costing || 0) + (item.total_costing || 0),
      total_sell: (acc.total_sell || 0) + (item.total_sell || 0),
      total_gp: (acc.total_gp || 0) + (item.total_gp || 0),
      total_gp_self: (acc.total_gp_self || 0) + (item.total_gp_self || 0),
      total_payment: (acc.total_payment || 0) + (item.total_payment || 0),
      total_debit: (acc.total_debit || 0) + (item.total_debit || 0),
      total_credit: (acc.total_credit || 0) + (item.total_credit || 0),
      error_count: (acc.error_count || 0) + (item.error_count || 0),
    }), {});
    // Ledger (Sales): balance runs on SALES − CREDIT
    totals.balance = (totals.total_sell || 0) - (totals.total_credit || 0);

    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2.5 text-left text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort(labelKey)}>
                    <div className="flex items-center gap-1.5">{labelText} {getSortIcon(labelKey)}</div>
                  </th>
                  <th className="px-3 py-2.5 text-center text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("count")}>
                    <div className="flex items-center justify-center gap-1.5">Parcels {getSortIcon("count")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_costing")}>
                    <div className="flex items-center justify-end gap-1.5">Costing {getSortIcon("total_costing")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_sell")}>
                    <div className="flex items-center justify-end gap-1.5">Sell {getSortIcon("total_sell")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_gp")}>
                    <div className="flex items-center justify-end gap-1.5">GP {getSortIcon("total_gp")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-purple-200 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_gp_self")}>
                    <div className="flex items-center justify-end gap-1.5">GP Self {getSortIcon("total_gp_self")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-red-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_debit")}>
                    <div className="flex items-center justify-end gap-1.5">Debit {getSortIcon("total_debit")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-green-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("total_credit")}>
                    <div className="flex items-center justify-end gap-1.5">Credit {getSortIcon("total_credit")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-orange-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("balance")}>
                    <div className="flex items-center justify-end gap-1.5">Balance {getSortIcon("balance")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-center text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("error_count")}>
                    <div className="flex items-center justify-center gap-1.5">Errors {getSortIcon("error_count")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-center text-sm font-semibold text-white w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => {
                  const label = isYearView ? item.year : isMonthView ? getMonthName(item.month) : `Day ${item.day}`;
                  const onClick = isYearView ? () => goToYear(item.year) : isMonthView ? () => goToMonth(item.month) : () => goToDay(item.day);
                  const itemBalance = (item.total_sell || 0) - (item.total_credit || 0);
                  
                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors" onClick={onClick}>
                      <td className="px-3 py-2.5 font-semibold text-blue-600 text-sm">{label}</td>
                      <td className="px-3 py-2.5 text-center text-sm font-medium">{item.count}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-slate-700">₹{item.total_costing?.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-slate-800 font-medium">₹{item.total_sell?.toLocaleString('en-IN')}</td>
                      <td className={`px-3 py-2.5 text-right text-sm font-semibold ${item.total_gp < 0 ? 'text-red-600' : 'text-green-600'}`}>₹{item.total_gp?.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-purple-600 font-medium">₹{item.total_gp_self?.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-red-600 font-medium bg-red-50/30">₹{(item.total_debit || 0)?.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-green-600 font-medium bg-green-50/30">₹{(item.total_credit || 0)?.toLocaleString('en-IN')}</td>
                      <td className={`px-3 py-2.5 text-right text-sm font-bold ${itemBalance > 0 ? 'text-orange-600 bg-orange-50/50' : itemBalance < 0 ? 'text-green-600 bg-green-50/50' : 'text-slate-500'}`}>
                        ₹{Math.abs(itemBalance)?.toLocaleString('en-IN')}{itemBalance < 0 ? ' CR' : itemBalance > 0 ? ' DR' : ''}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {item.error_count > 0 ? (
                          <Badge variant="destructive" className="gap-1 text-xs"><AlertTriangle className="w-3 h-3" />{item.error_count}</Badge>
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Button variant="ghost" size="sm" className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-100">
                          View <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals Footer */}
              <tfoot className="bg-gradient-to-r from-slate-800 to-slate-900">
                <tr>
                  <td className="px-3 py-3 text-left text-sm font-bold text-white">
                    TOTAL ({data.length} {labelText.toLowerCase()}s)
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold text-white">{totals.count?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white">₹{totals.total_costing?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white">₹{totals.total_sell?.toLocaleString('en-IN')}</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold ${totals.total_gp < 0 ? 'text-red-300' : 'text-green-300'}`}>₹{totals.total_gp?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-purple-300">₹{totals.total_gp_self?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-red-300 bg-red-900/20">₹{(totals.total_debit || 0)?.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-green-300 bg-green-900/20">₹{(totals.total_credit || 0)?.toLocaleString('en-IN')}</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold ${totals.balance > 0 ? 'text-orange-300 bg-orange-900/20' : totals.balance < 0 ? 'text-green-300 bg-green-900/20' : 'text-white'}`}>
                    ₹{Math.abs(totals.balance)?.toLocaleString('en-IN')}{totals.balance < 0 ? ' CR' : totals.balance > 0 ? ' DR' : ''}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold text-white">
                    {totals.error_count > 0 ? (
                      <span className="text-red-300">{totals.error_count}</span>
                    ) : (
                      <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                    )}
                  </td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render notifications table (for shifted, cancelled, date issues)
  const renderNotificationsTable = () => {
    const data = processedTrackingData.notifications || [];
    
    // Group by notification type
    const grouped = {
      shifted: data.filter(d => d.notification_type === "shifted"),
      cancelled: data.filter(d => d.notification_type === "cancelled"),
      replaced: data.filter(d => d.notification_type === "replaced"),
      no_date: data.filter(d => d.notification_type === "no_date")
    };
    
    const renderGroup = (items, title, bgColor, textColor, icon) => {
      if (!items || items.length === 0) return null;
      
      return (
        <div className="mb-6">
          <div className={`flex items-center gap-2 ${bgColor} ${textColor} px-4 py-2 rounded-t-lg`}>
            {icon}
            <span className="font-semibold">{title}</span>
            <span className="text-sm">({items.length})</span>
          </div>
          <div className="border border-t-0 rounded-b-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Tracking</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-right">Original Costing</th>
                  <th className="p-2 text-right">Current Costing</th>
                  <th className="p-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.tracking || idx} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-mono text-xs text-blue-600">{item.tracking}</td>
                    <td className="p-2 text-slate-600">{formatDMY(item.date)}</td>
                    <td className="p-2 text-right text-slate-400 line-through">
                      {item.original_total_cost_before_shift ? `₹${item.original_total_cost_before_shift.toLocaleString()}` : "-"}
                    </td>
                    <td className="p-2 text-right text-green-600 font-semibold">₹0</td>
                    <td className="p-2 text-sm text-slate-500">{item.notification_label || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };
    
    return (
      <Card>
        <CardContent className="p-4">
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Notification Items</p>
                <p className="text-sm text-amber-700 mt-1">
                  These items are shown for reference but do NOT affect account balances. 
                  Shifted and cancelled orders have zero cost. Items with date issues have auto-assigned dates.
                </p>
              </div>
            </div>
          </div>
          
          {data.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No notification items found for this client.
            </div>
          ) : (
            <>
              {renderGroup(grouped.shifted, "Shifted Orders", "bg-amber-100", "text-amber-800", <ArrowUpDown className="w-4 h-4" />)}
              {renderGroup(grouped.cancelled, "Cancelled Orders", "bg-red-100", "text-red-800", <X className="w-4 h-4" />)}
              {renderGroup(grouped.replaced, "Replaced Tracking", "bg-purple-100", "text-purple-800", <RefreshCw className="w-4 h-4" />)}
              {renderGroup(grouped.no_date, "Auto-Dated (Original Date Missing)", "bg-blue-100", "text-blue-800", <Calendar className="w-4 h-4" />)}
            </>
          )}
          
          <div className="mt-4 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setViewLevel("year")}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Yearly View
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render parcels table with expandable product rows - Full table without scroll, with totals
  const renderParcelsTable = () => {
    const data = getCurrentData();
    
    // Calculate totals
    const totals = data.reduce((acc, parcel) => ({
      costing: (acc.costing || 0) + (parcel.costing || 0),
      sell_price: (acc.sell_price || 0) + (parcel.sell_price || 0),
      gp: (acc.gp || 0) + (parcel.gp || 0),
      gp_self: (acc.gp_self || 0) + (parcel.gp_self || 0),
      payment_received: (acc.payment_received || 0) + (parcel.payment_received || 0),
      partner_collection: (acc.partner_collection || 0) + (parcel.partner_collection || 0),
    }), {});
    
    // Calculate debit/credit/balance for totals
    totals.total_debit = (totals.costing || 0) + (totals.gp_self || 0);
    totals.total_credit = totals.payment_received || 0;
    // Ledger (Sales): balance runs on SALES − CREDIT
    totals.balance = (totals.sell_price || 0) - totals.total_credit;
    
    // Count settled entries
    const settledCount = clientDetails?.summary?.settled_count || 0;
    
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {/* Filter bar for Hide Settled */}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
            <div className="text-sm text-slate-600">
              {data.length} entries {hideSettled && settledCount > 0 && <span className="text-amber-600">({settledCount} settled hidden)</span>}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideSettled}
                onChange={(e) => setHideSettled(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">Hide Settled</span>
            </label>
          </div>
          {/* In-ledger live search — filters the parcels of the CURRENT
              client's ledger by tracking / service / country / product /
              customer. Independent of the global header search. */}
          <div className="px-3 pb-2 -mt-1 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={parcelInPageFilter}
              onChange={(e) => setParcelInPageFilter(e.target.value)}
              placeholder="Search within this ledger: tracking, service, country, product..."
              className="flex-1 max-w-md text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              data-testid="in-ledger-parcel-search"
            />
            {parcelInPageFilter && (
              <>
                <span className="text-[11px] text-slate-500">
                  {data.filter((p) => {
                    const q = parcelInPageFilter.trim().toLowerCase();
                    return q
                      ? [p.tracking, p.service, p.country, p.product, p.customer_name, p.client_code]
                          .some(v => (v || "").toString().toLowerCase().includes(q))
                      : true;
                  }).length} / {data.length}
                </span>
                <button
                  onClick={() => setParcelInPageFilter("")}
                  className="text-[11px] text-rose-600 hover:underline"
                  data-testid="in-ledger-parcel-search-clear"
                >
                  Clear
                </button>
              </>
            )}
          </div>
          <div className="overflow-x-auto" data-testid="ledger-parcels-table">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2.5 text-center w-10"></th>
                  <th className="px-3 py-2.5 text-left text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("tracking")}>
                    <div className="flex items-center gap-1.5">Tracking {getSortIcon("tracking")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-center text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("date")}>
                    <div className="flex items-center justify-center gap-1.5">Date {getSortIcon("date")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("costing")}>
                    <div className="flex items-center justify-end gap-1.5">Costing {getSortIcon("costing")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("sell_price")}>
                    <div className="flex items-center justify-end gap-1.5">Sell {getSortIcon("sell_price")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("gp")}>
                    <div className="flex items-center justify-end gap-1.5">GP {getSortIcon("gp")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-purple-200 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("gp_self")}>
                    <div className="flex items-center justify-end gap-1.5">GP Self {getSortIcon("gp_self")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-cyan-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("partner_collection")} title="Invoice payment received by partner from customer (Info only - doesn't affect balance)">
                    <div className="flex items-center justify-end gap-1.5">Partner Coll. {getSortIcon("partner_collection")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-red-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("debit")}>
                    <div className="flex items-center justify-end gap-1.5">Debit {getSortIcon("debit")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-green-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("payment_received")}>
                    <div className="flex items-center justify-end gap-1.5">Credit {getSortIcon("payment_received")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-sm font-semibold text-orange-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("balance")}>
                    <div className="flex items-center justify-end gap-1.5">Balance {getSortIcon("balance")}</div>
                  </th>
                  <th className="px-3 py-2.5 text-center text-sm font-semibold text-white cursor-pointer hover:bg-slate-600" onClick={() => handleSort("status")}>
                    <div className="flex items-center justify-center gap-1.5">Status {getSortIcon("status")}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(parcelInPageFilter.trim()
                  ? data.filter((p) => {
                      const q = parcelInPageFilter.trim().toLowerCase();
                      return [p.tracking, p.service, p.country, p.product, p.customer_name, p.client_code]
                        .some(v => (v || "").toString().toLowerCase().includes(q));
                    })
                  : data
                ).map((parcel, idx) => {
                  const isExpanded = expandedRows.has(parcel.tracking);
                  const isLoading = loadingProducts[parcel.tracking];
                  const productData = trackingProducts[parcel.tracking];
                  
                  // Ledger (Sales): balance runs on SALES − CREDIT
                  const parcelDebit = (parcel.costing || 0) + (parcel.gp_self || 0);
                  const parcelCredit = parcel.payment_received || 0;
                  const parcelBalance = (parcel.sell_price || 0) - parcelCredit;
                  
                  const trackingUpper = (parcel.tracking || "").toUpperCase();
                  const isSearchedRow = !!searchedTracking && trackingUpper === (searchedTracking || "").toUpperCase();
                  return (
                    <>
                      <tr
                        key={parcel.tracking || idx}
                        ref={isSearchedRow ? (el) => {
                          // Scroll the matched row into view + flash a yellow
                          // highlight when the global search navigates here.
                          // Runs once per mount; consecutive searches re-mount
                          // because the parent re-renders with a new tracking.
                          if (el && !el.dataset._scrolledForSearch) {
                            el.dataset._scrolledForSearch = "1";
                            setTimeout(() => {
                              try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_e) {}
                              el.classList.add("ring-2", "ring-yellow-400", "bg-yellow-50");
                              setTimeout(() => el.classList.remove("ring-2", "ring-yellow-400", "bg-yellow-50"), 4000);
                            }, 250);
                          }
                        } : undefined}
                        data-tracking={parcel.tracking}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${parcel.status === "error" ? "bg-red-50" : ""} ${isExpanded ? "bg-blue-50" : ""} ${isSearchedRow ? "outline outline-2 outline-yellow-400" : ""}`}
                        onClick={() => toggleProductsExpand(parcel.tracking)}
                        data-testid={`parcel-row-${parcel.tracking}`}
                      >
                        <td className="px-2 py-2.5 text-center">
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-blue-500 mx-auto" />
                          ) : isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-blue-500 mx-auto" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-2.5 font-mono text-sm text-blue-600 font-medium">{parcel.tracking}</td>
                        <td className="px-3 py-2.5 text-center text-sm">{formatDMY(parcel.date)}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-slate-700">₹{parcel.costing?.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-slate-800 font-medium">₹{parcel.sell_price?.toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-right text-sm font-semibold ${parcel.gp < 0 ? "text-red-600" : "text-green-600"}`}>₹{parcel.gp?.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-purple-600 font-medium">₹{parcel.gp_self?.toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-right text-sm font-medium ${parcel.partner_collection > 0 ? 'text-cyan-600 bg-cyan-50/30' : 'text-slate-400'}`} title="Invoice paid by customer to partner">
                          {parcel.partner_collection > 0 ? `₹${parcel.partner_collection?.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-sm text-red-600 font-medium bg-red-50/30">₹{parcelDebit?.toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-right text-sm font-medium ${
                          parcel.payment_ref 
                            ? 'text-slate-500 bg-slate-50/50 italic' 
                            : parcelCredit > 0 
                              ? 'text-green-600 bg-green-50/30' 
                              : 'text-green-600 bg-green-50/30'
                        }`}>
                          {parcel.payment_ref ? (
                            <span title={`Part of multi-parcel payment - Credit applied to first parcel`}>
                              {parcel.payment_ref}
                            </span>
                          ) : (
                            `₹${parcelCredit?.toLocaleString()}`
                          )}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-sm font-bold ${parcelBalance > 0 ? 'text-orange-600 bg-orange-50/50' : parcelBalance < 0 ? 'text-green-600 bg-green-50/50' : 'text-slate-500'}`}>
                          ₹{Math.abs(parcelBalance)?.toLocaleString()}{parcelBalance < 0 ? ' CR' : parcelBalance > 0 ? ' DR' : ''}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {(parcel.error_count || 0) > 0 && (
                            <Badge
                              variant="destructive"
                              className="gap-1 text-xs cursor-pointer mr-1"
                              data-testid={`parcel-error-badge-${parcel.tracking}`}
                              title={(parcel.errors_list || []).map(e => (typeof e === 'string' ? e : (e?.message || e?.type || JSON.stringify(e)))).join('\n') || 'Errors on this parcel'}
                              onClick={(e) => { e.stopPropagation(); window.open(`/error-resolution?search=${encodeURIComponent(parcel.tracking || '')}`, '_blank'); }}
                            >
                              <AlertTriangle className="w-3 h-3" /> {parcel.error_count} err
                            </Badge>
                          )}
                          {parcel.status === "error" && <Badge variant="destructive" className="gap-1 text-xs"><AlertTriangle className="w-3 h-3" /> Error</Badge>}
                          {parcel.status === "settled" && <Badge className="gap-1 bg-green-100 text-green-700 text-xs"><CheckCircle className="w-3 h-3" /> Settled</Badge>}
                          {parcel.status === "pending" && <Badge variant="outline" className="gap-1 text-yellow-700 border-yellow-300 text-xs">Pending</Badge>}
                        </td>
                      </tr>
                      
                      {/* Expanded Products Row */}
                      {isExpanded && productData && (
                        <tr key={`${parcel.tracking}-products`} className="bg-blue-50/50">
                          <td colSpan={12} className="p-0">
                            <div className="p-4 border-l-4 border-blue-500 ml-4 mr-4 mb-2">
                              <div className="flex items-center gap-2 mb-3">
                                <Package className="w-4 h-4 text-blue-600" />
                                <span className="font-semibold text-blue-800 text-sm">
                                  Products in Parcel ({productData.summary?.total_products || 0} items)
                                </span>
                              </div>
                              
                              {/* Shipment Products (Costing) */}
                              {productData.shipment_products?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-slate-500 mb-2">Costing Details:</p>
                                  <div className="bg-white rounded-lg border overflow-hidden">
                                    <table className="w-full text-sm">
                                      <thead className="bg-slate-100">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-medium">Product</th>
                                          <th className="px-3 py-2 text-center font-medium">Qty</th>
                                          <th className="px-3 py-2 text-right font-medium">Med Cost</th>
                                          <th className="px-3 py-2 text-right font-medium">Ship Cost</th>
                                          <th className="px-3 py-2 text-right font-medium">Total</th>
                                          <th className="px-3 py-2 text-center font-medium">Weight</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {productData.shipment_products.map((prod, pIdx) => (
                                          <tr key={pIdx} className="border-t">
                                            <td className="px-3 py-2 text-slate-700">{prod.product_name}</td>
                                            <td className="px-3 py-2 text-center">{prod.quantity}</td>
                                            <td className="px-3 py-2 text-right text-slate-600">₹{prod.medicine_cost?.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right text-slate-600">₹{prod.shipping_cost?.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right font-semibold">₹{prod.total_cost?.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-center text-slate-500">{prod.weight}g</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              
                              {/* Sales Products */}
                              {productData.sales_products?.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-2">Sales Details:</p>
                                  <div className="bg-white rounded-lg border overflow-hidden">
                                    <table className="w-full text-sm">
                                      <thead className="bg-green-50">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-medium">Product</th>
                                          <th className="px-3 py-2 text-center font-medium">Qty</th>
                                          <th className="px-3 py-2 text-right font-medium">Sell Price</th>
                                          <th className="px-3 py-2 text-left font-medium">Customer</th>
                                          <th className="px-3 py-2 text-center font-medium">Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {productData.sales_products.map((prod, pIdx) => (
                                          <tr key={pIdx} className="border-t">
                                            <td className="px-3 py-2 text-slate-700">{prod.product_name}</td>
                                            <td className="px-3 py-2 text-center">{prod.quantity}</td>
                                            <td className="px-3 py-2 text-right text-green-600 font-semibold">₹{prod.sell_price?.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-slate-600">{prod.customer_name || "-"}</td>
                                            <td className="px-3 py-2 text-center text-slate-500">{formatDMY(prod.sale_date)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              
                              {/* No products found */}
                              {!productData.shipment_products?.length && !productData.sales_products?.length && (
                                <div className="text-center py-4 text-slate-500 text-sm">
                                  No product details found for this tracking number
                                </div>
                              )}
                              
                              {/* Summary */}
                              {productData.summary && (productData.shipment_products?.length > 0 || productData.sales_products?.length > 0) && (
                                <div className="mt-3 pt-3 border-t border-blue-200 flex gap-6 text-sm">
                                  <div>
                                    <span className="text-slate-500">Total Qty:</span>
                                    <span className="ml-2 font-semibold">{productData.summary.total_quantity}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Total Cost:</span>
                                    <span className="ml-2 font-semibold text-red-600">₹{productData.summary.total_cost?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Total Sell:</span>
                                    <span className="ml-2 font-semibold text-green-600">₹{productData.summary.total_sell?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Gross Profit:</span>
                                    <span className={`ml-2 font-semibold ${productData.summary.gross_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                      ₹{productData.summary.gross_profit?.toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
              {/* Totals Footer */}
              <tfoot className="bg-gradient-to-r from-slate-800 to-slate-900">
                <tr>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3 text-left text-sm font-bold text-white" colSpan={2}>
                    TOTAL ({data.length} parcels)
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white">₹{totals.costing?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white">₹{totals.sell_price?.toLocaleString()}</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold ${totals.gp < 0 ? 'text-red-300' : 'text-green-300'}`}>₹{totals.gp?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-purple-300">₹{totals.gp_self?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-red-300 bg-red-900/20">₹{(totals.total_debit || 0)?.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-green-300 bg-green-900/20">₹{(totals.total_credit || 0)?.toLocaleString()}</td>
                  <td className={`px-3 py-3 text-right text-sm font-bold ${totals.balance > 0 ? 'text-orange-300 bg-orange-900/20' : totals.balance < 0 ? 'text-green-300 bg-green-900/20' : 'text-white'}`}>
                    ₹{Math.abs(totals.balance)?.toLocaleString()}{totals.balance < 0 ? ' CR' : totals.balance > 0 ? ' DR' : ''}
                  </td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Get current columns for print dialog
  const getCurrentColumnOptions = () => {
    if (viewLevel === "summary") return columnOptions.summary;
    if (viewLevel === "parcels") return columnOptions.parcels;
    return columnOptions.hierarchy;
  };

  return (
    <div className="p-3 lg:p-6 max-w-[1600px] mx-auto" ref={printRef}>

      {/* Header - Mobile Responsive */}
      <div className="flex items-center justify-between mb-2 lg:mb-3 gap-2">
        <div className="flex items-center gap-2 lg:gap-3 min-w-0">
          {viewLevel !== "summary" && (
            <Button variant="outline" size="sm" onClick={goBack} className="gap-1 h-8 lg:h-9 px-2 lg:px-3 flex-shrink-0">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          )}
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base lg:text-xl font-bold text-slate-800 truncate">
                <span className="hidden sm:inline">Ledger Reconciliation</span>
                <span className="sm:hidden">Ledger</span>
              </h1>
              <p className="text-slate-500 text-xs lg:text-sm truncate hidden sm:block">
                {viewLevel === "summary" && "Sales Team ledger summary"}
                {viewLevel === "notifications" && `${selectedClient} - Notifications`}
                {viewLevel === "year" && `${selectedClient} - Yearly`}
                {viewLevel === "month" && `${selectedClient} - ${selectedYear}`}
                {viewLevel === "day" && `${selectedClient} - ${getMonthName(selectedMonth)} ${selectedYear}`}
                {viewLevel === "parcels" && searchedTracking && `${selectedClient} - "${searchedTracking}"`}
                {viewLevel === "parcels" && !searchedTracking && `${selectedClient} - ${selectedDay}/${selectedMonth}/${selectedYear}`}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 lg:h-9 px-2 lg:px-3 gap-1 ${showFilters ? "bg-blue-50 border-blue-300" : ""}`}>
            <Filter className="w-4 h-4" />
            <span className="hidden lg:inline">Filters</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchSummaryData(currentPage); if (selectedClient) fetchClientDetails(selectedClient); }} disabled={loading} className="h-8 lg:h-9 px-2 lg:px-3 gap-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">Refresh</span>
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {renderActionButtons()}
          </div>
        </div>
      </div>

      {/* Main View Toggle - Mobile Responsive */}
      <div className="mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 lg:gap-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5 lg:p-1 flex-shrink-0">
            <button
              onClick={() => setMainView("ledger")}
              className={`px-2 lg:px-4 py-1.5 lg:py-2 rounded-md text-xs lg:text-sm font-medium transition-all whitespace-nowrap touch-manipulation ${
                mainView === "ledger" 
                  ? "bg-white text-blue-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid="ledger-view-btn"
            >
              <BookOpen className="w-3.5 h-3.5 lg:w-4 lg:h-4 inline mr-1 lg:mr-2" />
              Ledger (Sales)
            </button>
            <button
              onClick={() => {
                setMainView("account");
                // Carry the drilled-down client into the Account subpanel
                if (selectedClient && selectedClient !== accountViewPartner) {
                  setAccountViewPartner(selectedClient);
                  fetchAccountViewData(selectedClient);
                }
              }}
              className={`px-2 lg:px-4 py-1.5 lg:py-2 rounded-md text-xs lg:text-sm font-medium transition-all whitespace-nowrap touch-manipulation ${
                mainView === "account" 
                  ? "bg-white text-emerald-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid="account-view-btn"
            >
              <CreditCard className="w-3.5 h-3.5 lg:w-4 lg:h-4 inline mr-1 lg:mr-2" />
              Account
            </button>
            <button
              onClick={() => setMainView("advances")}
              className={`px-2 lg:px-4 py-1.5 lg:py-2 rounded-md text-xs lg:text-sm font-medium transition-all whitespace-nowrap touch-manipulation ${
                mainView === "advances" 
                  ? "bg-white text-blue-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid="advances-view-btn"
            >
              <Wallet className="w-3.5 h-3.5 lg:w-4 lg:h-4 inline mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Advances</span>
              <span className="sm:hidden">Adv</span>
            </button>
          </div>
          {mainView === "advances" && (
            <Badge className="bg-blue-100 text-blue-700 text-xs flex-shrink-0">
              {advancePayments.length}
            </Badge>
          )}
          {mainView === "account" && accountViewData && (
            <Badge className="bg-emerald-100 text-emerald-700 text-xs flex-shrink-0">
              {accountViewMode === "payment" 
                ? `${accountViewData.payment_group_count} Groups` 
                : `${accountViewData.ledger_entries?.length || 0} Parcels`}
            </Badge>
          )}
        </div>
      </div>
        
        {/* Quick Filters + Global Search Row */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-3 lg:mb-4">
          {/* Sale Team Quick Filter */}
          <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
            <SelectTrigger className="w-[110px] sm:w-[140px] h-8 sm:h-9 lg:h-10 text-xs sm:text-sm" data-testid="sale-team-filter">
              <SelectValue placeholder="Sale Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"><Users className="w-4 h-4 inline mr-2 text-slate-500" />All Teams</SelectItem>
              <SelectItem value="client"><Users className="w-4 h-4 inline mr-2 text-green-600" />DP Clients</SelectItem>
              <SelectItem value="partner"><Building2 className="w-4 h-4 inline mr-2 text-purple-600" />Partners</SelectItem>
              <SelectItem value="agent"><UserCheck className="w-4 h-4 inline mr-2 text-blue-600" />Agents</SelectItem>
            </SelectContent>
          </Select>

          {/* Under Manager Quick Filter */}
          <Select value={underFilter} onValueChange={setUnderFilter}>
            <SelectTrigger className="w-[110px] sm:w-[140px] h-8 sm:h-9 lg:h-10 text-xs sm:text-sm" data-testid="under-manager-filter">
              <SelectValue placeholder="Manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"><Briefcase className="w-4 h-4 inline mr-2 text-slate-500" />All Managers</SelectItem>
              {managers.map(m => (
                <SelectItem key={m} value={m}>
                  <Briefcase className="w-4 h-4 inline mr-2 text-amber-600" />{m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active filter badges */}
          {(accountTypeFilter !== "all" || underFilter !== "all") && (
            <div className="flex items-center gap-1">
              {accountTypeFilter !== "all" && (
                <Badge variant="secondary" className={`gap-1 text-xs ${
                  accountTypeFilter === "partner" ? "bg-purple-100 text-purple-700" :
                  accountTypeFilter === "agent" ? "bg-blue-100 text-blue-700" :
                  "bg-green-100 text-green-700"
                }`}>
                  {accountTypeFilter === "partner" ? "Partners" : accountTypeFilter === "agent" ? "Agents" : "DP Clients"}
                  <button onClick={() => setAccountTypeFilter("all")} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
                </Badge>
              )}
              {underFilter !== "all" && (
                <Badge variant="secondary" className="gap-1 text-xs bg-amber-100 text-amber-700">
                  Under: {underFilter}
                  <button onClick={() => setUnderFilter("all")} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
                </Badge>
              )}
            </div>
          )}

          {/* Global Search */}
          <div className="relative flex items-center gap-2 flex-1 max-w-md ml-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              {/* Was <DMYDateInput> by mistake — that opens a calendar
                  popover on click and swallows keystrokes, so the user
                  couldn't actually search by tracking. Plain Input restores
                  free-text search. */}
              <Input
                type="text"
                placeholder="Search tracking, partner..."
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-9 pr-8 h-9 lg:h-10 w-full text-sm border-slate-200 focus:border-blue-500 rounded-lg"
                data-testid="global-search-input"
              />
              {globalSearchQuery && (
                <button
                  onClick={clearGlobalSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {isSearching && (
                <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />
              )}
            </div>
          
            {searchNavigated && (
              <Badge variant="secondary" className="gap-1 py-1 px-2 bg-blue-100 text-blue-700 text-xs hidden sm:flex">
                <Search className="w-3 h-3" />
                Result
                <button onClick={clearGlobalSearch} className="ml-1 hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          
          {/* Search Results Dropdown */}
          {showSearchResults && globalSearchResults && (
            <div className="absolute z-50 top-full right-0 mt-2 w-80 lg:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
              <div className="p-2 lg:p-3 bg-slate-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs lg:text-sm text-slate-600">
                    {globalSearchResults.total_matches} result{globalSearchResults.total_matches !== 1 ? 's' : ''}
                  </span>
                  {globalSearchResults.total_matches > 0 && (
                    <span className="text-[10px] lg:text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full hidden sm:inline">
                      Enter ↵
                    </span>
                  )}
                </div>
                <button onClick={() => setShowSearchResults(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="max-h-[280px] lg:max-h-[350px] overflow-y-auto">
                {/* Client Matches */}
                {globalSearchResults.client_matches?.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs font-semibold text-slate-500 px-2 py-1">CLIENTS</p>
                    {globalSearchResults.client_matches.map((result, idx) => (
                      <button
                        key={`client-${idx}`}
                        onClick={() => navigateToSearchResult(result)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-lg flex items-center justify-between group transition-colors"
                        data-testid={`search-result-client-${result.client_code}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Users className="w-3 h-3 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">{result.client_code}</p>
                            <p className="text-xs text-slate-500">{result.total_orders} orders</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Tracking Matches */}
                {globalSearchResults.tracking_matches?.length > 0 && (
                  <div className="p-2 border-t">
                    <p className="text-xs font-semibold text-slate-500 px-2 py-1">TRACKING</p>
                    {globalSearchResults.tracking_matches.slice(0, 10).map((result, idx) => (
                      <button
                        key={`tracking-${idx}`}
                        onClick={() => navigateToSearchResult(result)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-lg flex items-center justify-between group transition-colors"
                        data-testid={`search-result-tracking-${result.tracking}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Package className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-mono text-xs font-medium text-blue-700">{result.tracking}</p>
                            <p className="text-xs text-slate-500">{result.client_code}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                      </button>
                    ))}
                    {globalSearchResults.tracking_matches.length > 10 && (
                      <p className="text-xs text-center text-slate-400 py-1">
                        +{globalSearchResults.tracking_matches.length - 10} more
                      </p>
                    )}
                  </div>
                )}
                
                {/* No results */}
                {globalSearchResults.total_matches === 0 && (
                  <div className="p-6 text-center">
                    <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No results found</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Partner Ledger Account View - Traditional Accounting Format */}
      {mainView === "account" && (
        <Card className="border-slate-200 mb-4 lg:mb-6">
          <CardHeader className="p-3 lg:pb-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                    <CreditCard className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600 flex-shrink-0" />
                    <span className="truncate">Partner Ledger Account</span>
                  </CardTitle>
                  <p className="text-xs lg:text-sm text-slate-500 mt-1 hidden sm:block">
                    Traditional Debit/Credit ledger with running balance
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setShowPartnerLedgerUpload(true);
                    setPartnerLedgerResults(null);
                    setPartnerLedgerFile(null);
                    setSelectedPartnerEntries({});
                  }}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 h-8 text-xs"
                  data-testid="account-view-upload-sheet-btn"
                >
                  <Upload className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Upload Sheet</span>
                </Button>
                <div className="w-px h-5 bg-slate-200 hidden sm:block"></div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    console.log("Account View Debit clicked, partner:", accountViewPartner);
                    openAccountViewManualEntry('debit');
                  }}
                  disabled={!accountViewPartner}
                  className="border-red-200 text-red-700 hover:bg-red-50 h-8 text-xs"
                >
                  <Minus className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Debit</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => openAccountViewManualEntry('credit')}
                  disabled={!accountViewPartner}
                  className="border-green-200 text-green-700 hover:bg-green-50 h-8 text-xs"
                >
                  <Plus className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Credit</span>
                </Button>
                <div className="w-px h-5 bg-slate-200 hidden sm:block"></div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAccountReportDialog(true)}
                  disabled={!accountViewData}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50 h-8 text-xs"
                >
                  <BarChart3 className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Report</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!accountViewData) {
                      toast.error("Please load data first");
                      return;
                    }
                    setShowAccountPrintDialog(true);
                  }}
                  disabled={!accountViewData}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8 text-xs"
                  data-testid="account-print-btn"
                >
                  <Printer className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Print</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Partner Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] items-end gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="min-w-0">
                <Label className="text-xs text-slate-600 mb-1 block">Client Code</Label>
                <Select value={accountViewPartner} onValueChange={setAccountViewPartner}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partnersList.length === 0 ? (
                      <SelectItem value="_empty" disabled>No clients found - Click "Sync Data" to refresh</SelectItem>
                    ) : (
                      partnersList.map(code => (
                        <SelectItem key={code} value={code}>{code}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:contents">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">From</Label>
                  <DMYDateInput value={accountViewDateFrom} onChange={(e) => setAccountViewDateFrom(e.target.value)} className="h-9" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">To</Label>
                  <DMYDateInput value={accountViewDateTo} onChange={(e) => setAccountViewDateTo(e.target.value)} className="h-9" />
                </div>
              </div>
              <Button 
                onClick={() => fetchAccountViewData(accountViewPartner)}
                disabled={!accountViewPartner || accountViewLoading}
                className="h-9 bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              >
                {accountViewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Load
              </Button>
              {(accountViewDateFrom || accountViewDateTo) && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setAccountViewDateFrom("");
                    setAccountViewDateTo("");
                    if (accountViewPartner) {
                      setTimeout(() => fetchAccountViewData(accountViewPartner), 100);
                    }
                  }}
                  className="h-9 text-red-600 border-red-200 hover:bg-red-50 w-full sm:w-auto"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <span className="text-xs sm:text-sm font-medium text-blue-800 whitespace-nowrap">View:</span>
                <div className="inline-flex bg-white rounded-lg p-0.5 border border-blue-200">
                  <button
                    onClick={() => {
                      setAccountViewMode("parcel");
                      setAccountViewData(null);
                      setAccountTableFilter('all');
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      accountViewMode === "parcel" 
                        ? "bg-blue-600 text-white shadow-sm" 
                        : "text-blue-700 hover:bg-blue-50"
                    }`}
                  >
                    Parcel-wise
                  </button>
                  <button
                    onClick={() => {
                      setAccountViewMode("payment");
                      setAccountViewData(null);
                      setAccountTableFilter('all');
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      accountViewMode === "payment" 
                        ? "bg-blue-600 text-white shadow-sm" 
                        : "text-blue-700 hover:bg-blue-50"
                    }`}
                  >
                    Payment-wise
                  </button>
                </div>
              </div>
              <span className="text-xs text-blue-600 hidden sm:inline">
                {accountViewMode === "parcel" 
                  ? "Each parcel shown separately with its payment" 
                  : "Parcels grouped by payment - Credit shown once per payment"}
              </span>
              <button
                onClick={fetchDeletedEntries}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                data-testid="recently-deleted-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Recently Deleted
              </button>
            </div>

            {/* Account View Content */}
            {accountViewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : !accountViewData ? (
              <div className="text-center py-12 text-slate-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Select a Partner</p>
                <p className="text-sm mt-1">Choose a partner from the dropdown and click &quot;Load Account&quot; to view their ledger.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Account Header */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2">
                    <h3 className="text-sm sm:text-lg font-bold text-emerald-800 truncate">
                      {accountViewData.partner_name} ({accountViewData.partner_code})
                    </h3>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Period</p>
                      <p className="text-xs sm:text-sm font-medium">
                        {accountViewData.date_range?.from || 'All'} to {accountViewData.date_range?.to || 'All'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                    <div className="bg-white rounded px-2 sm:px-3 py-1 sm:py-1.5 border border-emerald-100">
                      <span className="text-slate-500">Self:</span>{' '}
                      <span className="font-semibold">{accountViewData.profit_sharing?.self_percentage}%</span>
                    </div>
                    <div className="bg-white rounded px-2 sm:px-3 py-1 sm:py-1.5 border border-emerald-100">
                      <span className="text-slate-500">Partner:</span>{' '}
                      <span className="font-semibold">{accountViewData.profit_sharing?.partner_percentage}%</span>
                    </div>
                    {accountViewData.profit_sharing?.third_name && (
                      <div className="bg-white rounded px-2 sm:px-3 py-1 sm:py-1.5 border border-emerald-100">
                        <span className="text-slate-500">{accountViewData.profit_sharing.third_name}:</span>{' '}
                        <span className="font-semibold">{accountViewData.profit_sharing?.third_percentage}%</span>
                      </div>
                    )}
                    <div className="sm:ml-auto bg-white rounded px-2 sm:px-3 py-1 sm:py-1.5 border border-emerald-100">
                      <span className="text-slate-500">Opening:</span>{' '}
                      <span className="font-bold text-xs sm:text-sm">{accountViewData.opening_balance?.toLocaleString('en-IN', {style:'currency', currency:'INR', maximumFractionDigits:0})}</span>
                    </div>
                  </div>
                </div>

                {/* Total Advances Tile — partner-level snapshot of working capital
                    parked in customer advances. Clickable: jumps to the Advances
                    tab pre-filtered for this partner. */}
                {(() => {
                  const advEntries = (accountViewData.ledger_entries || []).filter(e => e.is_advance_utilization && e.advance_ref);
                  if (advEntries.length === 0) return null;
                  const uniqueAdvances = new Map();
                  advEntries.forEach(e => {
                    if (!uniqueAdvances.has(e.advance_ref)) {
                      uniqueAdvances.set(e.advance_ref, {
                        ref: e.advance_ref,
                        customer: e.customer,
                        total: e.advance_total_amount || 0,
                        parcels: 0,
                      });
                    }
                    uniqueAdvances.get(e.advance_ref).parcels += 1;
                  });
                  const advList = Array.from(uniqueAdvances.values()).sort((a, b) => b.total - a.total);
                  const grandTotal = advList.reduce((s, a) => s + a.total, 0);
                  const uniqueCustomers = new Set(advList.map(a => a.customer)).size;
                  // Build sparkline points from the trend endpoint (last N months).
                  // We draw the liability_at_close so finance sees the working-capital
                  // build-up over time. If unavailable, the chart is silently hidden.
                  const trendMonths = advanceTrend?.months || [];
                  const trendOK = trendMonths.length >= 2;
                  const sparkW = 140, sparkH = 36, sparkPad = 2;
                  let pathD = "", areaD = "", maxAbs = 0, lastVal = 0;
                  if (trendOK) {
                    const vals = trendMonths.map(m => m.liability_at_close || 0);
                    maxAbs = Math.max(...vals.map(v => Math.abs(v)), 1);
                    const stepX = (sparkW - sparkPad * 2) / (vals.length - 1);
                    const midY = sparkH / 2;
                    const yFor = v => midY - (v / maxAbs) * (midY - sparkPad);
                    pathD = vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${sparkPad + i * stepX} ${yFor(v)}`).join(' ');
                    areaD = `${pathD} L ${sparkPad + (vals.length - 1) * stepX} ${midY} L ${sparkPad} ${midY} Z`;
                    lastVal = vals[vals.length - 1];
                  }
                  return (
                    <div
                      className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200 rounded-lg p-3 sm:p-4"
                      data-testid="account-advances-tile"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Wallet className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-semibold text-indigo-800">
                            Customer Advances on Hand
                          </span>
                          <Badge className="bg-white text-indigo-700 border-indigo-200 text-[10px] font-mono">
                            {advList.length} advance{advList.length > 1 ? 's' : ''} · {uniqueCustomers} customer{uniqueCustomers > 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(`Auto-link unallocated parcels by customer name for partner ${accountViewData.partner_code}?\n\nThis scans every upload of this partner and attaches any orphan parcel (no advance ref) to the existing advance reference of the SAME customer. Use this to fix cross-month gaps where the Feb sheet's advance row didn't parse but the Mar sheet's advance ref is known.`)) return;
                              try {
                                const resp = await fetch(
                                  `${BACKEND_URL}/api/partner-ledger-account/${accountViewData.partner_code}/repair-advance-links`,
                                  { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' }
                                );
                                const data = await resp.json();
                                if (resp.ok) {
                                  toast.success(`Linked ${data.linked_count} parcel(s) across ${Object.keys(data.by_customer || {}).length} customer(s).`);
                                  fetchAccountViewData(accountViewData.partner_code);
                                } else {
                                  toast.error(data.detail || 'Failed to repair advance links');
                                }
                              } catch (e) {
                                toast.error('Repair failed: ' + (e.message || e));
                              }
                            }}
                            className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 hover:bg-amber-50 px-2 py-1 rounded transition-colors border border-amber-300 bg-white"
                            title="Scan all uploads of this partner and attach orphan parcels (no advance ref) to the existing advance ref of the same customer. Fixes cross-month gaps."
                            data-testid="account-advances-repair-btn"
                          >
                            🔧 Repair Links
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMainView("advances");
                              setAdvanceSearchTerm(accountViewData.partner_code || "");
                              setAdvanceStatusFilter("all");
                              setAdvanceHideFullyUsed(false);
                            }}
                            className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline transition-colors"
                            data-testid="account-advances-view-all-btn"
                          >
                            View All in Advances →
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-base sm:text-lg font-bold text-indigo-900">
                              ₹{grandTotal.toLocaleString()}
                            </span>
                            <span className="text-[11px] text-slate-500">total linked to this partner</span>
                          </div>
                        </div>
                        {trendOK && (
                          <div
                            className="flex items-center gap-2 bg-white/70 rounded-md border border-indigo-100 px-2 py-1"
                            title={trendMonths.map(m => `${m.label}: ₹${(m.liability_at_close || 0).toLocaleString()} (recv ₹${m.received.toLocaleString()} / used ₹${m.utilized.toLocaleString()})`).join('\n')}
                            data-testid="account-advances-trend"
                          >
                            <div className="text-[10px] text-slate-500 leading-tight">
                              <div>Liability trend</div>
                              <div className="text-[9px]">last {trendMonths.length} mo</div>
                            </div>
                            <svg width={sparkW} height={sparkH} className="overflow-visible">
                              <line x1={sparkPad} x2={sparkW - sparkPad} y1={sparkH / 2} y2={sparkH / 2} stroke="#cbd5e1" strokeDasharray="2 2" strokeWidth="1" />
                              <path d={areaD} fill="#6366f1" fillOpacity="0.15" />
                              <path d={pathD} stroke="#4f46e5" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                              {trendMonths.map((m, i) => {
                                const stepX = (sparkW - sparkPad * 2) / (trendMonths.length - 1);
                                const midY = sparkH / 2;
                                const v = m.liability_at_close || 0;
                                const cy = midY - (v / maxAbs) * (midY - sparkPad);
                                return <circle key={m.ym} cx={sparkPad + i * stepX} cy={cy} r="1.8" fill="#4f46e5" />;
                              })}
                            </svg>
                            <div className="text-[10px] font-semibold leading-tight">
                              <div className={lastVal > 0 ? 'text-blue-700' : lastVal < 0 ? 'text-red-700' : 'text-slate-500'}>
                                ₹{Math.abs(lastVal).toLocaleString()}
                              </div>
                              <div className="text-[9px] text-slate-400">{lastVal > 0 ? 'liability' : lastVal < 0 ? 'over-used' : 'flat'}</div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {advList.slice(0, 8).map(a => {
                          const c = getAdvanceColor(a.ref);
                          return (
                            <button
                              key={a.ref}
                              type="button"
                              onClick={() => {
                                setMainView("advances");
                                setAdvanceSearchTerm(a.ref);
                                setAdvanceStatusFilter("all");
                                setAdvanceHideFullyUsed(false);
                              }}
                              className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 rounded border hover:brightness-95 hover:underline transition ${c.chip}`}
                              title={`${a.customer} — ${a.parcels} parcel${a.parcels > 1 ? 's' : ''} — click to open in Advances`}
                              data-testid={`account-advance-pill-${a.ref}`}
                            >
                              {a.ref} · ₹{a.total.toLocaleString()}
                              <span className="ml-1 opacity-70">×{a.parcels}</span>
                            </button>
                          );
                        })}
                        {advList.length > 8 && (
                          <span className="text-[10px] text-indigo-700 self-center">+{advList.length - 8} more</span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Multi-select filter chips. Click any chip to toggle it ON/OFF.
                    Multiple selections OR together (a row appears if it
                    matches ANY active chip). No active chips == "All entries". */}
                <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid="account-multi-filter-bar">
                  <span className="text-xs font-medium text-slate-500 inline-flex items-center gap-1 mr-1">
                    <Filter className="w-3.5 h-3.5" />
                    Filters:
                  </span>
                  {/* In-ledger live search for the Account view. Independent
                      of the global header search. Returns a "no match" row
                      inside the table when the query has zero hits so users
                      know the tracking isn't in this client's ledger. */}
                  <div className="inline-flex items-center gap-1.5 ml-1 mr-2 px-1.5 py-0.5 border border-slate-300 rounded-md bg-white">
                    <Search className="w-3 h-3 text-slate-400" />
                    <input
                      type="text"
                      value={parcelInPageFilter}
                      onChange={(e) => setParcelInPageFilter(e.target.value)}
                      placeholder="Search this ledger: tracking, country, product..."
                      className="text-[11px] w-64 outline-none bg-transparent"
                      data-testid="in-ledger-account-search"
                    />
                    {parcelInPageFilter && (
                      <button
                        onClick={() => setParcelInPageFilter("")}
                        className="text-[11px] text-rose-600 hover:underline"
                        data-testid="in-ledger-account-search-clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {(() => {
                    const allOptions = [
                      { k: 'negative_gp', label: 'Negative GP' },
                      { k: 'no_invoice', label: 'No Invoice' },
                      { k: 'no_sales', label: 'No Sales' },
                      { k: 'high_value', label: 'High Value (10K+)' },
                      { k: 'company_payments', label: 'Company Payments' },
                      { k: 'personal_collections', label: 'Personal Collections' },
                      ...(accountViewMode === 'payment' ? [{ k: 'multi_parcel', label: 'Multi-Parcel Payments' }] : []),
                      { k: 'pending_payment', label: 'Pending Payment' },
                      { k: 'dispute_adjustment', label: 'Dispute / Adjustment' },
                    ];
                    const active = new Set(accountTableFilter);
                    const toggle = (k) => {
                      setAccountTableFilter(prev => {
                        const cur = new Set(prev);
                        if (cur.has(k)) cur.delete(k);
                        else cur.add(k);
                        return Array.from(cur);
                      });
                    };
                    return (
                      <>
                        {allOptions.map(o => (
                          <button
                            key={o.k}
                            type="button"
                            onClick={() => toggle(o.k)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                              active.has(o.k)
                                ? 'bg-blue-600 text-white border-blue-700 font-semibold shadow-sm'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                            data-testid={`acct-filter-chip-${o.k}`}
                            aria-pressed={active.has(o.k)}
                          >
                            {active.has(o.k) ? '✓ ' : ''}{o.label}
                          </button>
                        ))}
                        {accountTableFilter.length > 0 && (
                          <button
                            onClick={() => setAccountTableFilter([])}
                            className="text-xs text-rose-600 hover:text-rose-800 underline flex items-center gap-0.5 ml-1"
                            data-testid="clear-table-filter"
                          >
                            <X className="w-3 h-3" />
                            Clear all ({accountTableFilter.length})
                          </button>
                        )}
                        {accountTableFilter.length > 0 && (
                          <span className="text-xs text-slate-500 ml-1">
                            Showing {accountViewMode === "parcel"
                              ? getSortedAccountViewData.ledger_entries?.length || 0
                              : getSortedAccountViewData.payment_groups?.length || 0} of {accountViewMode === "parcel"
                              ? accountViewData?.ledger_entries?.length || 0
                              : accountViewData?.payment_groups?.length || 0}
                            {accountTableFilter.length > 1 && ' · OR-combined'}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Ledger Table with Sticky Header */}
                <div className="border rounded-lg relative" style={{ contain: 'paint' }}>
                  {/* Mobile scroll hint */}
                  <div className="sm:hidden text-xs text-slate-400 text-center py-1 bg-slate-50 border-b flex items-center justify-center gap-1">
                    <ChevronLeft className="w-3 h-3" /> Swipe to see all columns <ChevronRight className="w-3 h-3" />
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm border-collapse min-w-[900px]">
                    <thead className="bg-slate-800 text-white sticky top-0 z-40 shadow-md">
                      <tr>
                        <th className="px-1.5 py-2 w-8 text-center">
                          <Checkbox 
                            checked={selectedTrackings.size > 0 && selectedTrackings.size === (accountViewMode === "parcel" 
                              ? (accountViewData?.ledger_entries?.length || 0) 
                              : (accountViewData?.payment_groups?.flatMap(g => g.parcels || [])?.length || 0))}
                            onCheckedChange={toggleSelectAll}
                            className="border-white data-[state=checked]:bg-white data-[state=checked]:text-slate-800"
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th 
                          className="px-2 sm:px-3 py-2 text-left font-semibold w-20 sm:w-24 cursor-pointer hover:bg-slate-700 transition-colors"
                          onClick={() => handleAccountViewSort('date')}
                        >
                          <div className="flex items-center gap-1">
                            Date {getAccountViewSortIcon('date')}
                          </div>
                        </th>
                        <th className="px-2 sm:px-3 py-2 text-left font-semibold">Particulars</th>
                        {accountViewMode === "parcel" && (
                          <th 
                            className="px-1.5 sm:px-2 py-2 text-center font-semibold w-16 sm:w-20 bg-slate-700 cursor-pointer hover:bg-slate-600 transition-colors"
                            onClick={() => handleAccountViewSort('payment_ref')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Ref {getAccountViewSortIcon('payment_ref')}
                            </div>
                          </th>
                        )}
                        <th 
                          className="px-2 sm:px-3 py-2 text-right font-semibold w-20 sm:w-24 bg-indigo-800 cursor-pointer hover:bg-indigo-700 transition-colors"
                          onClick={() => handleAccountViewSort('sale_amount')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Sale {getAccountViewSortIcon('sale_amount')}
                          </div>
                        </th>
                        <th 
                          className="px-2 sm:px-3 py-2 text-right font-semibold w-20 sm:w-24 bg-indigo-700 cursor-pointer hover:bg-indigo-600 transition-colors"
                          onClick={() => handleAccountViewSort('gp')}
                          title="GP = Sale - Cost"
                        >
                          <div className="flex items-center justify-end gap-1">
                            GP {getAccountViewSortIcon('gp')}
                          </div>
                        </th>
                        {/* Dynamic GP distribution columns */}
                        {accountViewData?.gp_columns?.map((col) => (
                          <th key={col.key} className="px-2 py-2 text-right font-semibold w-18 sm:w-22 bg-indigo-900 text-indigo-200 text-[10px] sm:text-xs">
                            {col.name} ({col.percentage}%)
                          </th>
                        ))}
                        <th 
                          className="px-2 sm:px-3 py-2 text-right font-semibold w-20 sm:w-24 bg-red-900 cursor-pointer hover:bg-red-800 transition-colors"
                          onClick={() => handleAccountViewSort('total_debit')}
                          title="Shipment Cost"
                        >
                          <div className="flex items-center justify-end gap-1">
                            Debit {getAccountViewSortIcon('total_debit')}
                          </div>
                        </th>
                        <th 
                          className="px-2 sm:px-3 py-2 text-right font-semibold w-20 sm:w-24 bg-green-800 cursor-pointer hover:bg-green-700 transition-colors"
                          onClick={() => handleAccountViewSort('total_credit')}
                          title="Company Received"
                        >
                          <div className="flex items-center justify-end gap-1">
                            Credit {getAccountViewSortIcon('total_credit')}
                          </div>
                        </th>
                        <th className="px-2 sm:px-3 py-2 text-right font-semibold w-20 sm:w-24 bg-orange-800" title="Partner Personal Collection">
                          Coll
                        </th>
                        <th 
                          className="px-2 sm:px-3 py-2 text-right font-semibold w-22 sm:w-28 cursor-pointer hover:bg-slate-700 transition-colors"
                          onClick={() => handleAccountViewSort('running_balance')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Balance {getAccountViewSortIcon('running_balance')}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Payment-wise View */}
                      {accountViewMode === "payment" && getSortedAccountViewData.payment_groups?.map((group, gIdx) => (
                        <React.Fragment key={group.payment_key || gIdx}>
                          {/* Payment Group Header - Now with columnar data */}
                          <tr 
                            className="bg-purple-100 border-t-4 border-purple-300 cursor-pointer hover:bg-purple-200 transition-colors"
                            onClick={() => togglePaymentGroupExpansion(group.payment_key)}
                          >
                            <td className="px-1.5 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={group.parcels?.every(p => selectedTrackings.has(p.tracking))}
                                onCheckedChange={() => {
                                  const groupTrackings = group.parcels?.map(p => p.tracking) || [];
                                  const allSelected = groupTrackings.every(t => selectedTrackings.has(t));
                                  setSelectedTrackings(prev => {
                                    const newSet = new Set(prev);
                                    groupTrackings.forEach(t => allSelected ? newSet.delete(t) : newSet.add(t));
                                    return newSet;
                                  });
                                }}
                                data-testid={`select-group-${gIdx}`}
                              />
                            </td>
                            <td className="px-3 py-2.5 text-slate-600 text-sm">{formatDMY(group.date)}</td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                {expandedPaymentGroups.has(group.payment_key) ? (
                                  <ChevronDown className="w-5 h-5 text-purple-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-purple-600" />
                                )}
                                <CreditCard className="w-4 h-4 text-purple-600" />
                                <span className="font-bold text-purple-800">
                                  PMT: {group.payment_mode || 'No Payment'}
                                </span>
                                <Badge className={`text-xs ${group.is_manual_group ? 'bg-yellow-100 text-yellow-800' : group.is_company_payment ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {group.is_manual_group ? 'M' : group.is_company_payment ? 'CO' : 'P'}
                                </Badge>
                                <Badge className="bg-purple-200 text-purple-800 text-xs">
                                  {group.parcel_count}
                                </Badge>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right text-slate-700 font-medium">
                              {group.group_summary?.total_sale > 0 ? `₹${group.group_summary.total_sale.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-slate-700 font-medium">
                              {group.group_summary?.total_gp > 0 ? `₹${group.group_summary.total_gp.toLocaleString()}` : group.group_summary?.total_gp < 0 ? <span className="text-red-600">₹{group.group_summary.total_gp.toLocaleString()}</span> : '-'}
                            </td>
                            {/* Dynamic GP distribution columns */}
                            {accountViewData?.gp_columns?.map((col) => (
                              <td key={col.key} className="px-2 py-2.5 text-right text-indigo-600 text-xs">-</td>
                            ))}
                            <td className="px-3 py-2.5 text-right font-semibold text-red-700">
                              {group.group_summary?.total_debit > 0 ? `₹${group.group_summary.total_debit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-green-700">
                              {group.group_summary?.total_credit > 0 ? `₹${group.group_summary.total_credit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-orange-600 font-medium">
                              {group.group_summary?.collection > 0 ? `₹${group.group_summary.collection.toLocaleString()}` : '-'}
                            </td>
                            <td className={`px-3 py-2.5 text-right font-bold ${
                              (group.group_summary?.closing_balance || 0) > 0 ? 'text-emerald-700' : 
                              (group.group_summary?.closing_balance || 0) < 0 ? 'text-red-700' : 'text-slate-500'
                            }`}>
                              ₹{(group.group_summary?.closing_balance || group.payment_entry?.balance || 0).toLocaleString()}
                            </td>
                          </tr>
                          
                          {/* Parcels in this payment group - show when expanded */}
                          {expandedPaymentGroups.has(group.payment_key) && (
                            <>
                              {group.parcels?.map((parcel, pIdx) => (
                                <React.Fragment key={`${group.payment_key}-${parcel.tracking || pIdx}`}>
                                  {/* Parcel Header within payment group - Now with columnar data */}
                                  <tr 
                                    className="bg-blue-50/70 border-t border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleParcelExpansion(`${group.payment_key}-${parcel.tracking}`);
                                    }}
                                  >
                                    <td className="px-1.5 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-center gap-0.5">
                                        <Checkbox
                                          checked={selectedTrackings.has(parcel.tracking)}
                                          onCheckedChange={() => toggleTrackingSelect(parcel.tracking)}
                                          className="h-3.5 w-3.5"
                                          data-testid={`select-payment-parcel-${parcel.tracking}`}
                                        />
                                        <button
                                          onClick={(e) => { e.stopPropagation(); openEditDialog(parcel); }}
                                          className="p-0.5 rounded hover:bg-blue-200 text-slate-400 hover:text-blue-700 transition-colors"
                                          title="Edit entry"
                                          data-testid={`edit-payment-parcel-${parcel.tracking}`}
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-slate-500 text-xs">{formatDMY(parcel.date)}</td>
                                    <td className="px-3 py-1.5 pl-8">
                                      <div className="flex items-center gap-2">
                                        {expandedParcels.has(`${group.payment_key}-${parcel.tracking}`) ? (
                                          <ChevronDown className="w-3 h-3 text-blue-500" />
                                        ) : (
                                          <ChevronRight className="w-3 h-3 text-blue-500" />
                                        )}
                                        <span className={`font-semibold text-xs ${parcel.is_manual_entry ? 'text-emerald-700' : 'text-blue-700'}`}>
                                          {parcel.is_manual_entry ? '' : '** '}{parcel.tracking}
                                        </span>
                                        {parcel.is_manual_entry && (
                                          <Badge className={`text-[10px] ${parcel.manual_entry_type === 'take' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200'}`}>
                                            {parcel.manual_entry_type === 'take' ? 'DR' : 'CR'}
                                          </Badge>
                                        )}
                                        {parcel.has_no_invoice && !parcel.is_manual_entry && (
                                          <Badge className="bg-orange-100 text-orange-700 text-[10px]">[NI]</Badge>
                                        )}
                                        {parcel.has_negative_gp && !parcel.is_manual_entry && (
                                          <Badge className="bg-red-100 text-red-700 text-[10px]">[NG]</Badge>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-slate-600 text-xs font-medium">
                                      {parcel.sale_amount ? `₹${parcel.sale_amount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className={`px-3 py-1.5 text-right text-xs font-medium ${parcel.gp < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                      {parcel.gp ? `₹${parcel.gp.toLocaleString()}` : '-'}
                                    </td>
                                    {/* Dynamic GP columns */}
                                    {accountViewData?.gp_columns?.map((col) => (
                                      <td key={col.key} className="px-2 py-1.5 text-right text-indigo-500 text-[10px]">
                                        {parcel[col.key] ? `₹${parcel[col.key].toLocaleString()}` : '-'}
                                      </td>
                                    ))}
                                    <td className="px-3 py-1.5 text-right font-medium text-red-600 text-xs">
                                      {parcel.parcel_summary?.total_debit > 0 ? `₹${parcel.parcel_summary.total_debit.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-xs text-slate-400">-</td>
                                    <td className="px-3 py-1.5 text-right text-orange-600 text-xs">
                                      {parcel.parcel_summary?.collection > 0 ? `₹${parcel.parcel_summary.collection.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-xs text-slate-500">
                                      ₹{(parcel.running_balance || parcel.entries?.[parcel.entries.length - 1]?.balance || 0).toLocaleString()}
                                    </td>
                                  </tr>
                                  
                                  {/* Parcel entries - show when parcel expanded */}
                                  {expandedParcels.has(`${group.payment_key}-${parcel.tracking}`) && parcel.entries?.map((entry, eIdx) => (
                                    <tr 
                                      key={`${group.payment_key}-${parcel.tracking}-${eIdx}`}
                                      className={`border-b border-slate-100 ${
                                        entry.type === 'debit' ? 'bg-red-50/30' : 
                                        entry.type === 'credit' ? 'bg-green-50/30' : 
                                        'bg-slate-50/30'
                                      }`}
                                    >
                                      <td className="px-1.5 py-1"></td>{/* Empty checkbox cell */}
                                      <td className="px-3 py-1 text-slate-500 text-xs">{formatDMY(parcel.date)}</td>
                                      <td className="px-3 py-1 pl-12 text-xs">{entry.description}</td>
                                      <td className="px-3 py-1 text-right text-slate-400 text-xs">
                                        {entry.category === 'sale' ? `₹${entry.sale_amount?.toLocaleString()}` : '-'}
                                      </td>
                                      <td className="px-3 py-1 text-right text-slate-400 text-xs">-</td>
                                      {/* Dynamic GP columns for entries */}
                                      {accountViewData?.gp_columns?.map((col) => (
                                        <td key={col.key} className="px-2 py-1 text-right text-slate-400 text-xs">-</td>
                                      ))}
                                      <td className="px-3 py-1 text-right font-medium text-red-600 text-xs">
                                        {entry.debit > 0 ? `₹${entry.debit?.toLocaleString()}` : '-'}
                                      </td>
                                      <td className="px-3 py-1 text-right font-medium text-green-600 text-xs">
                                        {entry.credit > 0 ? `₹${entry.credit?.toLocaleString()}` : '-'}
                                      </td>
                                      <td className="px-3 py-1 text-right text-orange-500 text-xs">
                                        {entry.collection > 0 ? `₹${entry.collection?.toLocaleString()}` : '-'}
                                      </td>
                                      <td className="px-3 py-1 text-right text-xs text-slate-500">
                                        ₹{entry.balance?.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                              
                              {/* Payment Entry Row - Credit shown ONCE at end of group */}
                              {group.payment_entry && (
                                <tr className={`border-t-2 ${group.is_company_payment ? 'bg-green-100 border-green-300' : 'bg-orange-50 border-orange-200'}`}>
                                  <td className="px-1.5 py-2"></td>{/* Empty checkbox cell */}
                                  <td className="px-3 py-2 text-slate-600 font-medium">{group.parcels?.[0]?.date}</td>
                                  <td className="px-3 py-2 pl-8 font-semibold">
                                    <div className="flex items-center gap-2">
                                      <CreditCard className="w-4 h-4" />
                                      {group.payment_entry.description}
                                      <span className="text-xs text-slate-500">({group.parcel_count} parcels)</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right">-</td>
                                  <td className="px-3 py-2 text-right">-</td>
                                  {/* Dynamic GP columns */}
                                  {accountViewData?.gp_columns?.map((col) => (
                                    <td key={col.key} className="px-2 py-2 text-right">-</td>
                                  ))}
                                  <td className="px-3 py-2 text-right">-</td>
                                  <td className={`px-3 py-2 text-right font-bold ${group.is_company_payment ? 'text-green-700' : 'text-slate-400'}`}>
                                    {group.is_company_payment ? `₹${group.payment_entry.credit?.toLocaleString()}` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-right text-orange-600 font-medium">
                                    {group.payment_entry.collection > 0 ? `₹${group.payment_entry.collection?.toLocaleString()}` : '-'}
                                  </td>
                                  <td className={`px-3 py-2 text-right font-bold ${
                                    group.payment_entry.balance > 0 ? 'text-emerald-700' : 
                                    group.payment_entry.balance < 0 ? 'text-red-700' : 
                                    'text-slate-600'
                                  }`}>
                                    ₹{group.payment_entry.balance?.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      
                      {/* Parcel-wise View (Original) */}
                      {accountViewMode === "parcel" && (() => {
                        const q = (parcelInPageFilter || "").trim().toLowerCase();
                        const all = getSortedAccountViewData.ledger_entries || [];
                        const filtered = q
                          ? all.filter(p => [p.tracking, p.service, p.country, p.product, p.customer_name, p.client_code]
                              .some(v => (v || "").toString().toLowerCase().includes(q)))
                          : all;
                        if (q && filtered.length === 0) {
                          return (
                            <tr><td colSpan={20} className="px-4 py-6 text-center text-sm text-slate-500" data-testid="in-ledger-no-match">
                              No parcel matches <b className="font-mono text-slate-700">{parcelInPageFilter}</b> in this ledger ({all.length} total). Try the global search above for an app-wide lookup.
                            </td></tr>
                          );
                        }
                        return filtered.map((parcel, idx) => {
                        const advColor = parcel.is_advance_utilization ? getAdvanceColor(parcel.advance_ref) : null;
                        return (
                        <React.Fragment key={parcel.tracking || idx}>
                          {/* Parcel Header Row - Now with columnar data and Ref */}
                          <tr 
                            className={`border-t-2 cursor-pointer hover:bg-blue-100 transition-colors ${
                              parcel.is_manual_entry
                                ? 'bg-emerald-50 border-emerald-300'
                                : advColor
                                ? advColor.row
                                : parcel.payment_parcel_count > 1 && !parcel.is_last_in_payment_group 
                                ? 'bg-amber-50 border-amber-200' 
                                : 'bg-blue-50 border-blue-200'
                            }`}
                            onClick={() => toggleParcelExpansion(parcel.tracking)}
                          >
                            <td className="px-1.5 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <Checkbox
                                  checked={selectedTrackings.has(parcel.tracking)}
                                  onCheckedChange={() => toggleTrackingSelect(parcel.tracking)}
                                  data-testid={`select-parcel-${parcel.tracking}`}
                                />
                                <button
                                  onClick={(e) => { e.stopPropagation(); openEditDialog(parcel); }}
                                  className="p-0.5 rounded hover:bg-blue-200 text-slate-400 hover:text-blue-700 transition-colors"
                                  title="Edit entry"
                                  data-testid={`edit-parcel-${parcel.tracking}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-slate-600 text-sm">{formatDMY(parcel.date)}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {expandedParcels.has(parcel.tracking) ? (
                                  <ChevronDown className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-blue-600" />
                                )}
                                {parcel.is_manual_entry ? (
                                  <>
                                    <Badge className={`text-xs ${parcel.manual_entry_type === 'take' ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-green-100 text-green-700 border border-green-300'}`}>
                                      {parcel.manual_entry_type === 'take' ? 'MANUAL DEBIT' : 'MANUAL CREDIT'}
                                    </Badge>
                                    <span className="font-semibold text-slate-700">{parcel.tracking}</span>
                                  </>
                                ) : (
                                  <span className="font-bold text-blue-700">** PARCEL: {parcel.tracking}</span>
                                )}
                                {parcel.has_no_invoice && !parcel.is_manual_entry && (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">[NO INV]</Badge>
                                )}
                                {parcel.has_negative_gp && !parcel.is_manual_entry && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">[NEG GP]</Badge>
                                )}
                                {parcel.is_advance_utilization && parcel.advance_ref && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMainView("advances");
                                      setAdvanceSearchTerm(parcel.advance_ref);
                                      setAdvanceStatusFilter("all");
                                      setAdvanceHideFullyUsed(false);
                                    }}
                                    className={`inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 ml-1 rounded border hover:brightness-95 hover:underline transition-all cursor-pointer ${advColor ? advColor.chip : 'bg-blue-100 text-blue-700 border-blue-300'}`}
                                    title={`Click to jump to Advances tab → filtered for ${parcel.advance_ref} (total ₹${(parcel.advance_total_amount || 0).toLocaleString()}, parcel ${parcel.advance_parcel_index || 1} of ${parcel.payment_parcel_count || 1})`}
                                    data-testid={`adv-chip-${parcel.tracking}`}
                                  >
                                    ⮑ ADV {parcel.advance_ref}
                                    {parcel.advance_total_amount > 0 && (
                                      <span className="ml-1 font-semibold">· ₹{parcel.advance_total_amount.toLocaleString()}</span>
                                    )}
                                  </button>
                                )}
                                {parcel.customer && (
                                  <span className="text-slate-500 text-xs ml-2">({parcel.customer})</span>
                                )}
                              </div>
                            </td>
                            {/* Ref Column - Payment Reference */}
                            <td className="px-2 py-2 text-center">
                              {parcel.payment_ref ? (
                                <Badge className={`text-[10px] ${
                                  parcel.payment_parcel_count > 1 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {parcel.payment_ref}
                                  {parcel.payment_parcel_count > 1 && (
                                    <span className="ml-1">({parcel.payment_parcel_count})</span>
                                  )}
                                </Badge>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700 font-medium">
                              {parcel.sale_amount ? `₹${parcel.sale_amount.toLocaleString()}` : '-'}
                            </td>
                            <td className={`px-3 py-2 text-right font-medium ${parcel.gp < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                              {parcel.gp ? `₹${parcel.gp.toLocaleString()}` : '-'}
                            </td>
                            {/* Dynamic GP distribution columns */}
                            {accountViewData?.gp_columns?.map((col) => (
                              <td key={col.key} className="px-2 py-2 text-right text-indigo-600 text-xs">
                                {parcel[col.key] ? `₹${parcel[col.key].toLocaleString()}` : '-'}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right font-semibold text-red-700">
                              {parcel.parcel_summary?.total_debit > 0 ? `₹${parcel.parcel_summary.total_debit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-green-700">
                              {parcel.parcel_summary?.total_credit > 0 ? `₹${parcel.parcel_summary.total_credit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right text-orange-600 font-medium">
                              {parcel.parcel_summary?.collection > 0 ? `₹${parcel.parcel_summary.collection.toLocaleString()}` : '-'}
                            </td>
                            <td className={`px-3 py-2 text-right font-bold ${
                              parcel.running_balance > 0 ? 'text-emerald-700' : 
                              parcel.running_balance < 0 ? 'text-red-700' : 'text-slate-500'
                            }`}>
                              ₹{(parcel.running_balance || parcel.entries?.[parcel.entries.length - 1]?.balance || 0).toLocaleString()}
                            </td>
                          </tr>
                          {/* Entry Rows - Show when expanded */}
                          {expandedParcels.has(parcel.tracking) && parcel.entries?.map((entry, entryIdx) => (
                            <tr 
                              key={`${parcel.tracking}-${entryIdx}`}
                              className={`border-b border-slate-100 ${
                                entry.type === 'debit' ? 'bg-red-50/30' : 
                                entry.type === 'credit' ? 'bg-green-50/30' : 
                                'bg-slate-50/50'
                              }`}
                            >
                              <td className="px-1.5 py-1.5"></td>{/* Empty checkbox cell for sub-entries */}
                              <td className="px-3 py-1.5 text-slate-600">{formatDMY(parcel.date)}</td>
                              <td className="px-3 py-1.5 pl-8">{entry.description}</td>
                              <td className="px-2 py-1.5"></td>{/* Empty Ref column for sub-entries */}
                              <td className="px-3 py-1.5 text-right text-slate-500">
                                {entry.category === 'sale' ? `₹${entry.sale_amount?.toLocaleString()}` : '-'}
                              </td>
                              <td className="px-3 py-1.5 text-right text-slate-500">-</td>
                              {/* Dynamic GP columns for entries */}
                              {accountViewData?.gp_columns?.map((col) => (
                                <td key={col.key} className="px-2 py-1.5 text-right text-slate-400 text-xs">-</td>
                              ))}
                              <td className="px-3 py-1.5 text-right font-medium text-red-700">
                                {entry.debit > 0 ? `₹${entry.debit?.toLocaleString()}` : '-'}
                              </td>
                              <td className="px-3 py-1.5 text-right font-medium text-green-700">
                                {entry.credit > 0 ? `₹${entry.credit?.toLocaleString()}` : '-'}
                              </td>
                              <td className="px-3 py-1.5 text-right text-orange-500">
                                {entry.collection > 0 ? `₹${entry.collection?.toLocaleString()}` : '-'}
                              </td>
                              <td className={`px-3 py-1.5 text-right font-semibold ${
                                entry.balance > 0 ? 'text-emerald-700' : 
                                entry.balance < 0 ? 'text-red-700' : 
                                'text-slate-500'
                              }`}>
                                ₹{entry.balance?.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                        );
                      });
                      })()}
                      
                      {/* Totals Row */}
                      <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                        <td></td>{/* checkbox col */}
                        <td colSpan={accountViewMode === "parcel" ? 3 : 2} className="px-3 py-2.5 text-right">
                          TOTALS
                          <span className="ml-1 text-xs font-normal text-slate-500">
                            ({accountViewMode === 'parcel'
                              ? `${accountViewData?.parcel_count || 0} parcels${accountViewData?.manual_entry_count ? ` + ${accountViewData.manual_entry_count} manual` : ''}`
                              : (() => {
                                  const groups = getSortedAccountViewData.payment_groups || [];
                                  const manualCount = groups.filter(g => g.is_manual_group).length;
                                  const paymentCount = groups.length - manualCount;
                                  return manualCount > 0
                                    ? `${paymentCount} payments + ${manualCount} manual`
                                    : `${paymentCount} payments`;
                                })()
                            })
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-indigo-800">
                          ₹{accountViewData.totals?.total_sale?.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right text-indigo-800">
                          ₹{accountViewData.totals?.total_gp?.toLocaleString()}
                        </td>
                        {/* Dynamic GP totals */}
                        {accountViewData?.gp_columns?.map((col) => (
                          <td key={col.key} className="px-2 py-2.5 text-right text-indigo-600 text-xs font-semibold">
                            {accountViewData.totals?.[`total_${col.key}`] ? `₹${accountViewData.totals[`total_${col.key}`].toLocaleString()}` : '-'}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right text-red-800">
                          ₹{accountViewData.totals?.total_debit?.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right text-green-800">
                          ₹{accountViewData.totals?.total_credit?.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right text-orange-700">
                          ₹{accountViewData.totals?.total_collection?.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right"></td>
                      </tr>
                      {/* Closing Balance Row */}
                      <tr className="bg-emerald-100 font-bold text-lg">
                        <td colSpan={(accountViewMode === "parcel" ? 3 : 2) + 2 + (accountViewData?.gp_columns?.length || 0) + 3} className="px-3 py-3 text-right text-emerald-800">CLOSING BALANCE</td>
                        <td className={`px-3 py-3 text-right ${
                          accountViewData.closing_balance > 0 ? 'text-emerald-800' : 
                          accountViewData.closing_balance < 0 ? 'text-red-800' : 
                          'text-slate-800'
                        }`}>
                          ₹{accountViewData.closing_balance?.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Balance Explanation */}
                <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-slate-500 mt-2">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-emerald-500 flex-shrink-0"></div>
                    <span>Positive = Partner owes you</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-500 flex-shrink-0"></div>
                    <span>Negative = You owe partner</span>
                  </div>
                </div>

                {/* Floating Delete Bar */}
                {selectedTrackings.size > 0 && (
                  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white rounded-xl shadow-2xl px-6 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4" data-testid="delete-bar">
                    <Checkbox 
                      checked={true} 
                      onCheckedChange={() => setSelectedTrackings(new Set())}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-red-600"
                    />
                    <span className="font-semibold text-sm">{selectedTrackings.size} selected</span>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-white text-red-600 hover:bg-red-50 px-4 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
                      data-testid="delete-selected-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedTrackings(new Set())}
                      className="text-white/80 hover:text-white text-xs underline"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        Confirm Delete
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        You are about to <strong className="text-red-600">permanently delete {selectedTrackings.size} tracking entries</strong> from all collections (uploads, sales, and orders).
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <p className="text-xs font-semibold text-red-700 mb-1">Entries to delete:</p>
                        {Array.from(selectedTrackings).map(t => (
                          <div key={t} className="text-xs text-red-600 font-mono">{t}</div>
                        ))}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">Reason for deletion <span className="text-red-500">*</span></label>
                        <textarea
                          value={deleteReason}
                          onChange={(e) => setDeleteReason(e.target.value)}
                          placeholder="e.g. Duplicate entry, Wrong tracking, Data correction..."
                          rows={2}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                          data-testid="delete-reason-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">Enter Admin Password to confirm</label>
                        <input
                          type="password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          placeholder="Admin password"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          data-testid="delete-password-input"
                          onKeyDown={(e) => e.key === 'Enter' && deletePassword && deleteReason.trim() && handleDeleteEntries()}
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteReason(''); }}
                          className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteEntries}
                          disabled={!deletePassword || !deleteReason.trim() || deleteLoading}
                          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold transition-colors"
                          data-testid="confirm-delete-btn"
                        >
                          {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          {deleteLoading ? 'Deleting...' : `Delete ${selectedTrackings.size} Entries`}
                        </button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* 3rd Partner Referrals Section */}
                {accountViewData.third_partner_referrals && accountViewData.third_partner_referrals.length > 0 && (
                  <div className="mt-4 p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Referral Credits to {accountViewData.profit_sharing?.third_name}
                    </h4>
                    <p className="text-xs text-purple-600 mb-3">
                      These amounts should be credited to {accountViewData.profit_sharing?.third_name}&apos;s ledger
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {accountViewData.third_partner_referrals.map((ref, idx) => (
                        <div key={idx} className="bg-white rounded px-3 py-2 border border-purple-100 text-sm">
                          <span className="font-mono text-purple-600">{ref.tracking}</span>
                          <span className="ml-2 font-semibold text-purple-800">₹{ref.amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-right">
                      <span className="text-sm font-bold text-purple-800">
                        Total: ₹{accountViewData.third_partner_referrals.reduce((sum, r) => sum + r.amount, 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Restore Deleted Entries Dialog - Outside conditional for accessibility */}
      <Dialog open={showRestoreDialog} onOpenChange={(open) => { setShowRestoreDialog(open); if (!open) setRestorePassword(''); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="w-5 h-5" />
              Recently Deleted Entries
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Entries can be restored within 7 days of deletion.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {deletedEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <RotateCcw className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="font-medium">No deleted entries</p>
                <p className="text-xs mt-1">Deleted entries appear here for 7 days</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Admin Password (required to restore)</label>
                  <input
                    type="password"
                    value={restorePassword}
                    onChange={(e) => setRestorePassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    data-testid="restore-password-input"
                  />
                </div>
                {deletedEntries.map((entry) => {
                  const deletedAt = new Date(entry.timestamp);
                  const expiresAt = entry.expires_at ? new Date(entry.expires_at) : new Date(deletedAt.getTime() + 7 * 24 * 60 * 60 * 1000);
                  const now = new Date();
                  const hoursLeft = Math.max(0, Math.round((expiresAt - now) / (1000 * 60 * 60)));
                  const daysLeft = Math.floor(hoursLeft / 24);
                  const timeLabel = daysLeft > 0 ? `${daysLeft}d ${hoursLeft % 24}h left` : `${hoursLeft}h left`;
                  
                  return (
                    <div key={entry.id} className="border border-slate-200 rounded-lg p-3 bg-white hover:border-amber-300 transition-colors" data-testid={`deleted-entry-${entry.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {deletedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            by {entry.performed_by || 'Unknown'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${hoursLeft < 24 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {timeLabel}
                        </span>
                      </div>
                      {entry.delete_reason && entry.delete_reason !== 'No reason provided' && (
                        <p className="text-xs text-rose-600 mb-2 italic px-0.5" data-testid={`delete-reason-${entry.id}`}>
                          Reason: {entry.delete_reason}
                        </p>
                      )}
                      <div className="bg-slate-50 rounded p-2 mb-2 max-h-24 overflow-y-auto">
                        <p className="text-xs font-semibold text-slate-600 mb-1">
                          {entry.tracking_numbers?.length || 0} tracking(s):
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(entry.tracking_numbers || []).slice(0, 10).map(t => (
                            <span key={t} className="text-xs font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200 text-slate-600">{t}</span>
                          ))}
                          {(entry.tracking_numbers || []).length > 10 && (
                            <span className="text-xs text-slate-400">+{entry.tracking_numbers.length - 10} more</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-3 text-xs text-slate-500">
                          {entry.deleted_counts?.shipment_orders > 0 && <span>{entry.deleted_counts.shipment_orders} orders</span>}
                          {entry.deleted_counts?.sales > 0 && <span>{entry.deleted_counts.sales} sales</span>}
                          {entry.deleted_counts?.partner_ledger_uploads > 0 && <span>{entry.deleted_counts.partner_ledger_uploads} uploads</span>}
                        </div>
                        <button
                          onClick={() => handleRestoreEntries(entry.id)}
                          disabled={!restorePassword || restoreLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          data-testid={`restore-btn-${entry.id}`}
                        >
                          {restoreLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Restore
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setEditEntry(null); setEditPassword(''); setEditReason(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pencil className="w-5 h-5" />
              Edit Ledger Entry
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {editEntry?.tracking} — {editEntry?.customer_name || 'Unknown'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Entry Date</label>
              <input value={editForm.entry_date}
                onChange={(e) => setEditForm(f => ({ ...f, entry_date: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="edit-entry-date"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Sale Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.sale_amount}
                  onChange={(e) => setEditForm(f => ({ ...f, sale_amount: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-sale-amount"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Cost / Debit</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.cost_amount}
                  onChange={(e) => setEditForm(f => ({ ...f, cost_amount: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-cost-amount"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.payment_amount}
                  onChange={(e) => setEditForm(f => ({ ...f, payment_amount: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-payment-amount"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Payment Mode</label>
                <input
                  type="text"
                  value={editForm.payment_mode}
                  onChange={(e) => setEditForm(f => ({ ...f, payment_mode: e.target.value }))}
                  placeholder="ESR, SELF, etc."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-payment-mode"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Reason for edit <span className="text-red-500">*</span></label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="e.g. Correcting sale amount, Wrong cost..."
                rows={2}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                data-testid="edit-reason-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Admin Password <span className="text-red-500">*</span></label>
              <input
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && editPassword && editReason.trim() && handleEditLedgerParcel()}
                data-testid="edit-password-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowEditDialog(false); setEditEntry(null); setEditPassword(''); setEditReason(''); }}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleEditLedgerParcel}
              disabled={!editPassword || !editReason.trim() || editLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="edit-confirm-btn"
            >
              {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Report Dialog - Outside conditional for accessibility */}
      <Dialog open={showAccountReportDialog} onOpenChange={setShowAccountReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Generate Account Reports
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Select reports to generate for {accountViewPartner} ({accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} view)
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Report Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {accountReportTypes.map(report => {
                const count = getAccountReportCount(report.id);
                const isSelected = selectedAccountReports.includes(report.id);
                return (
                  <div
                    key={report.id}
                    onClick={() => toggleAccountReportSelection(report.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{report.name}</span>
                      <Badge className={`bg-${report.color}-100 text-${report.color}-700 text-xs`}>
                        {count}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">{report.description}</p>
                  </div>
                );
              })}
            </div>
            
            {/* Generate Button */}
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-sm text-slate-500">
                {selectedAccountReports.length} report(s) selected
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedAccountReports([])}
                >
                  Clear All
                </Button>
                <Button 
                  onClick={generateAccountReports}
                  disabled={selectedAccountReports.length === 0 || accountReportLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {accountReportLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="w-4 h-4 mr-2" />
                  )}
                  Generate Reports
                </Button>
              </div>
            </div>
            
            {/* Report Results */}
            {accountReportData && Object.keys(accountReportData).length > 0 && (
              <div className="mt-4 space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Generated Reports
                </h3>
                
                {/* Outstanding Balance */}
                {accountReportData.outstanding_balance && (
                  <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border">
                    <h4 className="font-semibold text-slate-800 mb-2">Outstanding Balance Report</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Total Debit</p>
                        <p className="text-lg font-bold text-red-600">₹{accountReportData.outstanding_balance.total_debit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Total Credit</p>
                        <p className="text-lg font-bold text-green-600">₹{accountReportData.outstanding_balance.total_credit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Net Balance</p>
                        <p className={`text-lg font-bold ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.outstanding_balance.balance?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Status</p>
                        <p className={`text-sm font-semibold ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {accountReportData.outstanding_balance.status}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Profit Summary */}
                {accountReportData.profit_summary && (
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">Profit Share Summary</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Self Share ({accountViewData?.profit_sharing?.self_percentage}%)</p>
                        <p className="text-lg font-bold text-emerald-600">₹{accountReportData.profit_summary.self_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Partner Share ({accountViewData?.profit_sharing?.partner_percentage}%)</p>
                        <p className="text-lg font-bold text-blue-600">₹{accountReportData.profit_summary.partner_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Total GP</p>
                        <p className="text-lg font-bold text-slate-800">₹{accountReportData.profit_summary.total_gp?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Company Payments */}
                {accountReportData.company_payments && (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center justify-between">
                      <span>Company Payments Report</span>
                      <Badge className="bg-green-100 text-green-700">{accountReportData.company_payments.count} payments</Badge>
                    </h4>
                    {accountReportData.company_payments.total && (
                      <p className="text-sm text-green-700 mb-2">Total: ₹{accountReportData.company_payments.total?.toLocaleString()}</p>
                    )}
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.company_payments.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-green-700">{item.payment_mode || item.tracking}</span>
                          <span className="font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</span>
                        </div>
                      ))}
                      {accountReportData.company_payments.items?.length > 10 && (
                        <p className="text-xs text-center text-slate-400">+{accountReportData.company_payments.items.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Personal Collections */}
                {accountReportData.personal_collections && (
                  <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center justify-between">
                      <span>Personal Collections Report (Neutral)</span>
                      <Badge className="bg-yellow-100 text-yellow-700">{accountReportData.personal_collections.count} collections</Badge>
                    </h4>
                    {accountReportData.personal_collections.total && (
                      <p className="text-sm text-yellow-700 mb-2">Total: ₹{accountReportData.personal_collections.total?.toLocaleString()} (No balance effect)</p>
                    )}
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.personal_collections.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-yellow-700">{item.payment_mode || item.tracking}</span>
                          <span className="font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Multi-Parcel Payments */}
                {accountReportData.multi_parcel && (
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center justify-between">
                      <span>Multi-Parcel Payments</span>
                      <Badge className="bg-purple-100 text-purple-700">{accountReportData.multi_parcel.count} payments</Badge>
                    </h4>
                    {accountReportData.multi_parcel.note && (
                      <p className="text-sm text-purple-600 italic">{accountReportData.multi_parcel.note}</p>
                    )}
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.multi_parcel.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="text-purple-700">{item.payment_mode} ({item.parcel_count} parcels)</span>
                          <span className="font-semibold">₹{item.payment_amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Negative GP */}
                {accountReportData.negative_gp && accountReportData.negative_gp.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center justify-between">
                      <span>Negative GP Report (Loss)</span>
                      <Badge className="bg-red-100 text-red-700">{accountReportData.negative_gp.count} parcels</Badge>
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.negative_gp.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-red-700">{item.tracking}</span>
                          <span className="font-semibold text-red-600">GP: ₹{item.gp?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* No Invoice */}
                {accountReportData.no_invoice && accountReportData.no_invoice.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2 flex items-center justify-between">
                      <span>No Invoice Report</span>
                      <Badge className="bg-orange-100 text-orange-700">{accountReportData.no_invoice.count} parcels</Badge>
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.no_invoice.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-orange-700">{item.tracking}</span>
                          <span className="text-slate-500">{formatDMY(item.date)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pending Payment */}
                {accountReportData.pending_payment && accountReportData.pending_payment.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-300">
                    <h4 className="font-semibold text-slate-800 mb-2 flex items-center justify-between">
                      <span>Pending Payment Report</span>
                      <Badge className="bg-slate-200 text-slate-700">{accountReportData.pending_payment.count} items</Badge>
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.pending_payment.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-slate-700">{item.tracking || item.payment_mode || 'No Payment'}</span>
                          <span className="text-slate-500">{item.parcel_count || 1} parcel(s)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Dormant Account Report (Inline) */}
                {accountReportData.dormant_account && (
                  <div className={`p-4 rounded-lg border ${
                    accountReportData.dormant_account.is_dormant 
                      ? 'bg-gradient-to-r from-slate-100 to-gray-100 border-slate-400' 
                      : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                  }`}>
                    <h4 className={`font-semibold mb-2 flex items-center justify-between ${
                      accountReportData.dormant_account.is_dormant ? 'text-slate-700' : 'text-green-800'
                    }`}>
                      <span>💤 Dormant Account Report</span>
                      <Badge className={`${
                        accountReportData.dormant_account.is_dormant 
                          ? 'bg-slate-300 text-slate-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {accountReportData.dormant_account.is_dormant ? 'Dormant' : 'Active'}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-white px-3 py-2 rounded">
                        <span className="text-slate-500">Days Since Last Txn:</span>
                        <span className={`ml-2 font-semibold ${
                          accountReportData.dormant_account.days_since_last_transaction > 30 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {accountReportData.dormant_account.days_since_last_transaction ?? 'N/A'}
                        </span>
                      </div>
                      <div className="bg-white px-3 py-2 rounded">
                        <span className="text-slate-500">Last Txn:</span>
                        <span className="ml-2 font-semibold text-slate-700">
                          {accountReportData.dormant_account.last_transaction_date}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Dispute/Adjustment Report (Inline) */}
                {accountReportData.dispute_adjustment && accountReportData.dispute_adjustment.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-rose-50 to-red-50 rounded-lg border border-rose-300">
                    <h4 className="font-semibold text-rose-800 mb-2 flex items-center justify-between">
                      <span>⚖️ Dispute/Adjustment Report</span>
                      <Badge className="bg-rose-100 text-rose-700">{accountReportData.dispute_adjustment.count} items</Badge>
                    </h4>
                    <p className="text-xs text-rose-600 mb-2">Total Adjustment: ₹{accountReportData.dispute_adjustment.total_adjustment?.toLocaleString()}</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {accountReportData.dispute_adjustment.items?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-rose-700">{item.tracking}</span>
                          <span className={`font-semibold ${item.adjustment_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{item.adjustment_amount?.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Settlement Pending Report (Inline) */}
                {accountReportData.settlement_pending && accountReportData.settlement_pending.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-300">
                    <h4 className="font-semibold text-indigo-800 mb-2 flex items-center justify-between">
                      <span>🤝 Settlement Pending</span>
                      <Badge className="bg-indigo-100 text-indigo-700">{accountReportData.settlement_pending.count} items</Badge>
                    </h4>
                    <p className="text-xs text-indigo-600 mb-2">
                      Pending with {accountReportData.settlement_pending.third_party_name}: ₹{accountReportData.settlement_pending.total_pending?.toLocaleString()}
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {accountReportData.settlement_pending.items?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-indigo-700">{item.tracking}</span>
                          <span className="font-semibold text-indigo-800">₹{(item.third_party_share || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Month-End Summary (Inline) */}
                {accountReportData.month_end_summary && (
                  <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-300">
                    <h4 className="font-semibold text-cyan-800 mb-2 flex items-center justify-between">
                      <span>📅 Month-End Summary</span>
                      <Badge className="bg-cyan-100 text-cyan-700">
                        {accountReportData.month_end_summary.current_month}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-white px-3 py-2 rounded text-center">
                        <p className="text-xs text-slate-500">Debits</p>
                        <p className="font-semibold text-red-600">₹{accountReportData.month_end_summary.monthly_debits?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white px-3 py-2 rounded text-center">
                        <p className="text-xs text-slate-500">Credits</p>
                        <p className="font-semibold text-green-600">₹{accountReportData.month_end_summary.monthly_credits?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white px-3 py-2 rounded text-center">
                        <p className="text-xs text-slate-500">Closing</p>
                        <p className={`font-semibold ${accountReportData.month_end_summary.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.month_end_summary.closing_balance?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* High Value Transactions */}
                {accountReportData.high_value && accountReportData.high_value.count > 0 && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center justify-between">
                      <span>High Value Transactions (≥₹10K)</span>
                      <Badge className="bg-blue-100 text-blue-700">{accountReportData.high_value.count} transactions</Badge>
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {accountReportData.high_value.items?.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-1.5 rounded">
                          <span className="font-mono text-blue-700">{item.tracking || item.payment_mode}</span>
                          <span className="font-semibold text-blue-800">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Debit/Credit Entry Modal (Account View) */}
      <Dialog open={showAccountViewManualEntry} onOpenChange={setShowAccountViewManualEntry}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {accountViewManualEntryType === 'debit' ? (
                <>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Minus className="w-5 h-5 text-red-600" />
                  </div>
                  <span>Add Debit Entry</span>
                </>
              ) : (
                <>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Plus className="w-5 h-5 text-green-600" />
                  </div>
                  <span>Add Credit Entry</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                Partner: <span className="font-semibold text-slate-800">{accountViewPartner}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-amount">Amount (₹) *</Label>
              <Input
                id="entry-amount"
                type="number"
                placeholder="Enter amount"
                value={accountViewManualEntryForm.amount}
                onChange={(e) => setAccountViewManualEntryForm(prev => ({ ...prev, amount: e.target.value }))}
                className="text-lg font-semibold"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-date">Date *</Label>
              <DMYDateInput
  id="entry-date" value={accountViewManualEntryForm.date}
                onChange={(e) => setAccountViewManualEntryForm(prev => ({ ...prev, date: e.target.value }))}
              />
              <p className="text-xs text-slate-500">Select the date for this entry (can be past date)</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-category">Category</Label>
              <Select 
                value={accountViewManualEntryForm.category} 
                onValueChange={(value) => setAccountViewManualEntryForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                  <SelectItem value="advance">Advance Payment</SelectItem>
                  <SelectItem value="correction">Correction</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-description">Description / Particulars *</Label>
              <Input
                id="entry-description"
                placeholder="Enter description for this entry"
                value={accountViewManualEntryForm.description}
                onChange={(e) => setAccountViewManualEntryForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowAccountViewManualEntry(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccountViewManualEntrySubmit}
              disabled={accountViewManualEntryLoading}
              className={accountViewManualEntryType === 'debit' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {accountViewManualEntryLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : accountViewManualEntryType === 'debit' ? (
                <Minus className="w-4 h-4 mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add {accountViewManualEntryType === 'debit' ? 'Debit' : 'Credit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen Report View */}
      <Dialog open={showFullScreenReport} onOpenChange={setShowFullScreenReport}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full overflow-hidden flex flex-col">
          {/* Sticky Action Bar */}
          <div className="flex-shrink-0 bg-white border-b border-slate-200 p-4 print:hidden">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                  Account Reports - {accountViewPartner}
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} View | 
                  Generated: {new Date().toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  onClick={printReports}
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button 
                  onClick={exportReportsToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowFullScreenReport(false)}
                  className="hover:bg-slate-100"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Scrollable Report Content */}
          <div className="flex-1 overflow-y-auto p-6 print:p-0" id="print-report-content">
            {/* Print Header - Only visible when printing */}
            <div className="hidden print:block mb-6">
              <h1 className="text-2xl font-bold text-center">Account Reports - {accountViewPartner}</h1>
              <p className="text-center text-slate-500">
                {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} View | 
                Generated: {new Date().toLocaleString()}
              </p>
            </div>
            
            {accountReportData && (
              <div className="space-y-6 print:space-y-4">
                {/* Outstanding Balance */}
                {accountReportData.outstanding_balance && (
                  <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border print:border-2 print:p-4">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 print:text-lg">📊 Outstanding Balance Report</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 print:gap-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total Debit</p>
                        <p className="text-2xl font-bold text-red-600 print:text-xl">₹{accountReportData.outstanding_balance.total_debit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total Credit</p>
                        <p className="text-2xl font-bold text-green-600 print:text-xl">₹{accountReportData.outstanding_balance.total_credit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Net Balance</p>
                        <p className={`text-2xl font-bold print:text-xl ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.outstanding_balance.balance?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Status</p>
                        <p className={`text-lg font-bold ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {accountReportData.outstanding_balance.status}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Profit Summary */}
                {accountReportData.profit_summary && (
                  <div className="p-6 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200 print:border-2 print:p-4">
                    <h3 className="text-xl font-bold text-emerald-800 mb-4 print:text-lg">💰 Profit Share Summary</h3>
                    <div className="grid grid-cols-3 gap-6 print:gap-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Self Share ({accountViewData?.profit_sharing?.self_percentage}%)</p>
                        <p className="text-2xl font-bold text-emerald-600 print:text-xl">₹{accountReportData.profit_summary.self_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Partner Share ({accountViewData?.profit_sharing?.partner_percentage}%)</p>
                        <p className="text-2xl font-bold text-blue-600 print:text-xl">₹{accountReportData.profit_summary.partner_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total GP</p>
                        <p className="text-2xl font-bold text-slate-800 print:text-xl">₹{accountReportData.profit_summary.total_gp?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Company Payments */}
                {accountReportData.company_payments && (
                  <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-green-800 print:text-lg">🏦 Company Payments Report</h3>
                      <div className="flex items-center gap-4">
                        {accountReportData.company_payments.total && (
                          <span className="text-lg font-bold text-green-700">Total: ₹{accountReportData.company_payments.total?.toLocaleString()}</span>
                        )}
                        <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1">{accountReportData.company_payments.count} payments</Badge>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-green-100">
                            <th className="text-left p-3 border border-green-200">#</th>
                            <th className="text-left p-3 border border-green-200">Payment Mode / Tracking</th>
                            <th className="text-right p-3 border border-green-200">Amount</th>
                            <th className="text-right p-3 border border-green-200">Parcels</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.company_payments.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-green-50">
                              <td className="p-3 border border-green-100">{idx + 1}</td>
                              <td className="p-3 border border-green-100 font-mono">{item.payment_mode || item.tracking}</td>
                              <td className="p-3 border border-green-100 text-right font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</td>
                              <td className="p-3 border border-green-100 text-right">{item.parcel_count || 1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Personal Collections */}
                {accountReportData.personal_collections && (
                  <div className="p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-yellow-800 print:text-lg">👤 Personal Collections (Neutral)</h3>
                      <div className="flex items-center gap-4">
                        {accountReportData.personal_collections.total && (
                          <span className="text-lg font-bold text-yellow-700">Total: ₹{accountReportData.personal_collections.total?.toLocaleString()}</span>
                        )}
                        <Badge className="bg-yellow-100 text-yellow-700 text-sm px-3 py-1">{accountReportData.personal_collections.count} collections</Badge>
                      </div>
                    </div>
                    <p className="text-sm text-yellow-600 mb-3">These payments have no effect on balance (neutral)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-yellow-100">
                            <th className="text-left p-3 border border-yellow-200">#</th>
                            <th className="text-left p-3 border border-yellow-200">Payment Mode / Tracking</th>
                            <th className="text-right p-3 border border-yellow-200">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.personal_collections.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-yellow-50">
                              <td className="p-3 border border-yellow-100">{idx + 1}</td>
                              <td className="p-3 border border-yellow-100 font-mono">{item.payment_mode || item.tracking}</td>
                              <td className="p-3 border border-yellow-100 text-right font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Multi-Parcel Payments */}
                {accountReportData.multi_parcel && accountReportData.multi_parcel.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-purple-800 print:text-lg">📦 Multi-Parcel Payments</h3>
                      <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">{accountReportData.multi_parcel.count} payments</Badge>
                    </div>
                    {accountReportData.multi_parcel.note && (
                      <p className="text-sm text-purple-600 italic mb-3">{accountReportData.multi_parcel.note}</p>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-purple-100">
                            <th className="text-left p-3 border border-purple-200">#</th>
                            <th className="text-left p-3 border border-purple-200">Payment Mode</th>
                            <th className="text-right p-3 border border-purple-200">Amount</th>
                            <th className="text-right p-3 border border-purple-200">Parcel Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.multi_parcel.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-purple-50">
                              <td className="p-3 border border-purple-100">{idx + 1}</td>
                              <td className="p-3 border border-purple-100 font-mono">{item.payment_mode}</td>
                              <td className="p-3 border border-purple-100 text-right font-semibold">₹{item.payment_amount?.toLocaleString()}</td>
                              <td className="p-3 border border-purple-100 text-right font-bold text-purple-700">{item.parcel_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Negative GP */}
                {accountReportData.negative_gp && accountReportData.negative_gp.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-red-800 print:text-lg">⚠️ Negative GP Report (Loss)</h3>
                      <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">{accountReportData.negative_gp.count} parcels</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-red-100">
                            <th className="text-left p-3 border border-red-200">#</th>
                            <th className="text-left p-3 border border-red-200">Tracking</th>
                            <th className="text-right p-3 border border-red-200">GP (Loss)</th>
                            <th className="text-left p-3 border border-red-200">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.negative_gp.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-red-50">
                              <td className="p-3 border border-red-100">{idx + 1}</td>
                              <td className="p-3 border border-red-100 font-mono">{item.tracking}</td>
                              <td className="p-3 border border-red-100 text-right font-semibold text-red-600">₹{item.gp?.toLocaleString()}</td>
                              <td className="p-3 border border-red-100">{formatDMY(item.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* No Invoice */}
                {accountReportData.no_invoice && accountReportData.no_invoice.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-orange-800 print:text-lg">📋 No Invoice Report</h3>
                      <Badge className="bg-orange-100 text-orange-700 text-sm px-3 py-1">{accountReportData.no_invoice.count} parcels</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-orange-100">
                            <th className="text-left p-3 border border-orange-200">#</th>
                            <th className="text-left p-3 border border-orange-200">Tracking</th>
                            <th className="text-left p-3 border border-orange-200">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.no_invoice.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-orange-50">
                              <td className="p-3 border border-orange-100">{idx + 1}</td>
                              <td className="p-3 border border-orange-100 font-mono">{item.tracking}</td>
                              <td className="p-3 border border-orange-100">{formatDMY(item.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* High Value */}
                {accountReportData.high_value && accountReportData.high_value.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-blue-800 print:text-lg">💎 High Value Transactions (≥₹10K)</h3>
                      <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">{accountReportData.high_value.count} transactions</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-blue-100">
                            <th className="text-left p-3 border border-blue-200">#</th>
                            <th className="text-left p-3 border border-blue-200">Tracking / Payment</th>
                            <th className="text-right p-3 border border-blue-200">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.high_value.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-blue-50">
                              <td className="p-3 border border-blue-100">{idx + 1}</td>
                              <td className="p-3 border border-blue-100 font-mono">{item.tracking || item.payment_mode}</td>
                              <td className="p-3 border border-blue-100 text-right font-semibold text-blue-800">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Pending Payment */}
                {accountReportData.pending_payment && accountReportData.pending_payment.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-slate-800 print:text-lg">⏳ Pending Payment Report</h3>
                      <div className="flex items-center gap-3">
                        {accountReportData.pending_payment.total_pending > 0 && (
                          <span className="text-lg font-bold text-orange-600">
                            Total: ₹{accountReportData.pending_payment.total_pending?.toLocaleString()}
                          </span>
                        )}
                        <Badge className="bg-slate-200 text-slate-700 text-sm px-3 py-1">
                          {accountReportData.pending_payment.count} parcels
                        </Badge>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-2 border border-slate-200 w-10">#</th>
                            <th className="text-left p-2 border border-slate-200">Tracking</th>
                            <th className="text-left p-2 border border-slate-200">Customer</th>
                            <th className="text-right p-2 border border-slate-200">Sale Amount</th>
                            <th className="text-left p-2 border border-slate-200">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.pending_payment.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="p-2 border border-slate-100 text-slate-500">{idx + 1}</td>
                              <td className="p-2 border border-slate-100 font-mono text-slate-700">
                                {item.tracking || '-'}
                              </td>
                              <td className="p-2 border border-slate-100 text-slate-600 max-w-[150px] truncate">
                                {item.customer || '-'}
                              </td>
                              <td className="p-2 border border-slate-100 text-right font-semibold text-orange-600">
                                ₹{(item.sale_amount || 0).toLocaleString()}
                              </td>
                              <td className="p-2 border border-slate-100 text-slate-500">
                                {formatDMY(item.date)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Dormant Account Report */}
                {accountReportData.dormant_account && (
                  <div className={`p-6 rounded-lg border print:border-2 print:p-4 ${
                    accountReportData.dormant_account.is_dormant 
                      ? 'bg-gradient-to-r from-slate-100 to-gray-100 border-slate-400' 
                      : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-xl font-bold print:text-lg ${
                        accountReportData.dormant_account.is_dormant ? 'text-slate-700' : 'text-green-800'
                      }`}>
                        💤 Dormant Account Report
                      </h3>
                      <Badge className={`text-sm px-3 py-1 ${
                        accountReportData.dormant_account.is_dormant 
                          ? 'bg-slate-300 text-slate-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {accountReportData.dormant_account.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Account Status</p>
                        <p className={`text-lg font-bold ${accountReportData.dormant_account.is_dormant ? 'text-slate-600' : 'text-green-600'}`}>
                          {accountReportData.dormant_account.is_dormant ? 'Dormant' : 'Active'}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Days Since Last Txn</p>
                        <p className={`text-lg font-bold ${
                          accountReportData.dormant_account.days_since_last_transaction > 30 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {accountReportData.dormant_account.days_since_last_transaction ?? 'N/A'}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Last Transaction</p>
                        <p className="text-lg font-bold text-slate-700">
                          {accountReportData.dormant_account.last_transaction_date}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Old Parcels (&gt;30d)</p>
                        <p className="text-lg font-bold text-slate-700">
                          {accountReportData.dormant_account.old_parcels_count}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Dispute/Adjustment Report */}
                {accountReportData.dispute_adjustment && accountReportData.dispute_adjustment.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-rose-50 to-red-50 rounded-lg border border-rose-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-rose-800 print:text-lg">⚖️ Dispute/Adjustment Report</h3>
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold text-rose-700">
                          Total: ₹{accountReportData.dispute_adjustment.total_adjustment?.toLocaleString()}
                        </span>
                        <Badge className="bg-rose-100 text-rose-700 text-sm px-3 py-1">
                          {accountReportData.dispute_adjustment.count} items
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-rose-600 mb-3">
                      Parcels with negative GP or unusual margin (&gt;50% deviation)
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-rose-100">
                            <th className="text-left p-3 border border-rose-200">#</th>
                            <th className="text-left p-3 border border-rose-200">Tracking</th>
                            <th className="text-right p-3 border border-rose-200">Sale Amt</th>
                            <th className="text-right p-3 border border-rose-200">Cost Amt</th>
                            <th className="text-right p-3 border border-rose-200">Adjustment</th>
                            <th className="text-right p-3 border border-rose-200">%</th>
                            <th className="text-left p-3 border border-rose-200">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.dispute_adjustment.items?.slice(0, 50).map((item, idx) => (
                            <tr key={idx} className="hover:bg-rose-50">
                              <td className="p-3 border border-rose-100">{idx + 1}</td>
                              <td className="p-3 border border-rose-100 font-mono">{item.tracking}</td>
                              <td className="p-3 border border-rose-100 text-right">₹{(item.sale_amount || 0).toLocaleString()}</td>
                              <td className="p-3 border border-rose-100 text-right">₹{(item.cost_amount || 0).toLocaleString()}</td>
                              <td className={`p-3 border border-rose-100 text-right font-semibold ${
                                item.adjustment_amount < 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                ₹{item.adjustment_amount?.toLocaleString()}
                              </td>
                              <td className="p-3 border border-rose-100 text-right">{item.adjustment_pct}%</td>
                              <td className="p-3 border border-rose-100">{formatDMY(item.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {accountReportData.dispute_adjustment.items?.length > 50 && (
                        <p className="text-sm text-rose-500 mt-2 text-center">
                          Showing 50 of {accountReportData.dispute_adjustment.items.length} items. Export to Excel for full list.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Settlement Pending Report */}
                {accountReportData.settlement_pending && (accountReportData.settlement_pending.count > 0 || accountReportData.settlement_pending.has_third_party) && (
                  <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-indigo-800 print:text-lg">🤝 Settlement Pending Report</h3>
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold text-indigo-700">
                          Total: ₹{accountReportData.settlement_pending.total_pending?.toLocaleString()}
                        </span>
                        <Badge className="bg-indigo-100 text-indigo-700 text-sm px-3 py-1">
                          {accountReportData.settlement_pending.count} items
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-indigo-600 mb-3">
                      Profit share pending settlement with <span className="font-semibold">{accountReportData.settlement_pending.third_party_name}</span>
                    </p>
                    {accountReportData.settlement_pending.count > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-indigo-100">
                              <th className="text-left p-3 border border-indigo-200">#</th>
                              <th className="text-left p-3 border border-indigo-200">Tracking</th>
                              <th className="text-right p-3 border border-indigo-200">Sale Amount</th>
                              <th className="text-right p-3 border border-indigo-200">Third Party Share</th>
                              <th className="text-left p-3 border border-indigo-200">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accountReportData.settlement_pending.items?.slice(0, 50).map((item, idx) => (
                              <tr key={idx} className="hover:bg-indigo-50">
                                <td className="p-3 border border-indigo-100">{idx + 1}</td>
                                <td className="p-3 border border-indigo-100 font-mono">{item.tracking}</td>
                                <td className="p-3 border border-indigo-100 text-right">₹{(item.sale_amount || 0).toLocaleString()}</td>
                                <td className="p-3 border border-indigo-100 text-right font-semibold text-indigo-700">
                                  ₹{(item.third_party_share || 0).toLocaleString()}
                                </td>
                                <td className="p-3 border border-indigo-100">{formatDMY(item.date)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {accountReportData.settlement_pending.items?.length > 50 && (
                          <p className="text-sm text-indigo-500 mt-2 text-center">
                            Showing 50 of {accountReportData.settlement_pending.items.length} items. Export to Excel for full list.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-indigo-500">
                        <p>No pending settlements found for this partner.</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Month-End Summary Report */}
                {accountReportData.month_end_summary && (
                  <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-cyan-800 print:text-lg">📅 Month-End Summary</h3>
                      <Badge className="bg-cyan-100 text-cyan-700 text-sm px-3 py-1">
                        {accountReportData.month_end_summary.current_month} {accountReportData.month_end_summary.current_year}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-xs text-slate-500 mb-1">Opening Balance</p>
                        <p className="text-lg font-bold text-slate-700">₹{accountReportData.month_end_summary.opening_balance?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-xs text-slate-500 mb-1">Monthly Debits</p>
                        <p className="text-lg font-bold text-red-600">₹{accountReportData.month_end_summary.monthly_debits?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-xs text-slate-500 mb-1">Monthly Credits</p>
                        <p className="text-lg font-bold text-green-600">₹{accountReportData.month_end_summary.monthly_credits?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-xs text-slate-500 mb-1">Net Movement</p>
                        <p className={`text-lg font-bold ${accountReportData.month_end_summary.net_movement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.month_end_summary.net_movement?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-xs text-slate-500 mb-1">Monthly Parcels</p>
                        <p className="text-lg font-bold text-blue-600">{accountReportData.month_end_summary.monthly_parcels}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border border-2 border-cyan-200">
                        <p className="text-xs text-slate-500 mb-1">Closing Balance</p>
                        <p className={`text-lg font-bold ${accountReportData.month_end_summary.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.month_end_summary.closing_balance?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {/* Month-over-Month Comparison */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-100">
                      <h4 className="text-sm font-semibold text-cyan-700 mb-2">📊 Previous Month Comparison</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Prev Month Debits</p>
                          <p className="font-semibold text-slate-700">₹{accountReportData.month_end_summary.prev_month_debits?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Month-over-Month Change</p>
                          <p className={`font-semibold ${accountReportData.month_end_summary.month_over_month_change >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {accountReportData.month_end_summary.month_over_month_change >= 0 ? '+' : ''}
                            ₹{accountReportData.month_end_summary.month_over_month_change?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ledger Preview Dialog - Full Screen Print View */}
      <Dialog open={showLedgerPreview} onOpenChange={setShowLedgerPreview}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] w-full h-full overflow-hidden flex flex-col p-0">
          {/* Sticky Action Bar */}
          <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-3 print:hidden">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  PLA - {accountViewPartner}
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} | 
                  {accountViewMode === "payment" 
                    ? ` ${accountViewData?.payment_groups?.length || 0} groups`
                    : ` ${accountViewData?.ledger_entries?.length || 0} entries`}
                  {showFullDetails && " | Full Details"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Column Selector Dropdown */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="column-selector-btn"
                  >
                    <Filter className="w-4 h-4 mr-1" />
                    Columns ({Object.values(exportColumns).filter(v => v).length - (accountViewMode !== 'payment' && exportColumns.grp_bal ? 1 : 0)})
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                  {showColumnSelector && (
                    <div 
                      className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-3 min-w-[200px]"
                      data-testid="column-selector-dropdown"
                    >
                      <p className="text-xs font-semibold text-slate-500 mb-2">Select Export Columns</p>
                      <div className="space-y-1.5">
                        {[
                          { key: 'sno', label: 'S.No' },
                          { key: 'date', label: 'Date' },
                          { key: 'tracking', label: 'Tracking' },
                          { key: 'sale', label: 'Sale' },
                          { key: 'gp', label: 'GP' },
                          { key: 'coll', label: 'Collection' },
                          { key: 'debit', label: 'Debit' },
                          { key: 'credit', label: 'Credit' },
                          { key: 'balance', label: 'Balance' },
                          ...(accountViewMode === 'payment' ? [{ key: 'grp_bal', label: 'Grp Bal' }] : [])
                        ].map(col => (
                          <label 
                            key={col.key} 
                            className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded"
                          >
                            <Checkbox
                              checked={exportColumns[col.key]}
                              onCheckedChange={(checked) => setExportColumns(prev => ({ ...prev, [col.key]: checked }))}
                              data-testid={`col-checkbox-${col.key}`}
                            />
                            <span className="text-sm text-slate-700">{col.label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 pt-2 border-t border-slate-100 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-xs flex-1"
                          onClick={() => setExportColumns({
                            sno: true, date: true, tracking: true, sale: true, gp: true,
                            coll: true, debit: true, credit: true, balance: true, grp_bal: true
                          })}
                        >
                          Select All
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-xs flex-1"
                          onClick={() => setExportColumns({
                            sno: false, date: false, tracking: false, sale: false, gp: false,
                            coll: false, debit: false, credit: false, balance: false, grp_bal: false
                          })}
                        >
                          Deselect
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Full Details Toggle */}
                <Button 
                  variant={showFullDetails ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFullDetails(!showFullDetails)}
                  className={showFullDetails ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-300 text-purple-700 hover:bg-purple-50"}
                >
                  <List className="w-4 h-4 mr-1" />
                  {showFullDetails ? "Full Details ON" : "Full Details"}
                </Button>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.print()}
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print
                </Button>
                <Button 
                  size="sm"
                  onClick={() => {
                    handleExportAccountView('excel');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  Excel
                </Button>
                <Button 
                  size="sm"
                  onClick={() => {
                    handleExportAccountView('pdf');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowLedgerPreview(false)}
                  className="hover:bg-slate-100 ml-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Full Spreadsheet View */}
          <div className="flex-1 overflow-auto bg-white" id="ledger-print-content">
            {accountViewData && (
              <div className="min-w-[1100px]">
                {/* Document Header - Simplified PLA */}
                <div className="bg-gray-50 border-b border-slate-300 px-6 py-3 print:py-4">
                  <div className="text-center">
                    <h1 className="text-lg font-bold text-slate-800">PLA - {accountViewPartner}</h1>
                    <p className="text-xs text-slate-500 mt-1">
                      {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"}
                      {accountViewDateFrom && ` | ${accountViewDateFrom}`}
                      {accountViewDateTo && ` to ${accountViewDateTo}`}
                      {` | Opening: ₹${(accountViewData.opening_balance || 0).toLocaleString()}`}
                      {` | Closing: ₹${accountViewData.closing_balance?.toLocaleString()}`}
                    </p>
                  </div>
                </div>
                
                {/* Excel-style Table - LIGHTER colors for print */}
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-200 sticky top-0 print:bg-gray-200">
                    <tr>
                      <th className="p-2 text-left border border-gray-300 w-10 font-semibold text-gray-700">S.No</th>
                      <th className="p-2 text-left border border-gray-300 w-20 font-semibold text-gray-700">Date</th>
                      <th className="p-2 text-left border border-gray-300 font-semibold text-gray-700">Tracking</th>
                      <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">Sale</th>
                      <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">GP</th>
                      <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">Coll</th>
                      <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-700">Debit</th>
                      <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-700">Credit</th>
                      <th className="p-2 text-right border border-gray-300 w-28 font-semibold text-gray-700">Balance</th>
                      {accountViewMode === "payment" && (
                        <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-orange-700">Grp Bal</th>
                      )}
                    </tr>
                    {/* TOTALS Summary Row - Frozen with header */}
                    <tr className="bg-gray-300 font-bold text-sm">
                      <td className="p-2 border border-gray-400 text-center">-</td>
                      <td className="p-2 border border-gray-400 font-bold">
                        TOTALS
                        <span className="ml-1 text-xs font-normal text-gray-600">
                          ({accountViewMode === 'parcel'
                            ? `${accountViewData.ledger_entries?.length || 0} parcels`
                            : `${accountViewData.payment_groups?.length || 0} payments`
                          })
                        </span>
                      </td>
                      <td className="p-2 border border-gray-400">-</td>
                      <td className="p-2 border border-gray-400 text-right text-blue-700">
                        {(() => {
                          // Calculate totals based on view mode
                          if (accountViewMode === 'parcel') {
                            return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.sale_amount || 0), 0) || 0).toLocaleString();
                          } else {
                            // Payment-wise: sum from all parcels in all groups
                            let total = 0;
                            accountViewData.payment_groups?.forEach(g => {
                              g.parcels?.forEach(p => { total += p.sale_amount || 0; });
                            });
                            return total.toLocaleString();
                          }
                        })()}
                      </td>
                      <td className="p-2 border border-gray-400 text-right text-blue-700">
                        {(() => {
                          if (accountViewMode === 'parcel') {
                            return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.gp || 0), 0) || 0).toLocaleString();
                          } else {
                            let total = 0;
                            accountViewData.payment_groups?.forEach(g => {
                              g.parcels?.forEach(p => { total += p.gp || 0; });
                            });
                            return total.toLocaleString();
                          }
                        })()}
                      </td>
                      <td className="p-2 border border-gray-400 text-right text-purple-700 italic">
                        {/* Only sum personal collection (not ESR/Company payments) */}
                        {(() => {
                          let total = 0;
                          if (accountViewMode === 'parcel') {
                            // Parcel-wise: look for personal entries in each parcel
                            accountViewData.ledger_entries?.forEach(e => {
                              const collEntry = e.entries?.find(en => en.category === 'collection');
                              total += collEntry?.collection || 0;
                            });
                          } else {
                            // Payment-wise: sum payment_amount from non-company payment groups
                            accountViewData.payment_groups?.forEach(g => {
                              if (!g.is_company_payment && g.payment_amount > 0) {
                                total += g.payment_amount;
                              }
                            });
                          }
                          return total.toLocaleString();
                        })()}
                      </td>
                      <td className="p-2 border border-gray-400 text-right text-red-700">
                        {(() => {
                          if (accountViewMode === 'parcel') {
                            return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.parcel_summary?.total_debit || 0), 0) || 0).toLocaleString();
                          } else {
                            let total = 0;
                            accountViewData.payment_groups?.forEach(g => {
                              g.parcels?.forEach(p => { total += p.parcel_summary?.total_debit || 0; });
                            });
                            return total.toLocaleString();
                          }
                        })()}
                      </td>
                      <td className="p-2 border border-gray-400 text-right text-green-700">
                        {(() => {
                          if (accountViewMode === 'parcel') {
                            return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.parcel_summary?.total_credit || 0), 0) || 0).toLocaleString();
                          } else {
                            // FIX: Use group_summary.total_credit (only company payments) instead of payment_amount (all payments)
                            let total = 0;
                            accountViewData.payment_groups?.forEach(g => {
                              total += g.group_summary?.total_credit || 0;
                            });
                            return total.toLocaleString();
                          }
                        })()}
                      </td>
                      <td className="p-2 border border-gray-400 text-right font-bold text-slate-800">
                        {accountViewData.closing_balance?.toLocaleString()}
                      </td>
                      {accountViewMode === "payment" && (
                        <td className="p-2 border border-gray-400 text-right font-bold">
                          {/* Grp Bal Total: Sum of (Group Sales - Group Payment) for multi-parcel groups only */}
                          {(() => {
                            let totalGrpBal = 0;
                            accountViewData.payment_groups?.forEach(g => {
                              if (g.parcel_count >= 2 && g.payment_amount > 0) {
                                const groupSales = g.parcels?.reduce((sum, p) => sum + (p.sale_amount || 0), 0) || 0;
                                totalGrpBal += groupSales - g.payment_amount;
                              }
                            });
                            const isPositive = totalGrpBal > 0;
                            const isNegative = totalGrpBal < 0;
                            return (
                              <span className={isPositive ? 'text-red-600' : isNegative ? 'text-green-600' : 'text-gray-500'}>
                                {totalGrpBal !== 0 ? totalGrpBal.toLocaleString() : '-'}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row */}
                    <tr className="bg-gray-100 font-semibold">
                      <td className="p-2 border border-gray-200 text-center">-</td>
                      <td className="p-2 border border-gray-200">-</td>
                      <td className="p-2 border border-gray-200 font-bold">OPENING</td>
                      <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                      <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                      <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                      <td className="p-2 border border-gray-200 text-right">-</td>
                      <td className="p-2 border border-gray-200 text-right">-</td>
                      <td className="p-2 border border-gray-200 text-right font-bold">
                        {(accountViewData.opening_balance || 0).toLocaleString()}
                      </td>
                      {accountViewMode === "payment" && (
                        <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                      )}
                    </tr>
                    
                    {/* Parcel-wise View */}
                    {accountViewMode === "parcel" && accountViewData.ledger_entries?.map((entry, idx) => {
                      const hasNoInvoice = entry.has_no_invoice;
                      const hasNegGp = entry.has_negative_gp;
                      
                      // Extract values from nested entries array
                      const subEntries = entry.entries || [];
                      const costingEntry = subEntries.find(e => e.category === 'costing');
                      const gpSelfEntry = subEntries.find(e => e.category === 'gp_self');
                      const paymentEntry = subEntries.find(e => e.category && (e.category.includes('payment') || e.category.includes('esr')));
                      const collEntry = subEntries.find(e => e.category === 'collection');
                      const lastEntry = subEntries[subEntries.length - 1];
                      
                      const costAmount = costingEntry?.debit || 0;
                      const gpSelf = gpSelfEntry?.debit || 0;
                      const saleAmount = entry.sale_amount || 0;
                      const gpPartner = entry.gp || 0;
                      const creditAmount = paymentEntry?.credit || entry.parcel_summary?.total_credit || 0;
                      const collectionAmount = entry.parcel_summary?.collection || collEntry?.collection || 0;
                      const personalCollectionAmount = collEntry?.collection || 0;
                      const totalDebit = entry.parcel_summary?.total_debit || costAmount;
                      
                      // Get running balance from last sub-entry
                      const finalBalance = lastEntry?.balance ?? 0;
                      const paymentZero = !creditAmount || creditAmount === 0;
                      
                      // Get intermediate balances from sub-entries
                      const balanceAfterCosting = costingEntry?.balance ?? finalBalance;
                      const balanceAfterGpSelf = gpSelfEntry?.balance ?? balanceAfterCosting;
                      
                      // Determine row background color
                      let parcelHeaderBg = 'bg-blue-50/50';
                      if (hasNoInvoice) {
                        parcelHeaderBg = 'bg-amber-50';
                      } else if (paymentZero) {
                        parcelHeaderBg = 'bg-red-50/30';
                      }
                      
                      if (!showFullDetails) {
                        // COMPACT VIEW - Single row per parcel
                        return (
                          <tr key={`entry-${idx}`} className={parcelHeaderBg}>
                            <td className="p-2 border border-gray-200 text-center">{idx + 1}</td>
                            <td className="p-2 border border-gray-200 text-xs">{formatDMY(entry.date)}</td>
                            <td className="p-2 border border-gray-200">
                              <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{entry.tracking}</span>
                              {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                              {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                              {paymentZero && !hasNoInvoice && <span className="ml-2 text-xs text-red-500 font-bold">[P]</span>}
                            </td>
                            <td className="p-2 border border-gray-200 text-right text-gray-500">
                              {saleAmount ? saleAmount.toLocaleString() : '-'}
                            </td>
                            <td className="p-2 border border-gray-200 text-right text-gray-500">
                              {gpPartner ? gpPartner.toLocaleString() : '-'}
                            </td>
                            <td className={`p-2 border border-gray-200 text-right ${personalCollectionAmount > 0 ? 'text-purple-600 font-medium italic' : 'text-gray-400'}`}>
                              {personalCollectionAmount > 0 ? personalCollectionAmount.toLocaleString() : '-'}
                            </td>
                            <td className="p-2 border border-gray-200 text-right font-medium">
                              {totalDebit > 0 ? totalDebit.toLocaleString() : '-'}
                            </td>
                            <td className={`p-2 border border-gray-200 text-right font-medium ${creditAmount > 0 ? 'bg-green-50' : ''}`}>
                              {creditAmount > 0 ? creditAmount.toLocaleString() : '-'}
                            </td>
                            <td className={`p-2 border border-gray-200 text-right font-bold ${
                              finalBalance >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {finalBalance?.toLocaleString()}
                            </td>
                          </tr>
                        );
                      }
                      
                      // FULL DETAILS VIEW - Multiple rows per parcel
                      return (
                        <React.Fragment key={`entry-${idx}`}>
                          {/* Parcel Header Row */}
                          <tr className={`${parcelHeaderBg} font-semibold`}>
                            <td className="p-2 border border-gray-200 text-center">{idx + 1}</td>
                            <td colSpan={8} className="p-2 border border-gray-200">
                              <span className="font-bold">** PARCEL: </span>
                              <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{entry.tracking}</span>
                              {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                              {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                            </td>
                          </tr>
                          
                          {/* Costing Row */}
                          {costAmount > 0 && (
                            <tr className="bg-white">
                              <td className="p-1 border border-gray-200"></td>
                              <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                              <td className="p-1 border border-gray-200 pl-8 text-gray-600">Costing</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-red-600">{costAmount.toLocaleString()}</td>
                              <td className="p-1 border border-gray-200 text-right">-</td>
                              <td className={`p-1 border border-gray-200 text-right ${balanceAfterCosting >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {balanceAfterCosting.toLocaleString()}
                              </td>
                            </tr>
                          )}
                          
                          {/* GP Self Row */}
                          {gpSelf > 0 && (
                            <tr className="bg-white">
                              <td className="p-1 border border-gray-200"></td>
                              <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                              <td className="p-1 border border-gray-200 pl-8 text-gray-600">
                                GP Self ({accountViewData.profit_sharing?.self_percentage || 50}%)
                              </td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-red-600">{gpSelf.toLocaleString()}</td>
                              <td className="p-1 border border-gray-200 text-right">-</td>
                              <td className={`p-1 border border-gray-200 text-right ${balanceAfterGpSelf >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {balanceAfterGpSelf.toLocaleString()}
                              </td>
                            </tr>
                          )}
                          
                          {/* Sale Invoice Row (Info only) */}
                          <tr className="bg-gray-50/50">
                            <td className="p-1 border border-gray-200"></td>
                            <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                            <td className="p-1 border border-gray-200 pl-8 text-gray-600">Sale Invoice</td>
                            <td className="p-1 border border-gray-200 text-right text-gray-500">{saleAmount ? saleAmount.toLocaleString() : '-'}</td>
                            <td className="p-1 border border-gray-200 text-right text-gray-500">{gpPartner ? gpPartner.toLocaleString() : '-'}</td>
                            <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                            <td className="p-1 border border-gray-200 text-right">-</td>
                            <td className="p-1 border border-gray-200 text-right">-</td>
                            <td className={`p-1 border border-gray-200 text-right ${balanceAfterGpSelf >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {(gpSelf > 0 ? balanceAfterGpSelf : balanceAfterCosting).toLocaleString()}
                            </td>
                          </tr>
                          
                          {/* Payment/Credit Row */}
                          {creditAmount > 0 && (
                            <tr className="bg-green-50/50">
                              <td className="p-1 border border-gray-200"></td>
                              <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                              <td className="p-1 border border-gray-200 pl-8 text-green-700 font-medium">
                                {paymentEntry?.description || 'Payment'}
                              </td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right">-</td>
                              <td className="p-1 border border-gray-200 text-right text-green-600 font-medium">{creditAmount.toLocaleString()}</td>
                              <td className={`p-1 border border-gray-200 text-right font-bold ${finalBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {finalBalance?.toLocaleString()}
                              </td>
                            </tr>
                          )}
                          
                          {/* Personal Collection Row (INFO only - no balance effect) */}
                          {personalCollectionAmount > 0 && !creditAmount && (
                            <tr className="bg-purple-50/50">
                              <td className="p-1 border border-gray-200"></td>
                              <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                              <td className="p-1 border border-gray-200 pl-8 text-purple-700 font-medium italic">
                                {personalEntry?.description || 'Personal Collection'} (INFO)
                              </td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-purple-600 font-medium italic">
                                {personalCollectionAmount.toLocaleString()}
                              </td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                              <td className={`p-1 border border-gray-200 text-right font-bold ${finalBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {finalBalance?.toLocaleString()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Payment-wise View - Grouped with all parcels expanded */}
                    {accountViewMode === "payment" && accountViewData.payment_groups?.map((group, gIdx) => {
                      const isPaymentZero = !group.payment_amount || group.payment_amount === 0;
                      const groupBgClass = isPaymentZero ? 'bg-red-50' : 'bg-purple-50/50';
                      
                      // Check if this group has personal collection (not company payment)
                      const isPersonalCollection = !group.is_company_payment && group.payment_amount > 0;
                      const groupPersonalCollection = isPersonalCollection ? group.payment_amount : 0;
                      
                      // Calculate Group Balance (Sales - Payment) for multi-parcel groups
                      const isMultiParcel = group.parcel_count >= 2;
                      const groupTotalSales = group.parcels?.reduce((sum, p) => sum + (p.sale_amount || 0), 0) || 0;
                      const groupBalance = isMultiParcel && group.payment_amount > 0 ? groupTotalSales - group.payment_amount : null;
                      
                      return (
                        <React.Fragment key={`group-${gIdx}`}>
                          {/* Payment Group Header Row */}
                          <tr className={`${groupBgClass} font-semibold`}>
                            <td colSpan={10} className={`p-2 border ${isPaymentZero ? 'border-red-200 text-red-700' : 'border-purple-200 text-purple-700'}`}>
                              PMT: {group.payment_mode || 'No Pmt'} 
                              {group.payment_amount > 0 && ` — ₹${group.payment_amount.toLocaleString()}`}
                              {isPaymentZero && ' ⚠️'}
                              <span className="ml-2 text-xs font-normal">({group.parcel_count})</span>
                              {group.is_company_payment && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1 rounded">[CO]</span>}
                              {isPersonalCollection && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1 rounded">[P]</span>}
                            </td>
                          </tr>
                          
                          {/* All Parcels in this Payment Group */}
                          {group.parcels?.map((parcel, pIdx) => {
                            const isLastParcel = pIdx === group.parcels.length - 1;
                            const showCredit = isLastParcel && group.is_company_payment && group.payment_amount > 0;
                            const showPersonalColl = isLastParcel && isPersonalCollection;
                            const hasNoInvoice = parcel.has_no_invoice;
                            const hasNegGp = parcel.has_negative_gp;
                            
                            // Extract values from nested entries array
                            const subEntries = parcel.entries || [];
                            const costingEntry = subEntries.find(e => e.category === 'costing');
                            const gpSelfEntry = subEntries.find(e => e.category === 'gp_self');
                            const lastEntry = subEntries[subEntries.length - 1];
                            
                            const costAmount = costingEntry?.debit || 0;
                            const gpSelf = gpSelfEntry?.debit || 0;
                            const saleAmount = parcel.sale_amount || 0;
                            const gpPartner = parcel.gp || 0;
                            const totalDebit = parcel.parcel_summary?.total_debit || costAmount;
                            
                            // Get running balance from last sub-entry
                            const finalBalance = lastEntry?.balance ?? 0;
                            
                            // Get intermediate balances from sub-entries
                            const balanceAfterCosting = costingEntry?.balance ?? finalBalance;
                            const balanceAfterGpSelf = gpSelfEntry?.balance ?? balanceAfterCosting;
                            
                            // Determine row background
                            let rowBgClass = 'bg-blue-50/30';
                            if (hasNoInvoice) {
                              rowBgClass = 'bg-amber-50';
                            } else if (isPaymentZero) {
                              rowBgClass = 'bg-red-50/30';
                            }
                            
                            if (!showFullDetails) {
                              // COMPACT VIEW
                              return (
                                <tr key={`parcel-${gIdx}-${pIdx}`} className={rowBgClass}>
                                  <td className="p-2 border border-gray-200 text-center text-gray-500">{pIdx + 1}</td>
                                  <td className="p-2 border border-gray-200 text-xs">{formatDMY(parcel.date)}</td>
                                  <td className="p-2 border border-gray-200 pl-6">
                                    <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{parcel.tracking}</span>
                                    {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                                    {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                                  </td>
                                  <td className="p-2 border border-gray-200 text-right text-gray-500">
                                    {saleAmount ? saleAmount.toLocaleString() : '-'}
                                  </td>
                                  <td className="p-2 border border-gray-200 text-right text-gray-500">
                                    {gpPartner ? gpPartner.toLocaleString() : '-'}
                                  </td>
                                  <td className={`p-2 border border-gray-200 text-right ${showPersonalColl ? 'text-purple-600 font-medium italic' : 'text-gray-400'}`}>
                                    {showPersonalColl ? groupPersonalCollection.toLocaleString() : '-'}
                                  </td>
                                  <td className="p-2 border border-gray-200 text-right font-medium">
                                    {totalDebit > 0 ? totalDebit.toLocaleString() : '-'}
                                  </td>
                                  <td className={`p-2 border border-gray-200 text-right font-medium ${showCredit ? 'bg-green-50' : ''}`}>
                                    {showCredit ? group.payment_amount.toLocaleString() : '-'}
                                  </td>
                                  <td className={`p-2 border border-gray-200 text-right font-bold ${
                                    finalBalance >= 0 ? 'text-green-700' : 'text-red-700'
                                  }`}>
                                    {finalBalance?.toLocaleString()}
                                  </td>
                                  {/* Grp Bal column - only show on last parcel of multi-parcel groups */}
                                  <td className={`p-2 border border-gray-200 text-right font-medium ${
                                    isLastParcel && groupBalance !== null 
                                      ? (groupBalance > 0 ? 'text-red-600 bg-red-50' : groupBalance < 0 ? 'text-green-600 bg-green-50' : 'text-gray-500')
                                      : 'text-gray-400'
                                  }`}>
                                    {isLastParcel && groupBalance !== null ? groupBalance.toLocaleString() : '-'}
                                  </td>
                                </tr>
                              );
                            }
                            
                            // FULL DETAILS VIEW
                            return (
                              <React.Fragment key={`parcel-${gIdx}-${pIdx}`}>
                                {/* Parcel Header */}
                                <tr className={`${rowBgClass} font-semibold`}>
                                  <td className="p-2 border border-gray-200 text-center text-gray-500">{pIdx + 1}</td>
                                  <td colSpan={9} className="p-2 border border-gray-200 pl-6">
                                    <span className="font-bold">** PARCEL: </span>
                                    <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{parcel.tracking}</span>
                                    {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                                    {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                                  </td>
                                </tr>
                                
                                {/* Costing Row */}
                                {costAmount > 0 && (
                                  <tr className="bg-white">
                                    <td className="p-1 border border-gray-200"></td>
                                    <td className="p-1 border border-gray-200 text-xs pl-4">{parcel.date || ''}</td>
                                    <td className="p-1 border border-gray-200 pl-10 text-gray-600">Costing</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-red-600">{costAmount.toLocaleString()}</td>
                                    <td className="p-1 border border-gray-200 text-right">-</td>
                                    <td className={`p-1 border border-gray-200 text-right ${balanceAfterCosting >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {balanceAfterCosting.toLocaleString()}
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                  </tr>
                                )}
                                
                                {/* GP Self Row */}
                                {gpSelf > 0 && (
                                  <tr className="bg-white">
                                    <td className="p-1 border border-gray-200"></td>
                                    <td className="p-1 border border-gray-200 text-xs pl-4">{parcel.date || ''}</td>
                                    <td className="p-1 border border-gray-200 pl-10 text-gray-600">
                                      GP Self ({accountViewData.profit_sharing?.self_percentage || 50}%)
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-red-600">{gpSelf.toLocaleString()}</td>
                                    <td className="p-1 border border-gray-200 text-right">-</td>
                                    <td className={`p-1 border border-gray-200 text-right ${balanceAfterGpSelf >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {balanceAfterGpSelf.toLocaleString()}
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                  </tr>
                                )}
                                
                                {/* Sale Invoice Row */}
                                <tr className="bg-gray-50/50">
                                  <td className="p-1 border border-gray-200"></td>
                                  <td className="p-1 border border-gray-200 text-xs pl-4">{parcel.date || ''}</td>
                                  <td className="p-1 border border-gray-200 pl-10 text-gray-600">Sale Invoice</td>
                                  <td className="p-1 border border-gray-200 text-right text-gray-500">{saleAmount ? saleAmount.toLocaleString() : '-'}</td>
                                  <td className="p-1 border border-gray-200 text-right text-gray-500">{gpPartner ? gpPartner.toLocaleString() : '-'}</td>
                                  <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                  <td className="p-1 border border-gray-200 text-right">-</td>
                                  <td className="p-1 border border-gray-200 text-right">-</td>
                                  <td className={`p-1 border border-gray-200 text-right ${(gpSelf > 0 ? balanceAfterGpSelf : balanceAfterCosting) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    {(gpSelf > 0 ? balanceAfterGpSelf : balanceAfterCosting).toLocaleString()}
                                  </td>
                                  <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                </tr>
                                
                                {/* Payment Row - Only on last parcel of group */}
                                {showCredit && (
                                  <tr className="bg-green-50/50">
                                    <td className="p-1 border border-gray-200"></td>
                                    <td className="p-1 border border-gray-200 text-xs pl-4">{parcel.date || ''}</td>
                                    <td className="p-1 border border-gray-200 pl-10 text-green-700 font-medium">
                                      {group.payment_mode} (Covers {group.parcel_count} parcels)
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-green-600 font-medium">
                                      {group.payment_amount.toLocaleString()}
                                    </td>
                                    <td className={`p-1 border border-gray-200 text-right font-bold ${(finalBalance + group.payment_amount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {(finalBalance + group.payment_amount)?.toLocaleString()}
                                    </td>
                                    {/* Grp Bal - show on payment row for multi-parcel groups */}
                                    <td className={`p-1 border border-gray-200 text-right font-medium ${
                                      groupBalance !== null 
                                        ? (groupBalance > 0 ? 'text-red-600 bg-red-50' : groupBalance < 0 ? 'text-green-600 bg-green-50' : 'text-gray-500')
                                        : 'text-gray-400'
                                    }`}>
                                      {groupBalance !== null ? groupBalance.toLocaleString() : '-'}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Personal Collection Row (INFO only) - Only show on last parcel if there's personal collection */}
                                {showPersonalColl && (
                                  <tr className="bg-purple-50/50">
                                    <td className="p-1 border border-gray-200"></td>
                                    <td className="p-1 border border-gray-200 text-xs pl-4">{parcel.date || ''}</td>
                                    <td className="p-1 border border-gray-200 pl-10 text-purple-700 font-medium italic">
                                      {group.payment_entry?.description || 'Personal Collection'} (INFO)
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-purple-600 font-medium italic">
                                      {groupPersonalCollection.toLocaleString()}
                                    </td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                                    <td className={`p-1 border border-gray-200 text-right font-bold ${finalBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {finalBalance?.toLocaleString()}
                                    </td>
                                    {/* Grp Bal for personal collection groups - show on last parcel for multi-parcel */}
                                    <td className={`p-1 border border-gray-200 text-right font-medium ${
                                      groupBalance !== null 
                                        ? (groupBalance > 0 ? 'text-red-600 bg-red-50' : groupBalance < 0 ? 'text-green-600 bg-green-50' : 'text-gray-500')
                                        : 'text-gray-400'
                                    }`}>
                                      {groupBalance !== null ? groupBalance.toLocaleString() : '-'}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Closing Balance / Totals Row - LIGHTER for print */}
                    <tr className="bg-gray-200 font-bold">
                      <td colSpan={6} className="p-2 border border-gray-300 text-right">
                        CLOSING
                      </td>
                      <td className="p-2 border border-gray-300 text-right">
                        {accountViewData.totals?.total_debit?.toLocaleString()}
                      </td>
                      <td className="p-2 border border-gray-300 text-right">
                        {accountViewData.totals?.total_credit?.toLocaleString()}
                      </td>
                      <td className={`p-2 border border-gray-300 text-right ${
                        accountViewData.closing_balance >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {accountViewData.closing_balance?.toLocaleString()}
                      </td>
                      {accountViewMode === "payment" && (
                        <td className="p-2 border border-gray-300 text-right text-gray-400">-</td>
                      )}
                    </tr>
                  </tbody>
                </table>
                
                {/* Minimal Footer - no legend for clean print */}
                <div className="bg-gray-50 border-t border-gray-300 px-4 py-2 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>[NI]=No Invoice | [NG]=Neg GP | [P]=Pending | [CO]=Company | + = Owes you | - = You owe</span>
                    <span>PLA</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Partner Ledger Upload Dialog */}
      <Dialog open={showPartnerLedgerUpload} onOpenChange={setShowPartnerLedgerUpload}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <Upload className="w-6 h-6" />
              Partner Ledger Upload
            </DialogTitle>
            <DialogDescription>
              Upload Excel file with partner sales and payment data. Payments will be auto-detected and GP splits calculated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {!partnerLedgerResults ? (
              /* Upload Form */
              <div className="space-y-4">
                {/* Download Template Banner - Two Options */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <FileSpreadsheet className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Download Template</p>
                      <p className="text-sm text-amber-600">Choose the format you prefer</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-2 bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => window.open(`${BACKEND_URL}/api/partner-ledger/template-raw`, '_blank')}
                      data-testid="download-template-raw-btn"
                    >
                      <Download className="w-4 h-4" />
                      Raw Format (Familiar)
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-2 bg-white border-purple-300 text-purple-700 hover:bg-purple-100"
                      onClick={() => window.open(`${BACKEND_URL}/api/partner-ledger/template`, '_blank')}
                      data-testid="download-template-btn"
                    >
                      <Download className="w-4 h-4" />
                      Structured Format
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    • <strong>Raw Format:</strong> Familiar layout with customer address, medicine, quantity columns (recommended for existing partners)
                    <br/>• <strong>Structured Format:</strong> Simplified columns focused on tracking and payments
                  </p>
                </div>

                {/* File Upload */}
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setPartnerLedgerFile(e.target.files[0])}
                    className="hidden"
                    id="partner-ledger-file"
                  />
                  <label htmlFor="partner-ledger-file" className="cursor-pointer">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                    <p className="text-lg font-medium text-slate-700">
                      {partnerLedgerFile ? partnerLedgerFile.name : "Click to select Excel file"}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Supports .xlsx and .xls files
                    </p>
                  </label>
                </div>

                {/* Duplicate Handling Option */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <Label className="text-sm font-medium mb-3 block">If duplicate entries found:</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="duplicate"
                        value="skip"
                        checked={duplicateHandling === "skip"}
                        onChange={() => setDuplicateHandling("skip")}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">Skip existing entries (safe mode)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="duplicate"
                        value="overwrite"
                        checked={duplicateHandling === "overwrite"}
                        onChange={() => setDuplicateHandling("overwrite")}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">Overwrite with new data</span>
                    </label>
                  </div>
                </div>

                {/* Upload Button */}
                <Button
                  onClick={handlePartnerLedgerUpload}
                  disabled={!partnerLedgerFile || partnerLedgerUploading}
                  className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  {partnerLedgerUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing Excel...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload & Analyze
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* Results Review */
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{partnerLedgerResults.sheets_processed}</p>
                    <p className="text-xs text-purple-600">Partners</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center cursor-pointer hover:bg-green-100" onClick={() => setPartnerLedgerStatusFilter(partnerLedgerStatusFilter === 'matched' ? 'all' : 'matched')}>
                    <p className="text-2xl font-bold text-green-700">{partnerLedgerStatusCounts.matched}</p>
                    <p className="text-xs text-green-600">Matched ✓</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center cursor-pointer hover:bg-orange-100" onClick={() => setPartnerLedgerStatusFilter(partnerLedgerStatusFilter === 'no_payment' ? 'all' : 'no_payment')}>
                    <p className="text-2xl font-bold text-orange-700">{partnerLedgerStatusCounts.no_payment}</p>
                    <p className="text-xs text-orange-600">No Payment</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center cursor-pointer hover:bg-yellow-100" onClick={() => setPartnerLedgerStatusFilter(partnerLedgerStatusFilter === 'unknown_receiver' ? 'all' : 'unknown_receiver')}>
                    <p className="text-2xl font-bold text-yellow-700">{partnerLedgerStatusCounts.unknown_receiver}</p>
                    <p className="text-xs text-yellow-600">Unknown Recv</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center cursor-pointer hover:bg-red-100" onClick={() => setPartnerLedgerStatusFilter(partnerLedgerStatusFilter === 'unknown_tracking' ? 'all' : 'unknown_tracking')}>
                    <p className="text-2xl font-bold text-red-700">{partnerLedgerStatusCounts.unknown_tracking}</p>
                    <p className="text-xs text-red-600">Unknown Track</p>
                  </div>
                  <div
                    className={`border rounded-lg p-3 text-center cursor-pointer transition-colors ${
                      partnerLedgerStatusFilter === 'esr_reship'
                        ? 'bg-pink-100 border-pink-400 ring-2 ring-pink-300'
                        : 'bg-pink-50 border-pink-200 hover:bg-pink-100'
                    }`}
                    onClick={() => setPartnerLedgerStatusFilter(partnerLedgerStatusFilter === 'esr_reship' ? 'all' : 'esr_reship')}
                    data-testid="esr-reship-filter-card"
                    title="Free reships paid via ESR (zero-amount entries)"
                  >
                    <p className="text-2xl font-bold text-pink-700">{partnerLedgerStatusCounts.esr_reship || 0}</p>
                    <p className="text-xs text-pink-700">ESR Reship</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">
                      {partnerLedgerResults.partners?.reduce((sum, p) => sum + (p.advance_payments?.length || 0), 0)}
                    </p>
                    <p className="text-xs text-blue-600">Advance Payments</p>
                  </div>
                </div>

                {/* Parser Anomalies — red-flag warnings from the upload parser */}
                {partnerLedgerResults.parser_anomalies?.length > 0 && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4" data-testid="parser-anomalies-card">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Parser Warnings ({partnerLedgerResults.parser_anomalies.length})
                      </h4>
                      <span className="text-[10px] uppercase tracking-wider font-bold text-red-700 bg-red-200 px-2 py-0.5 rounded">
                        Needs attention
                      </span>
                    </div>
                    <p className="text-xs text-red-700 mb-3">
                      The parser detected issues that may indicate misallocated payments.
                      Review the sheet, correct the source, and re-upload.
                    </p>
                    <div className="space-y-1.5 max-h-60 overflow-auto">
                      {partnerLedgerResults.parser_anomalies.map((a, idx) => (
                        <div
                          key={idx}
                          className="bg-white border border-red-200 rounded px-3 py-2 text-xs flex items-start gap-2"
                          data-testid={`parser-anomaly-${idx}`}
                        >
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold text-[10px] uppercase whitespace-nowrap">
                            {a.type === "advance_ref_orphan" ? "Orphan Advance" : a.type}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-800">{a.message}</div>
                            <div className="text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                              {a.partner_code && <span>Partner: <b>{a.partner_code}</b></span>}
                              {a.tracking && <span>Tracking: <b className="font-mono">{a.tracking}</b></span>}
                              {a.advance_ref && <span>Ref: <b className="font-mono">{a.advance_ref}</b></span>}
                              {a.row_num && <span>Row: <b>{a.row_num}</b></span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Advance Payments Summary - Show if any detected */}
                {partnerLedgerResults.partners?.some(p => p.advance_payments?.length > 0) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      Advance Payments Detected
                    </h4>
                    <div className="space-y-2">
                      {partnerLedgerResults.partners?.flatMap(p => 
                        (p.advance_payments || []).map(adv => ({...adv, partner_code: p.partner_code}))
                      ).map((adv, idx) => {
                        const balance = (adv.amount || 0) - (adv.utilized || 0);
                        return (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-blue-100 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <Badge className="bg-blue-100 text-blue-700 font-mono">{adv.ref}</Badge>
                              <span className="text-sm text-slate-600">{adv.customer || adv.partner_code}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="text-right">
                                <span className="text-slate-500">Amount: </span>
                                <span className="font-semibold text-green-600">₹{(adv.amount || 0).toLocaleString()}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-slate-500">Used: </span>
                                <span className="font-semibold text-orange-600">₹{(adv.utilized || 0).toLocaleString()}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-slate-500">Balance: </span>
                                <span className={`font-bold ${balance > 0 ? 'text-blue-600' : balance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  ₹{Math.abs(balance).toLocaleString()}{balance < 0 ? ' (Over)' : ''}
                                </span>
                              </div>
                              <Badge variant="secondary" className="bg-slate-100">
                                {adv.parcels?.length || 0} parcels
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Status Filter Info */}
                {partnerLedgerStatusFilter !== 'all' && (
                  <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <span className="text-sm text-blue-700">
                      Filtering by: <strong>{partnerLedgerStatusFilter.replace('_', ' ').toUpperCase()}</strong>
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setPartnerLedgerStatusFilter('all')} className="text-blue-600 hover:text-blue-800">
                      <X className="w-4 h-4 mr-1" /> Clear Filter
                    </Button>
                  </div>
                )}

                {/* Partner List */}
                <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                  {partnerLedgerResults.partners?.filter(partner => {
                    if (partnerLedgerStatusFilter === 'all') return true;
                    if (partnerLedgerStatusFilter === 'esr_reship') {
                      return partner.entries?.some(e =>
                        e.is_reship && (e.payment_mode || "").toUpperCase() === "ESR"
                      );
                    }
                    return partner.entries?.some(entry => entry.status === partnerLedgerStatusFilter);
                  }).map((partner, pIdx) => (
                    <div key={partner.partner_code} className="bg-white">
                      {/* Partner Header */}
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                        onClick={() => togglePartnerExpand(partner.partner_code)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedPartners.has(partner.partner_code) ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <div>
                            <p className="font-semibold text-slate-800">{partner.partner_code}</p>
                            <p className="text-xs text-slate-500">
                              {getFilteredEntries(partner.entries || []).length} entries {partnerLedgerStatusFilter !== 'all' ? `(${partnerLedgerStatusFilter})` : ''} • {partner.tracking_matches} matched
                              {partner.gp_ratios && (
                                <span className="ml-2 text-purple-600">
                                  (Self: {Math.round(partner.gp_ratios.self * 100)}%
                                  {partner.gp_ratios.ratio_2 > 0 && `, ${partner.gp_ratios.share_2_name}: ${Math.round(partner.gp_ratios.ratio_2 * 100)}%`}
                                  {partner.gp_ratios.ratio_3 > 0 && `, ${partner.gp_ratios.share_3_name}: ${Math.round(partner.gp_ratios.ratio_3 * 100)}%`})
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={partner.ledger_found ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}>
                            {partner.ledger_found ? "Ledger Found" : "New Partner"}
                          </Badge>
                          <span className="text-sm text-slate-600">
                            {getSelectedCount(partner.partner_code, partner.entries)}/{partner.entries.length} selected
                          </span>
                        </div>
                      </div>

                      {/* Entries Table (Expanded) */}
                      {expandedPartners.has(partner.partner_code) && partner.entries.length > 0 && (
                        <div className="border-t bg-slate-50 p-3">
                          {/* Workflow Status Summary */}
                          {workflowSummary?.by_partner?.[partner.partner_code] && (
                            <div className="flex items-center gap-3 mb-2 text-xs">
                              <span className="text-slate-500">Workflow:</span>
                              <Badge variant="outline" className="bg-slate-100">{workflowSummary.by_partner[partner.partner_code].pending || 0} Pending</Badge>
                              <Badge variant="outline" className="bg-blue-100 text-blue-700">{workflowSummary.by_partner[partner.partner_code].verified || 0} Verified</Badge>
                              <Badge variant="outline" className="bg-green-100 text-green-700">{workflowSummary.by_partner[partner.partner_code].approved || 0} Approved</Badge>
                              <Badge variant="outline" className="bg-red-100 text-red-700">{workflowSummary.by_partner[partner.partner_code].rejected || 0} Rejected</Badge>
                            </div>
                          )}
                          
                          {/* Toolbar */}
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleAllEntries(partner.partner_code, getWorkflowFilteredEntries(partner.entries || []), true)}
                                className="text-xs text-green-600 hover:text-green-700"
                              >
                                <CheckSquare className="w-3 h-3 mr-1" /> Select All
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleAllEntries(partner.partner_code, getWorkflowFilteredEntries(partner.entries || []), false)}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                <Square className="w-3 h-3 mr-1" /> Deselect All
                              </Button>
                              
                              {/* Workflow Filter */}
                              <Select value={workflowFilter} onValueChange={setWorkflowFilter}>
                                <SelectTrigger className="h-7 w-[120px] text-xs">
                                  <SelectValue placeholder="Filter..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="verified">Verified</SelectItem>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Edit Button */}
                              {canEditLimited && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowBulkEditDialog(true)}
                                  className="text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                                >
                                  <Edit className="w-3 h-3 mr-1" /> Edit Mode
                                </Button>
                              )}
                              
                              {/* Verify Buttons */}
                              {canVerify && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleVerifyEntries("verify")}
                                    className="text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Verify
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleVerifyEntries("unverify")}
                                    className="text-xs text-slate-500 hover:text-slate-700"
                                  >
                                    Unverify
                                  </Button>
                                </>
                              )}
                              
                              {/* Approve/Reject Buttons (Admin only) */}
                              {canApprove && (
                                <>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleApproveEntries("approve")}
                                    className="text-xs bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowRejectDialog(true)}
                                    className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                                  >
                                    <X className="w-3 h-3 mr-1" /> Reject
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSyncToSales}
                                    className="text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
                                    title="Sync approved entries to Sales & Profit"
                                  >
                                    <Download className="w-3 h-3 mr-1" /> Sync to Sales
                                  </Button>
                                </>
                              )}
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setShowAuditLogDialog(true);
                                  loadAuditLogs();
                                }}
                                className="text-xs text-slate-600 hover:text-slate-800"
                              >
                                <List className="w-3 h-3 mr-1" /> Log
                              </Button>
                            </div>
                          </div>
                          
                          <table className="w-full text-sm">
                            <thead className="bg-slate-200">
                              <tr>
                                <th className="px-2 py-1.5 text-left w-8"></th>
                                <th className="px-2 py-1.5 text-left">Customer</th>
                                <th className="px-2 py-1.5 text-left">Tracking</th>
                                <th className="px-2 py-1.5 text-right">Sales (INR)</th>
                                <th className="px-2 py-1.5 text-right">Payment</th>
                                <th className="px-2 py-1.5 text-center">Mode <Edit className="w-3 h-3 inline text-blue-500" /></th>
                                <th className="px-2 py-1.5 text-center">Type</th>
                                <th className="px-2 py-1.5 text-center">Adv Ref</th>
                                <th className="px-2 py-1.5 text-center">Status</th>
                                <th className="px-2 py-1.5 text-center">Workflow</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getWorkflowFilteredEntries(partner.entries || []).map((entry, eIdx) => {
                                // Find original index for selection state
                                const originalIdx = partner.entries?.findIndex(e => e.row_num === entry.row_num && e.tracking === entry.tracking) ?? eIdx;
                                const isEditingMode = editingCell?.partnerCode === partner.partner_code && 
                                                      editingCell?.entryIndex === originalIdx && 
                                                      editingCell?.field === "payment_mode";
                                const workflowStatus = entry.workflow_status || "pending";
                                return (
                                <tr 
                                  key={eIdx} 
                                  className={`border-b hover:bg-white ${selectedPartnerEntries[partner.partner_code]?.[originalIdx] ? 'bg-purple-50/50' : ''} ${entry.status === 'no_payment' ? 'bg-orange-50/30' : ''} ${entry.status === 'unknown_receiver' ? 'bg-yellow-50/30' : ''} ${entry.status === 'unknown_tracking' ? 'bg-red-50/30' : ''} ${entry.edited ? 'ring-1 ring-yellow-400' : ''} ${workflowStatus === 'approved' ? 'bg-green-50/30' : ''} ${workflowStatus === 'rejected' ? 'bg-red-50/30 opacity-60' : ''}`}
                                >
                                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => toggleEntrySelection(partner.partner_code, originalIdx)}>
                                    {selectedPartnerEntries[partner.partner_code]?.[originalIdx] ? (
                                      <CheckSquare className="w-4 h-4 text-purple-600" />
                                    ) : (
                                      <Square className="w-4 h-4 text-slate-400" />
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs max-w-[120px] truncate" title={entry.customer_name || ''}>
                                    {entry.customer_name || <span className="text-slate-300">-</span>}
                                  </td>
                                  <td className="px-2 py-1.5 font-mono text-xs">
                                    {entry.tracking || <span className="text-slate-400 italic">No tracking</span>}
                                    {entry.tracking_matched && <CheckCircle className="w-3 h-3 text-green-500 inline ml-1" />}
                                    {entry.is_advance_entry && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1 rounded">ADV</span>}
                                    {entry.is_reship && (entry.payment_mode || "").toUpperCase() === "ESR" && (
                                      <span className="ml-1 text-[10px] bg-pink-100 text-pink-700 border border-pink-200 px-1 rounded font-medium" title="Free reship paid via ESR (zero-cost write-off)">
                                        ESR RESHIP
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-medium text-purple-700">
                                    {entry.sales_amount > 0 ? `₹${entry.sales_amount?.toLocaleString()}` : <span className="text-slate-300">-</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-medium">
                                    {entry.amount > 0 ? `₹${entry.amount?.toLocaleString()}` : <span className="text-orange-500">-</span>}
                                  </td>
                                  <td 
                                    className="px-2 py-1.5 text-center cursor-pointer hover:bg-blue-50 transition-colors"
                                    onDoubleClick={(e) => {
                                      e.stopPropagation();
                                      handleCellDoubleClick(partner.partner_code, originalIdx, "payment_mode", entry.payment_mode);
                                    }}
                                    title="Double-click to edit"
                                  >
                                    {isEditingMode ? (
                                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                        <Select value={editingValue} onValueChange={(val) => setEditingValue(val)}>
                                          <SelectTrigger className="h-7 w-[140px] text-xs">
                                            <SelectValue placeholder="Select mode" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {PAYMENT_MODES.map(mode => (
                                              <SelectItem key={mode.value} value={mode.value}>
                                                <span className={mode.isCompany ? "text-green-700" : "text-red-700"}>
                                                  {mode.label}
                                                </span>
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleCellEditSave}>
                                          <CheckCircle className="w-4 h-4 text-green-600" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleCellEditCancel}>
                                          <X className="w-4 h-4 text-red-600" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <Badge 
                                        variant="outline" 
                                        className={`text-xs ${entry.edited ? 'bg-yellow-100 border-yellow-400' : ''}`}
                                        title={entry.edited ? `Edited by ${entry.edited_by}` : "Double-click to edit"}
                                      >
                                        {entry.payment_mode || "Unknown"}
                                        {entry.edited && <span className="ml-1 text-yellow-600">✎</span>}
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {entry.status === 'no_payment' ? (
                                      <span className="text-orange-500 text-xs font-medium">-</span>
                                    ) : entry.is_company_payment ? (
                                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                                        <CreditCard className="w-3 h-3" /> Credit
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                                        <Wallet className="w-3 h-3" /> Debit
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {entry.advance_ref ? (
                                      <Badge className="bg-blue-100 text-blue-700 text-[10px] font-mono">
                                        {entry.advance_ref}
                                      </Badge>
                                    ) : (
                                      <span className="text-slate-300">-</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {entry.status === 'matched' && (
                                      <Badge className="bg-green-100 text-green-700 text-xs">Matched</Badge>
                                    )}
                                    {entry.status === 'no_payment' && (
                                      <Badge className="bg-orange-100 text-orange-700 text-xs">No Payment</Badge>
                                    )}
                                    {entry.status === 'unknown_receiver' && (
                                      <Badge className="bg-yellow-100 text-yellow-700 text-xs">Unknown Recv</Badge>
                                    )}
                                    {entry.status === 'unknown_tracking' && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">Unknown Track</Badge>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {workflowStatus === 'pending' && (
                                      <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600">Pending</Badge>
                                    )}
                                    {workflowStatus === 'verified' && (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs" title={`Verified by ${entry.verified_by}`}>
                                        ✓ Verified
                                      </Badge>
                                    )}
                                    {workflowStatus === 'approved' && (
                                      <Badge className="bg-green-100 text-green-700 text-xs" title={`Approved by ${entry.approved_by}`}>
                                        ✓✓ Approved
                                      </Badge>
                                    )}
                                    {workflowStatus === 'rejected' && (
                                      <Badge className="bg-red-100 text-red-700 text-xs" title={entry.reject_reason || `Rejected by ${entry.rejected_by}`}>
                                        ✗ Rejected
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* GP Split Preview */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    GP Split Preview
                  </p>
                  <p className="text-sm text-purple-600">
                    When processed, entries will automatically create GP split ledger entries based on each partner&apos;s configured ratios.
                    <br />
                    <span className="font-medium">Self portion = DEBIT</span> (partner owes us) | 
                    <span className="font-medium ml-1">Partner shares = CREDIT</span> (we owe them)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Dialog Footer */}
          <div className="border-t pt-4 flex items-center justify-between">
            {partnerLedgerResults ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setPartnerLedgerResults(null);
                    setPartnerLedgerFile(null);
                    setSelectedPartnerEntries({});
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Upload
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowPartnerLedgerUpload(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessPartnerLedger}
                    disabled={partnerLedgerProcessing}
                    className="gap-2 bg-purple-600 hover:bg-purple-700"
                  >
                    {partnerLedgerProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Process Selected Entries
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowPartnerLedgerUpload(false)} className="ml-auto">
                Cancel
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Advance Payments View */}
      {mainView === "advances" && (
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  Advance Payments Dashboard
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Track advance payments from customers and their utilization
                </p>
              </div>
              <div className="flex items-center gap-2">
                {userRole === "super_admin" && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="hydrate-advance-invoices-btn"
                    title="Permanently fill missing parcel invoice amounts in the stored data (uses shipment_orders.total_cost)"
                    onClick={async () => {
                      const tk = localStorage.getItem("token");
                      // eslint-disable-next-line no-alert
                      const pw = window.prompt("Enter admin password to hydrate missing parcel invoices (writes to DB):");
                      if (!pw) return;
                      try {
                        toast.info("Scanning for parcels missing invoice amounts…");
                        const drRes = await fetch(`${BACKEND_URL}/api/admin/hydrate-advance-parcel-invoices`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${tk}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ password: pw, dry_run: true }),
                        });
                        const drJ = await drRes.json();
                        if (!drRes.ok) {
                          toast.error(drJ.detail || `Preview failed (HTTP ${drRes.status})`);
                          return;
                        }
                        if (drJ.parcels_hydrated === 0) {
                          toast.success("✅ All parcels already have invoice amounts — nothing to hydrate.");
                          return;
                        }
                        // eslint-disable-next-line no-alert
                        const ok = window.confirm(
                          `Hydrate ${drJ.parcels_hydrated} parcel(s) across ${drJ.uploads_modified} upload(s)?\n\n` +
                          `+₹${(drJ.total_invoice_value_added || 0).toLocaleString()} invoice value will be filled in.\n` +
                          `${drJ.advances_recomputed} advance(s) will have utilized/balance/status recomputed.\n` +
                          `${drJ.parcels_still_missing_cost} parcel(s) have no matching shipment_orders row — those will stay 0.\n\n` +
                          `This rewrites stored partner_ledger_uploads documents. Continue?`
                        );
                        if (!ok) return;
                        toast.info("Hydrating…");
                        const rr = await fetch(`${BACKEND_URL}/api/admin/hydrate-advance-parcel-invoices`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${tk}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ password: pw, dry_run: false }),
                        });
                        const rj = await rr.json();
                        if (!rr.ok) {
                          toast.error(rj.detail || `Hydrate failed (HTTP ${rr.status})`);
                          return;
                        }
                        toast.success(
                          `✅ Hydrated ${rj.parcels_hydrated} parcels · +₹${(rj.total_invoice_value_added || 0).toLocaleString()} · ${rj.advances_recomputed} advances recomputed.`,
                          { duration: 8000 }
                        );
                        await fetchAdvancePayments();
                      } catch (e) {
                        toast.error(`Error: ${e.message}`);
                      }
                    }}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    🔧 Hydrate Invoices
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={fetchAdvancePayments} disabled={advancePaymentsLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${advancePaymentsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {advancePaymentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : advancePayments.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No Advance Payments Found</p>
                <p className="text-sm mt-1">Upload partner ledger sheets with advance payment entries to see them here.</p>
              </div>
            ) : (() => {
              // Compute filtered list
              const fromIso = advanceDateFrom ? toISO(advanceDateFrom) : "";
              const toIsoStr = advanceDateTo ? toISO(advanceDateTo) : "";
              const search = (advanceSearchTerm || "").trim().toLowerCase();
              // Auto-adjust tolerance: any residual ≤ ₹20 is treated as Fully Used
              // (rounding / self-discount), so the row no longer shows under
              // Credit-Remaining or Debit-Remaining filters.
              const ROUND_TOL = 20.0;
              const statusOf = (adv) => {
                const balance = (adv.amount || 0) - (adv.utilized || 0);
                if (Math.abs(balance) <= ROUND_TOL) return "fully_used";
                return balance > 0 ? "credit_remaining" : "debit_remaining";
              };
              // When the user explicitly picks "Fully Used", we must show those
              // rows even though the default toggle hides them.
              const effectiveHideFullyUsed =
                advanceHideFullyUsed && advanceStatusFilter !== "fully_used";

              const filtered = advancePayments.filter((adv) => {
                const status = statusOf(adv);
                if (effectiveHideFullyUsed && status === "fully_used") return false;
                if (advanceStatusFilter !== "all" && status !== advanceStatusFilter) return false;
                if (fromIso || toIsoStr) {
                  const advIso = toISO(adv.date);
                  if (!advIso) return false;
                  if (fromIso && advIso < fromIso) return false;
                  if (toIsoStr && advIso > toIsoStr) return false;
                }
                if (search) {
                  const hay = `${adv.ref || ''} ${adv.customer || ''} ${adv.partner_code || ''} ${(adv.parcels || []).map(p => p.tracking || '').join(' ')}`.toLowerCase();
                  if (!hay.includes(search)) return false;
                }
                return true;
              });

              // Counts for filter pills (same tolerance rule)
              const counts = advancePayments.reduce((acc, adv) => {
                const status = statusOf(adv);
                acc[status] = (acc[status] || 0) + 1;
                acc.all++;
                return acc;
              }, { all: 0, fully_used: 0, credit_remaining: 0, debit_remaining: 0 });

              // Map of customer-name (normalized) -> count of advance refs.
              // Drives the "Customer Ledger Book" icon — shown only when a
              // customer has 2+ separate advance references so finance can
              // open a merged Dr/Cr view across all of them.
              const customerCount = advancePayments.reduce((acc, adv) => {
                const c = (adv.customer || "").trim().toUpperCase().replace(/\s+/g, " ");
                if (!c) return acc;
                acc[c] = (acc[c] || 0) + 1;
                return acc;
              }, {});

              const totals = filtered.reduce((acc, adv) => {
                const balance = (adv.amount || 0) - (adv.utilized || 0);
                acc.amount += adv.amount || 0;
                acc.utilized += adv.utilized || 0;
                // Only count balances above the auto-adjust tolerance toward
                // Credit/Debit totals — sub-₹20 residues are silently absorbed.
                if (Math.abs(balance) > ROUND_TOL) {
                  if (balance > 0) acc.creditBalance += balance;
                  else acc.debitBalance += Math.abs(balance);
                }
                acc.parcels += (adv.parcels || []).length;
                return acc;
              }, { amount: 0, utilized: 0, creditBalance: 0, debitBalance: 0, parcels: 0 });

              return (
                <>
                  {/* Filter Bar */}
                  <div className="mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60 space-y-3">
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-slate-600">From Date</label>
                        <DMYDateInput
                          value={advanceDateFrom}
                          onChange={(e) => setAdvanceDateFrom(e.target.value)}
                          placeholder="DD/MM/YYYY"
                          className="w-36 h-9"
                          data-testid="advance-date-from"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-slate-600">To Date</label>
                        <DMYDateInput
                          value={advanceDateTo}
                          onChange={(e) => setAdvanceDateTo(e.target.value)}
                          placeholder="DD/MM/YYYY"
                          className="w-36 h-9"
                          data-testid="advance-date-to"
                        />
                      </div>
                      <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                        <label className="text-xs font-semibold text-slate-600">Search (Ref / Customer / Tracking)</label>
                        <Input
                          value={advanceSearchTerm}
                          onChange={(e) => setAdvanceSearchTerm(e.target.value)}
                          placeholder="Type to search..."
                          className="h-9"
                          data-testid="advance-search"
                        />
                      </div>
                      <Button
                        variant={advanceHideFullyUsed ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAdvanceHideFullyUsed(v => !v)}
                        className={advanceHideFullyUsed ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                        data-testid="advance-hide-fully-used-btn"
                      >
                        {advanceHideFullyUsed ? "Showing Active Only" : "Hide Fully Used"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Build Excel from `filtered` advances
                          const summaryRows = filtered.map((adv) => {
                            const balance = (adv.amount || 0) - (adv.utilized || 0);
                            const statusKey = statusOf(adv);
                            const status = statusKey === "fully_used" ? "Fully Used" : statusKey === "credit_remaining" ? "Credit Remaining" : "Debit Remaining";
                            const autoAdjustedAmt = (statusKey === "fully_used" && balance !== 0) ? Math.abs(balance) : 0;
                            return {
                              "Date": formatDMY(adv.date),
                              "Ref ID": adv.ref || "",
                              "Customer": adv.customer || "",
                              "Partner": adv.partner_code || "",
                              "Mode": adv.mode || "",
                              "Total Amount": adv.amount || 0,
                              "Utilized": adv.utilized || 0,
                              "Balance": balance,
                              "Direction": balance > 0 ? "Cr" : balance < 0 ? "Dr" : "",
                              "Status": status,
                              "Auto-Adjusted (≤₹20)": autoAdjustedAmt,
                              "Parcels Count": (adv.parcels || []).length,
                              "Parcel Numbers": (adv.parcels || []).map(p => p.tracking).filter(Boolean).join(", "),
                            };
                          });
                          const parcelRows = filtered.flatMap((adv) =>
                            (adv.parcels || []).map((p) => ({
                              "Advance Ref": adv.ref || "",
                              "Advance Date": formatDMY(adv.date),
                              "Advance Customer": adv.customer || "",
                              "Partner": adv.partner_code || "",
                              "Tracking": p.tracking || "",
                              "Parcel Customer": p.customer || "",
                              "Parcel Date": formatDMY(p.date),
                              "Invoice Amount": p.invoice || p.amount || 0,
                            }))
                          );
                          const TOL = 20.0;
                          const totalsRow = [
                            ["Total Advances", filtered.length],
                            ["Total Amount", filtered.reduce((s, a) => s + (a.amount || 0), 0)],
                            ["Total Utilized", filtered.reduce((s, a) => s + (a.utilized || 0), 0)],
                            ["Total Credit Balance (above ₹20)", filtered.reduce((s, a) => { const b = (a.amount || 0) - (a.utilized || 0); return s + (b > TOL ? b : 0); }, 0)],
                            ["Total Debit Balance (above ₹20)", filtered.reduce((s, a) => { const b = (a.amount || 0) - (a.utilized || 0); return s + (b < -TOL ? Math.abs(b) : 0); }, 0)],
                            ["Total Auto-Adjusted (≤ ₹20)", filtered.reduce((s, a) => { const b = (a.amount || 0) - (a.utilized || 0); return s + (Math.abs(b) > 0 && Math.abs(b) <= TOL ? Math.abs(b) : 0); }, 0)],
                            ["Total Parcels", filtered.reduce((s, a) => s + ((a.parcels || []).length), 0)],
                            [],
                            ["Filters Applied"],
                            ["From Date", advanceDateFrom || "(none)"],
                            ["To Date", advanceDateTo || "(none)"],
                            ["Status", advanceStatusFilter],
                            ["Hide Fully Used", advanceHideFullyUsed ? "Yes" : "No"],
                            ["Search", advanceSearchTerm || "(none)"],
                            ["Generated At", formatDMY(new Date()) + " " + new Date().toLocaleTimeString()],
                          ];
                          const wb = XLSX.utils.book_new();
                          const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
                          XLSX.utils.book_append_sheet(wb, wsSummary, "Advances");
                          if (parcelRows.length > 0) {
                            const wsParcels = XLSX.utils.json_to_sheet(parcelRows);
                            XLSX.utils.book_append_sheet(wb, wsParcels, "Parcel Details");
                          }
                          const wsMeta = XLSX.utils.aoa_to_sheet(totalsRow);
                          XLSX.utils.book_append_sheet(wb, wsMeta, "Totals & Filters");
                          const fname = `advance_payments_${formatDMY(new Date(), { compact: true })}.xlsx`;
                          XLSX.writeFile(wb, fname);
                        }}
                        disabled={filtered.length === 0}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="advance-export-excel-btn"
                      >
                        <Download className="w-4 h-4 mr-1.5" />
                        Export Excel ({filtered.length})
                      </Button>
                      {(advanceDateFrom || advanceDateTo || advanceStatusFilter !== "all" || advanceHideFullyUsed || advanceSearchTerm) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAdvanceDateFrom("");
                            setAdvanceDateTo("");
                            setAdvanceStatusFilter("all");
                            setAdvanceHideFullyUsed(false);
                            setAdvanceSearchTerm("");
                          }}
                          className="text-slate-500 hover:text-slate-700"
                          data-testid="advance-clear-filters-btn"
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                    {/* Status Pills */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "all", label: "All", color: "bg-slate-700 text-white", inactive: "bg-white text-slate-600 border border-slate-300" },
                        { key: "credit_remaining", label: "Credit Remaining", color: "bg-blue-600 text-white", inactive: "bg-blue-50 text-blue-700 border border-blue-200" },
                        { key: "debit_remaining", label: "Debit Remaining", color: "bg-red-600 text-white", inactive: "bg-red-50 text-red-700 border border-red-200" },
                        { key: "fully_used", label: "Fully Used", color: "bg-emerald-600 text-white", inactive: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
                      ].map(pill => (
                        <button
                          key={pill.key}
                          onClick={() => setAdvanceStatusFilter(pill.key)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${advanceStatusFilter === pill.key ? pill.color : pill.inactive}`}
                          data-testid={`advance-pill-${pill.key}`}
                        >
                          {pill.label} <span className="opacity-80">({counts[pill.key] || 0})</span>
                        </button>
                      ))}
                    </div>
                    {/* Totals row */}
                    <div className="flex flex-wrap gap-4 text-xs pt-2 border-t border-slate-200">
                      <span className="text-slate-500">Showing <b className="text-slate-800">{filtered.length}</b> of {advancePayments.length}</span>
                      <span className="text-slate-500">Total Advance: <b className="text-slate-800">₹{totals.amount.toLocaleString()}</b></span>
                      <span className="text-slate-500">Utilized: <b className="text-orange-600">₹{totals.utilized.toLocaleString()}</b></span>
                      <span className="text-slate-500">Credit Balance: <b className="text-blue-600">₹{totals.creditBalance.toLocaleString()}</b></span>
                      <span className="text-slate-500">Debit Balance: <b className="text-red-600">₹{totals.debitBalance.toLocaleString()}</b></span>
                      <span className="text-slate-500">Parcels: <b className="text-slate-800">{totals.parcels}</b></span>
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="text-center py-10 text-slate-500 border border-dashed border-slate-200 rounded-lg">
                      <p className="font-medium">No advances match the current filters</p>
                      <p className="text-xs mt-1">Try adjusting date range, status or search term.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="advance-payments-table">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold">Date</th>
                            <th className="px-3 py-2 text-left font-semibold">Ref ID</th>
                            <th className="px-3 py-2 text-left font-semibold">Customer</th>
                            <th className="px-3 py-2 text-left font-semibold">Partner</th>
                            <th className="px-3 py-2 text-right font-semibold">Total Amount</th>
                            <th className="px-3 py-2 text-right font-semibold">Utilized</th>
                            <th className="px-3 py-2 text-right font-semibold">Balance</th>
                            <th className="px-3 py-2 text-left font-semibold">Parcel Numbers</th>
                            <th className="px-3 py-2 text-center font-semibold">Status</th>
                            <th className="px-3 py-2 text-center font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((adv, idx) => {
                            const balance = (adv.amount || 0) - (adv.utilized || 0);
                            const status = statusOf(adv);
                            // Was this row auto-adjusted as discount? (sub-₹20 residue absorbed)
                            const autoAdjusted = status === "fully_used" && balance !== 0;
                            const parcels = adv.parcels || [];
                            const parcelCount = parcels.length;
                            const showCount = 2;
                            const trackingPreview = parcels.slice(0, showCount).map(p => p.tracking).filter(Boolean);
                            const moreCount = Math.max(0, parcelCount - trackingPreview.length);
                            return (
                              <React.Fragment key={adv.ref || idx}>
                                <tr className="border-b border-slate-100 hover:bg-blue-50 transition-colors">
                                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatDMY(adv.date)}</td>
                                  <td className="px-3 py-2 font-mono text-blue-600 font-medium">{adv.ref}</td>
                                  <td className="px-3 py-2">{adv.customer || '-'}</td>
                                  <td className="px-3 py-2">{adv.partner_code || '-'}</td>
                                  <td className="px-3 py-2 text-right font-medium text-green-600">₹{(adv.amount || 0).toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right font-medium text-orange-600">₹{(adv.utilized || 0).toLocaleString()}</td>
                                  <td className={`px-3 py-2 text-right font-bold ${balance > 0 ? 'text-blue-600' : balance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                    ₹{Math.abs(balance).toLocaleString()}{balance < 0 ? ' (Dr)' : balance > 0 ? ' (Cr)' : ''}
                                  </td>
                                  <td className="px-3 py-2">
                                    {parcelCount === 0 ? (
                                      <span className="text-xs text-slate-400 italic">No parcels linked</span>
                                    ) : (
                                      <div className="flex flex-wrap gap-1 items-center">
                                        {trackingPreview.map((tk, i) => (
                                          <span
                                            key={i}
                                            className="inline-flex items-center px-2 py-0.5 rounded bg-blue-600 text-white text-[11px] font-mono font-semibold"
                                            data-testid={`advance-parcel-${adv.ref}-${i}`}
                                          >
                                            {tk}
                                          </span>
                                        ))}
                                        {moreCount > 0 && (
                                          <button
                                            type="button"
                                            onClick={() => setSelectedAdvanceRef(selectedAdvanceRef === adv.ref ? null : adv.ref)}
                                            className="inline-flex items-center px-2 py-0.5 rounded bg-slate-700 text-white text-[11px] font-semibold hover:bg-slate-900"
                                            data-testid={`advance-parcel-more-${adv.ref}`}
                                          >
                                            +{moreCount} more
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {status === "fully_used" && (
                                      <div className="inline-flex flex-col items-center gap-0.5">
                                        <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">Fully Used</Badge>
                                        {autoAdjusted && (
                                          <span
                                            className="text-[9px] text-emerald-700 italic"
                                            title={`Residual ₹${Math.abs(balance).toFixed(2)} auto-adjusted as discount (≤ ₹20 tolerance)`}
                                            data-testid={`advance-auto-adjusted-${adv.ref}`}
                                          >
                                            auto-adjusted ₹{Math.abs(balance).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {status === "credit_remaining" && <Badge className="bg-blue-600 text-white hover:bg-blue-700">Credit Remaining</Badge>}
                                    {status === "debit_remaining" && <Badge className="bg-red-600 text-white hover:bg-red-700">Debit Remaining</Badge>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="inline-flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => setSelectedAdvanceRef(selectedAdvanceRef === adv.ref ? null : adv.ref)}
                                        data-testid={`advance-toggle-${adv.ref}`}
                                      >
                                        {selectedAdvanceRef === adv.ref ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          // ref alone is NOT unique — multiple partners can have
                                          // ref="BOB" / "ESR" / "PMT-XYZ". Pass customer + amount +
                                          // partner_code + upload_id to guarantee the eye-icon
                                          // opens the EXACT advance the user clicked.
                                          const qs = new URLSearchParams({ ref: adv.ref || "" });
                                          if (adv.customer) qs.append("customer", adv.customer);
                                          if (adv.amount != null) qs.append("amount", String(adv.amount));
                                          if (adv.partner_code) qs.append("partner_code", adv.partner_code);
                                          if (adv.upload_id) qs.append("upload_id", adv.upload_id);
                                          window.open(`/advance-ledger?${qs.toString()}`, "_blank", "noopener");
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                        title="View Dr/Cr ledger for this advance (opens in new tab) — supports print & Excel export"
                                        data-testid={`advance-view-ledger-${adv.ref}`}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      {(() => {
                                        const cnorm = (adv.customer || "").trim().toUpperCase().replace(/\s+/g, " ");
                                        if (!cnorm || (customerCount[cnorm] || 0) < 2) return null;
                                        return (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              window.open(`/advance-ledger?customer=${encodeURIComponent(adv.customer)}`, "_blank", "noopener");
                                            }}
                                            className="text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                                            title={`Customer Ledger Book — merged Dr/Cr view across ${customerCount[cnorm]} advance refs of ${adv.customer}`}
                                            data-testid={`advance-customer-book-${adv.ref}`}
                                          >
                                            <BookOpen className="w-4 h-4" />
                                          </Button>
                                        );
                                      })()}
                                      {userRole === "super_admin" && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setDeleteAdvanceTarget(adv);
                                            setDeleteAdvancePassword("");
                                            setDeleteAdvanceReason("");
                                          }}
                                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                          title="Delete advance payment (admin only)"
                                          data-testid={`advance-delete-${adv.ref}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {selectedAdvanceRef === adv.ref && parcelCount > 0 && (
                                  <tr>
                                    <td colSpan={10} className="bg-slate-50 p-4">
                                      <div className="text-xs font-semibold text-slate-700 mb-2">All Linked Parcels ({parcelCount}):</div>
                                      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                                        {parcels.map((p, pIdx) => (
                                          <div key={pIdx} className="flex items-center gap-3 text-xs bg-white rounded px-3 py-2 border border-slate-200">
                                            <span className="font-mono text-blue-700 font-semibold">{p.tracking}</span>
                                            <span className="text-slate-600 truncate flex-1">{p.customer || '-'}</span>
                                            <span className="text-green-600 font-medium whitespace-nowrap">₹{(p.invoice || p.amount || 0).toLocaleString()}</span>
                                            <span className="text-slate-400 whitespace-nowrap">{formatDMY(p.date)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Delete Advance Payment Dialog (admin only, password-confirmed) */}
      <Dialog open={!!deleteAdvanceTarget} onOpenChange={(open) => { if (!open) { setDeleteAdvanceTarget(null); setDeleteAdvancePassword(""); setDeleteAdvanceReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-advance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Delete Advance Payment
            </DialogTitle>
            <DialogDescription>
              This will permanently remove the advance from all uploaded ledger sheets. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteAdvanceTarget && (
            <div className="space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm space-y-1">
                <div><span className="text-slate-600">Ref:</span> <span className="font-mono font-semibold text-red-700">{deleteAdvanceTarget.ref}</span></div>
                <div><span className="text-slate-600">Customer:</span> <span className="font-medium">{deleteAdvanceTarget.customer || '-'}</span></div>
                <div><span className="text-slate-600">Partner:</span> <span className="font-medium">{deleteAdvanceTarget.partner_code || '-'}</span></div>
                <div><span className="text-slate-600">Amount:</span> <span className="font-medium text-green-700">₹{(deleteAdvanceTarget.amount || 0).toLocaleString()}</span></div>
                <div><span className="text-slate-600">Linked parcels:</span> <span className="font-medium">{(deleteAdvanceTarget.parcels || []).length}</span></div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Confirm with your password <span className="text-red-600">*</span></label>
                <Input
                  type="password"
                  value={deleteAdvancePassword}
                  onChange={(e) => setDeleteAdvancePassword(e.target.value)}
                  placeholder="Your login password"
                  autoFocus
                  data-testid="delete-advance-password-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && deleteAdvancePassword && !deleteAdvanceLoading) handleDeleteAdvance(); }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Reason (optional)</label>
                <Textarea
                  rows={2}
                  value={deleteAdvanceReason}
                  onChange={(e) => setDeleteAdvanceReason(e.target.value)}
                  placeholder="e.g. Stale entry from deleted 2025 data"
                  data-testid="delete-advance-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteAdvanceTarget(null); setDeleteAdvancePassword(""); setDeleteAdvanceReason(""); }}
              disabled={deleteAdvanceLoading}
              data-testid="delete-advance-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAdvance}
              disabled={deleteAdvanceLoading || !deleteAdvancePassword}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-advance-confirm-btn"
            >
              {deleteAdvanceLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" />Delete Permanently</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Original Ledger View - UNTOUCHED */}
      {mainView === "ledger" && (
        <>
          {viewLevel !== "summary" && renderBreadcrumb()}
      {viewLevel === "summary" && renderFilterPanel()}
      <SummaryCards data={viewLevel === "summary" ? summaryData?.summary : (clientDetails?.account_view_totals ? {
        total_debit: (clientDetails.account_view_totals.total_debit || 0) + (clientDetails.account_view_totals.total_gp_self || 0),
        // UNIFIED credit — same figure as the summary list, Ledger (Costing)
        // and Sales & Profit. GP-to-partner is NOT mixed into this chip.
        total_credit: (clientDetails.account_view_totals.total_credit || 0),
        balance: ((clientDetails.account_view_totals.total_debit || 0) + (clientDetails.account_view_totals.total_gp_self || 0)) - (clientDetails.account_view_totals.total_credit || 0),
        // Ledger (Sales): balance chip runs on SALES − CREDIT
        total_sales_balance: clientDetails.account_view_totals.sales_balance ?? ((clientDetails.account_view_totals.total_sale || 0) - (clientDetails.account_view_totals.total_credit || 0)),
        total_costing: clientDetails.account_view_totals.total_debit || 0,
        total_gp_self: clientDetails.account_view_totals.total_gp_self || 0
      } : clientDetails?.summary)} />

      {/* Notifications Button - Show when viewing client details */}
      {selectedClient && viewLevel !== "summary" && viewLevel !== "notifications" && processedTrackingData.notifications?.length > 0 && (
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={() => setViewLevel("notifications")}
            className="gap-2 border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
          >
            <AlertTriangle className="w-4 h-4" />
            View Notifications ({processedTrackingData.notifications.length})
            <span className="text-xs text-amber-600 ml-2">
              (Shifted, Cancelled, Date Issues)
            </span>
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && (
        <>
          {viewLevel === "summary" && renderSummaryTable()}
          {viewLevel === "notifications" && renderNotificationsTable()}
          {(viewLevel === "year" || viewLevel === "month" || viewLevel === "day") && renderHierarchyTable()}
          {viewLevel === "parcels" && renderParcelsTable()}
        </>
      )}

      {/* Pagination Controls for Summary View */}
      {viewLevel === "summary" && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
          <div className="text-sm text-slate-500">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} clients
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newPage = 1;
                setCurrentPage(newPage);
                fetchSummaryData(newPage);
              }}
              disabled={currentPage === 1 || loading}
              className="h-8 px-2"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                fetchSummaryData(newPage);
              }}
              disabled={currentPage === 1 || loading}
              className="h-8 px-3"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Prev</span>
            </Button>
            <span className="text-sm font-medium px-3 py-1 bg-slate-100 rounded">
              Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                fetchSummaryData(newPage);
              }}
              disabled={currentPage >= Math.ceil(totalCount / pageSize) || loading}
              className="h-8 px-3"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const lastPage = Math.ceil(totalCount / pageSize);
                setCurrentPage(lastPage);
                fetchSummaryData(lastPage);
              }}
              disabled={currentPage >= Math.ceil(totalCount / pageSize) || loading}
              className="h-8 px-2"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
            <Select
              value={pageSize.toString()}
              onValueChange={(val) => {
                setPageSize(parseInt(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="150">150</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="9999">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {viewLevel !== "summary" && (
        <div className="mt-4 text-sm text-slate-500 text-center">
          {getCurrentData().length} items
        </div>
      )}

      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              Print Options
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">{getPrintTitle()}</p>
              <p className="text-xs text-blue-600 mt-1">
                {viewLevel === "summary" ? filteredClients.length : getCurrentData().length} items will be printed
              </p>
            </div>
            
            {/* Include detailed parcels option - only show if not on parcels view */}
            {viewLevel !== "summary" && viewLevel !== "parcels" && (
              <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <Checkbox 
                  id="include-details" 
                  checked={printIncludeDetails} 
                  onCheckedChange={setPrintIncludeDetails}
                />
                <div>
                  <Label htmlFor="include-details" className="text-sm font-medium text-purple-800 cursor-pointer">
                    Include Detailed Parcel List
                  </Label>
                  <p className="text-xs text-purple-600">Print all {clientDetails?.tracking_verification?.length || 0} parcels for partner</p>
                </div>
              </div>
            )}
            
            {/* Column selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Select Columns to Print</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => toggleAllColumns(true)}>Select All</Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleAllColumns(false)}>Clear All</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg max-h-[200px] overflow-y-auto">
                {getCurrentColumnOptions().map(col => (
                  <div key={col.key} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`col-${col.key}`} 
                      checked={printColumns[col.key]} 
                      onCheckedChange={(checked) => setPrintColumns(prev => ({...prev, [col.key]: checked}))}
                    />
                    <Label htmlFor={`col-${col.key}`} className="text-sm cursor-pointer">{col.label}</Label>
                  </div>
                ))}
                
                {/* Show parcel columns if including details */}
                {printIncludeDetails && viewLevel !== "parcels" && (
                  <>
                    <div className="col-span-2 mt-2 pt-2 border-t">
                      <p className="text-xs font-medium text-slate-500 mb-2">Parcel Detail Columns:</p>
                    </div>
                    {columnOptions.parcels.map(col => (
                      <div key={`parcel-${col.key}`} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`parcel-col-${col.key}`} 
                          checked={printColumns[col.key]} 
                          onCheckedChange={(checked) => setPrintColumns(prev => ({...prev, [col.key]: checked}))}
                        />
                        <Label htmlFor={`parcel-col-${col.key}`} className="text-sm cursor-pointer">{col.label}</Label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowPrintDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handlePrint} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" /> Print Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Entries Dialog */}
      <Dialog open={showEntriesDialog} onOpenChange={setShowEntriesDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5 text-blue-600" />
              Manual Entries - {selectedClient}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {manualEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No manual entries found for this client
              </div>
            ) : (
              <div className="max-h-[400px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-semibold">Date</th>
                      <th className="p-2 text-center font-semibold">Type</th>
                      <th className="p-2 text-right font-semibold">Amount</th>
                      <th className="p-2 text-left font-semibold">Narration</th>
                      <th className="p-2 text-center font-semibold">Status</th>
                      <th className="p-2 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualEntries.map((entry) => (
                      <tr key={entry.id} className="border-b hover:bg-slate-50">
                        <td className="p-2">{formatDMY(entry.date)}</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            entry.entry_type === "take" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                          }`}>
                            {entry.entry_type === "take" ? "DEBIT" : "CREDIT"}
                          </span>
                        </td>
                        <td className={`p-2 text-right font-semibold ${
                          entry.entry_type === "take" ? "text-red-600" : "text-green-600"
                        }`}>
                          ₹{entry.amount?.toLocaleString()}
                        </td>
                        <td className="p-2 text-slate-600 max-w-[200px] truncate" title={entry.narration}>
                          {entry.narration}
                        </td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            entry.status === "approved" ? "bg-green-100 text-green-700" :
                            entry.status === "rejected" ? "bg-red-100 text-red-700" :
                            "bg-yellow-100 text-yellow-700"
                          }`}>
                            {entry.status}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEntry(entry)}
                              className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setShowEntriesDialog(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog 
        open={showManualEntryDialog} 
        onOpenChange={setShowManualEntryDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualEntryType === "take" ? (
                <><DollarSign className="w-5 h-5 text-red-600" />{editingEntryId ? "Edit" : "Add"} Debit Entry (Take)</>
              ) : (
                <><DollarSign className="w-5 h-5 text-green-600" />{editingEntryId ? "Edit" : "Add"} Credit Entry (Give)</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={`p-3 rounded-lg ${manualEntryType === "take" ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
              <p className={`text-sm ${manualEntryType === "take" ? "text-red-700" : "text-green-700"}`}>
                {manualEntryType === "take" ? "Recording amount to collect from client" : "Recording payment received from client"}
              </p>
            </div>
            
            {/* Client Code Field */}
            <div>
              <Label className="text-sm font-medium">Client Code *</Label>
              {selectedClient && !editingEntryId ? (
                <div className="mt-1 p-2 bg-slate-100 rounded border text-sm font-semibold">
                  {selectedClient}
                  <input type="hidden" value={selectedClient} />
                </div>
              ) : (
                <div className="mt-1">
                  <Select 
                    value={manualEntryClientCode} 
                    onValueChange={setManualEntryClientCode}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client code" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {summaryData?.clients?.map(client => (
                        <SelectItem key={client.client_code} value={client.client_code}>
                          {client.client_code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-sm font-medium">Amount (₹) *</Label>
              <Input type="number" placeholder="Enter amount" value={manualEntryAmount} onChange={(e) => setManualEntryAmount(e.target.value)} className="mt-1" />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Date *</Label>
              <DMYDateInput value={manualEntryDate} onChange={(e) => setManualEntryDate(e.target.value)} className="mt-1" />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Narration/Description *</Label>
              <Input placeholder="Enter description" value={manualEntryNarration} onChange={(e) => setManualEntryNarration(e.target.value)} className="mt-1" />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Tracking Number (Optional)</Label>
              <Input placeholder="Enter tracking number if applicable" value={manualEntryTracking} onChange={(e) => setManualEntryTracking(e.target.value)} className="mt-1" />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowManualEntryDialog(false); resetManualEntryForm(); }} className="flex-1">Cancel</Button>
              <Button onClick={handleCreateManualEntry} className={`flex-1 ${manualEntryType === "take" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}>
                {editingEntryId ? "Update" : (manualEntryType === "take" ? "Add Debit" : "Add Credit")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Export Ledger Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Export Format Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Export Format</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setExportFormat("excel")}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    exportFormat === "excel" 
                      ? "border-green-500 bg-green-50 text-green-700" 
                      : "border-slate-200 hover:border-green-300 hover:bg-green-50/50"
                  }`}
                >
                  <FileSpreadsheet className="w-8 h-8" />
                  <span className="font-medium">Excel (.xlsx)</span>
                  <span className="text-xs text-slate-500">Best for data analysis</span>
                </button>
                <button
                  onClick={() => setExportFormat("pdf")}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    exportFormat === "pdf" 
                      ? "border-red-500 bg-red-50 text-red-700" 
                      : "border-slate-200 hover:border-red-300 hover:bg-red-50/50"
                  }`}
                >
                  <FileText className="w-8 h-8" />
                  <span className="font-medium">PDF</span>
                  <span className="text-xs text-slate-500">Best for printing</span>
                </button>
              </div>
            </div>

            {/* Export Scope Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Export Scope</Label>
              <Select value={exportScope} onValueChange={setExportScope}>
                <SelectTrigger>
                  <SelectValue placeholder="Select what to export" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current View ({viewLevel === "summary" ? `${filteredClients.length} accounts` : `${getCurrentData().length} items`})</SelectItem>
                  <SelectItem value="all_clients">Sales Team Summary</SelectItem>
                  {selectedClient && (
                    <SelectItem value="selected_client">Full Report: {selectedClient} (All Years/Months/Days)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Date Range</Label>
              <Select value={exportDateRange} onValueChange={setExportDateRange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="year">Last 365 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {exportDateRange === "custom" && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">From Date</Label>
                  <DMYDateInput value={exportDateFrom} onChange={(e) => setExportDateFrom(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To Date</Label>
                  <DMYDateInput value={exportDateTo} onChange={(e) => setExportDateTo(e.target.value)} className="mt-1" />
                </div>
              </div>
            )}

            {/* Include Parcel Details Option */}
            {(exportScope === "selected_client" || (exportScope === "current" && viewLevel !== "parcels" && viewLevel !== "summary")) && (
              <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <Checkbox 
                  id="export-include-details" 
                  checked={exportIncludeDetails} 
                  onCheckedChange={setExportIncludeDetails}
                />
                <div>
                  <Label htmlFor="export-include-details" className="text-sm font-medium text-purple-800 cursor-pointer">
                    Include Parcel-Level Details
                  </Label>
                  <p className="text-xs text-purple-600">
                    {exportFormat === "excel" 
                      ? "Adds separate sheet with all individual parcels" 
                      : "Adds detailed parcel listing pages"
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Export Preview */}
            <div className="bg-slate-100 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-700 mb-1">Export Preview:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Format: <span className="font-medium">{exportFormat === "excel" ? "Excel (.xlsx)" : "PDF"}</span></li>
                <li>• Scope: <span className="font-medium">
                  {exportScope === "current" ? "Current View" : 
                   exportScope === "all_clients" ? "Sales Team Summary" : 
                   `Full Report for ${selectedClient}`}
                </span></li>
                <li>• Date Range: <span className="font-medium">
                  {exportDateRange === "all" ? "All Time" :
                   exportDateRange === "today" ? "Today" :
                   exportDateRange === "week" ? "Last 7 Days" :
                   exportDateRange === "month" ? "Last 30 Days" :
                   exportDateRange === "year" ? "Last 365 Days" :
                   `${exportDateFrom || "..."} to ${exportDateTo || "..."}`}
                </span></li>
                {exportIncludeDetails && <li>• <span className="font-medium text-purple-600">Including parcel details</span></li>}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowExportDialog(false)} className="flex-1" disabled={exporting}>
                Cancel
              </Button>
              <Button 
                onClick={handleExport} 
                className={`flex-1 gap-2 ${exportFormat === "excel" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export {exportFormat === "excel" ? "Excel" : "PDF"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={showPdfPreview} onOpenChange={setShowPdfPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-bold text-blue-800 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  PDF Preview - {printSettings?.companyName || "EAC"}
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Review your report before downloading or printing
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePrintFromPreview}
                  variant="outline"
                  className="gap-2"
                  data-testid="pdf-preview-print-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
                <Button
                  onClick={handleDownloadPDF}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="pdf-preview-download-btn"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* Preview Content */}
          <div className="flex-1 overflow-auto bg-slate-100 p-4 rounded-lg mt-4">
            {pdfPreviewData && (
              <div className="bg-white shadow-lg rounded-lg mx-auto max-w-5xl">
                {/* Report Header */}
                <div 
                  className="text-center py-6 px-8 rounded-t-lg"
                  style={{ backgroundColor: printSettings?.headerColor || '#1e40af' }}
                >
                  <h1 className="text-2xl font-bold text-white mb-1">
                    {printSettings?.companyName || "EAC"}
                  </h1>
                  <h2 className="text-lg text-white/90">
                    Ledger Reconciliation Report
                  </h2>
                  <p className="text-sm text-white/75 mt-2">
                    {viewLevel === "summary" ? "Sales Team Summary" : selectedClient ? `Account: ${selectedClient}` : ""}
                    {selectedYear ? ` | Year: ${selectedYear}` : ""}
                    {selectedMonth ? ` | Month: ${getMonthName(selectedMonth)}` : ""}
                    {" | Generated: " + new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                
                {/* Summary Cards */}
                <div className="flex justify-center gap-4 py-6 px-8 bg-slate-50 border-b">
                  <div className="bg-white border rounded-lg px-6 py-3 text-center shadow-sm min-w-[120px]">
                    <p className="text-xs text-slate-500 uppercase font-medium">Records</p>
                    <p className="text-xl font-bold text-slate-800">{pdfPreviewData.data?.length || 0}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg px-6 py-3 text-center shadow-sm min-w-[120px]">
                    <p className="text-xs text-red-600 uppercase font-medium">Total Debit</p>
                    <p className="text-xl font-bold text-red-700">
                      ₹{(pdfPreviewData.summaryInfo?.total_debit || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg px-6 py-3 text-center shadow-sm min-w-[120px]">
                    <p className="text-xs text-green-600 uppercase font-medium">Total Credit</p>
                    <p className="text-xl font-bold text-green-700">
                      ₹{(pdfPreviewData.summaryInfo?.total_credit || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-6 py-3 text-center shadow-sm min-w-[120px]">
                    <p className="text-xs text-amber-600 uppercase font-medium">Balance</p>
                    <p className="text-xl font-bold text-amber-700">
                      ₹{(pdfPreviewData.summaryInfo?.total_balance || pdfPreviewData.summaryInfo?.balance || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                
                {/* Data Table Preview */}
                <div className="p-6 overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr style={{ backgroundColor: printSettings?.headerColor || '#1e40af' }}>
                        {viewLevel === "summary" ? (
                          <>
                            <th className="text-left text-white font-semibold py-3 px-4 border-b-2">Client Code</th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Orders</th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Sales</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Costing</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">GP Self</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Debit</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Credit</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Balance</th>
                          </>
                        ) : viewLevel === "parcels" ? (
                          <>
                            <th className="text-left text-white font-semibold py-3 px-4 border-b-2">Tracking</th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Date</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Costing</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Sell Price</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">GP</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">GP Self</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Payment</th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Status</th>
                          </>
                        ) : (
                          <>
                            <th className="text-left text-white font-semibold py-3 px-4 border-b-2">
                              {viewLevel === "year" ? "Year" : viewLevel === "month" ? "Month" : "Day"}
                            </th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Parcels</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Costing</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Sell Price</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">GP</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">GP Self</th>
                            <th className="text-right text-white font-semibold py-3 px-4 border-b-2">Payment</th>
                            <th className="text-center text-white font-semibold py-3 px-4 border-b-2">Errors</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {pdfPreviewData.data?.slice(0, 15).map((item, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          {viewLevel === "summary" ? (
                            <>
                              <td className="py-2 px-4 font-semibold text-blue-700 border-b">{item.client_code}</td>
                              <td className="py-2 px-4 text-center border-b">{item.order_count || 0}</td>
                              <td className="py-2 px-4 text-center border-b">{item.sales_count || 0}</td>
                              <td className="py-2 px-4 text-right font-mono border-b">₹{(item.total_costing || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono text-purple-600 border-b">₹{(item.total_gp_self || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono text-red-600 border-b">₹{(item.total_debit || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono text-green-600 border-b">₹{(item.total_credit || 0).toLocaleString('en-IN')}</td>
                              <td className={`py-2 px-4 text-right font-mono font-bold border-b ${item.balance > 0 ? 'text-amber-600' : item.balance < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                                ₹{(item.balance || 0).toLocaleString('en-IN')}
                              </td>
                            </>
                          ) : viewLevel === "parcels" ? (
                            <>
                              <td className="py-2 px-4 font-mono text-blue-700 border-b">{item.tracking}</td>
                              <td className="py-2 px-4 text-center border-b">{formatDMY(item.date)}</td>
                              <td className="py-2 px-4 text-right font-mono border-b">₹{(item.costing || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono border-b">₹{(item.sell_price || 0).toLocaleString('en-IN')}</td>
                              <td className={`py-2 px-4 text-right font-mono border-b ${item.gp < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                ₹{(item.gp || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 px-4 text-right font-mono text-purple-600 border-b">₹{(item.gp_self || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono text-green-600 border-b">₹{(item.payment_received || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-center border-b">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  item.status === 'error' ? 'bg-red-100 text-red-700' : 
                                  item.status === 'settled' ? 'bg-green-100 text-green-700' : 
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {item.status || 'Pending'}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-2 px-4 font-semibold text-blue-700 border-b">
                                {viewLevel === "year" ? item.year : viewLevel === "month" ? getMonthName(item.month) : `Day ${item.day}`}
                              </td>
                              <td className="py-2 px-4 text-center border-b">{item.count || 0}</td>
                              <td className="py-2 px-4 text-right font-mono border-b">₹{(item.total_costing || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono border-b">₹{(item.total_sell || 0).toLocaleString('en-IN')}</td>
                              <td className={`py-2 px-4 text-right font-mono border-b ${item.total_gp < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                ₹{(item.total_gp || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 px-4 text-right font-mono text-purple-600 border-b">₹{(item.total_gp_self || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-right font-mono text-green-600 border-b">₹{(item.total_payment || 0).toLocaleString('en-IN')}</td>
                              <td className="py-2 px-4 text-center border-b">
                                {item.error_count > 0 ? (
                                  <span className="text-red-600 font-semibold">⚠ {item.error_count}</span>
                                ) : (
                                  <span className="text-green-600">✓</span>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {pdfPreviewData.data?.length > 15 && (
                    <p className="text-center text-sm text-slate-500 mt-4 italic">
                      Showing first 15 of {pdfPreviewData.data.length} records. Full data will be included in PDF download.
                    </p>
                  )}
                </div>
                
                {/* Footer */}
                <div className="px-6 py-4 bg-slate-100 rounded-b-lg border-t flex justify-between text-xs text-slate-500">
                  <span>Confidential - For Internal Use Only</span>
                  <span>{printSettings?.companyName || "EAC"} Business Management System</span>
                  <span>Page 1 of 1</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer Actions */}
          <div className="border-t pt-4 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              <span className="font-medium">Tip:</span> Click &quot;Download PDF&quot; for a high-quality PDF file, or &quot;Print&quot; to send directly to your printer.
            </p>
            <Button variant="outline" onClick={() => setShowPdfPreview(false)}>
              Close Preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Bulk Edit Payment Mode
            </DialogTitle>
            <DialogDescription>
              Apply a payment mode to all selected entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Payment Mode</Label>
              <Select value={bulkEditMode} onValueChange={setBulkEditMode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment mode..." />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map(mode => (
                    <SelectItem key={mode.value} value={mode.value}>
                      <span className={mode.isCompany ? "text-green-700" : "text-red-700"}>
                        {mode.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-slate-100 rounded-lg p-3 text-sm text-slate-600">
              <p>This will update the payment mode for all selected entries across all partners.</p>
              <p className="mt-1 font-medium">
                Selected entries: {Object.values(selectedPartnerEntries).reduce((sum, partner) => 
                  sum + Object.values(partner || {}).filter(Boolean).length, 0
                )}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkEdit} disabled={!bulkEditMode}>
              Apply to Selected
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditLogDialog} onOpenChange={setShowAuditLogDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5 text-slate-600" />
              Edit Audit Log
            </DialogTitle>
            <DialogDescription>
              Track all changes made to ledger entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {auditLogsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No edits have been made yet
              </div>
            ) : (
              <div className="space-y-2">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{log.partner_code} - {log.tracking || `Entry #${log.entry_index}`}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(log.edited_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-slate-600">Field:</span> <span className="font-medium">{log.field}</span>
                    </div>
                    <div className="text-sm flex items-center gap-2">
                      <span className="text-red-600 line-through">{log.old_value || "(empty)"}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-green-600 font-medium">{log.new_value}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      By: {log.edited_by} ({log.edited_by_role})
                      {log.bulk_edit && <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Bulk Edit</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAuditLogDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              Reject Entries
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting the selected entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={3}
              />
            </div>
            
            <div className="bg-red-50 rounded-lg p-3 text-sm text-red-600">
              <p>Rejected entries will be sent back to the Accounts team for review.</p>
              <p className="mt-1 font-medium">
                Entries to reject: {Object.values(selectedPartnerEntries).reduce((sum, partner) => 
                  sum + Object.values(partner || {}).filter(Boolean).length, 0
                )}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleApproveEntries("reject")}
              disabled={!rejectReason.trim()}
            >
              Reject Selected
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Collections Dialog */}
      <Dialog open={showPartnerCollections} onOpenChange={setShowPartnerCollections}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              <Wallet className="w-5 h-5" />
              Partner Collections
              <Badge variant="outline" className="ml-2 bg-cyan-50 text-cyan-700 border-cyan-200">Info Only</Badge>
            </DialogTitle>
            <DialogDescription>
              Invoice payments received by partners from customers. This view is for <strong>information and invoice matching</strong> only - it does NOT affect the ledger balance calculation.
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center pb-4 border-b">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Partner:</Label>
              <Input
                placeholder="Filter by partner code"
                className="w-40"
                value={selectedClient || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  fetchPartnerCollections(val || null);
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Status:</Label>
              <Select value={collectionsFilter} onValueChange={(val) => {
                setCollectionsFilter(val);
                fetchPartnerCollections(selectedClient);
              }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                  <SelectItem value="unmatched">Unmatched</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchPartnerCollections(selectedClient)}
              className="gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${collectionsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* Summary Cards */}
          {partnerCollections?.summary && (
            <div className="grid grid-cols-5 gap-3 py-4 border-b">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-xs text-slate-500 uppercase font-medium">Total Collections</div>
                <div className="text-lg font-bold text-slate-700">{partnerCollections.summary.total_collections}</div>
              </div>
              <div className="bg-cyan-50 rounded-lg p-3 text-center">
                <div className="text-xs text-cyan-600 uppercase font-medium">Invoice Amount</div>
                <div className="text-lg font-bold text-cyan-700">₹{partnerCollections.summary.total_invoice_amount?.toLocaleString()}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-xs text-green-600 uppercase font-medium">To Company</div>
                <div className="text-lg font-bold text-green-700">₹{partnerCollections.summary.total_payment_to_company?.toLocaleString()}</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <div className="text-xs text-emerald-600 uppercase font-medium">Matched</div>
                <div className="text-lg font-bold text-emerald-700">{partnerCollections.summary.matched_count}</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <div className="text-xs text-amber-600 uppercase font-medium">Pending Settlement</div>
                <div className="text-lg font-bold text-amber-700">₹{partnerCollections.summary.pending_settlement?.toLocaleString()}</div>
              </div>
            </div>
          )}
          
          {/* Collections Table */}
          <div className="flex-1 overflow-auto">
            {collectionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                <span className="ml-2 text-slate-500">Loading collections...</span>
              </div>
            ) : partnerCollections?.collections?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold">Tracking</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Partner</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Order Date</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Collection Date</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-cyan-700">Invoice Amt</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Costing</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-green-700">GP</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-blue-700">To Company</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerCollections.collections.map((coll, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-blue-600">{coll.tracking}</td>
                      <td className="px-3 py-2 font-medium">{coll.partner_code}</td>
                      <td className="px-3 py-2 text-slate-600">{coll.customer_name || '-'}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{formatDMY(coll.order_date)}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{coll.collection_date || '-'}</td>
                      <td className="px-3 py-2 text-right text-cyan-700 font-medium bg-cyan-50/50">₹{coll.invoice_amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-slate-600">₹{coll.costing?.toLocaleString()}</td>
                      <td className={`px-3 py-2 text-right font-medium ${coll.gp < 0 ? 'text-red-600' : 'text-green-600'}`}>₹{coll.gp?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-blue-700 font-medium">₹{coll.payment_to_company?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">
                        {coll.matched_status === 'matched' ? (
                          <Badge className="bg-green-100 text-green-700 gap-1">
                            <CheckCircle className="w-3 h-3" /> Matched
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1">
                            Unmatched
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No partner collections found</p>
                <p className="text-sm">Upload partner ledger files to track invoice payments</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-between pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={exportPartnerCollectionsToExcel}
              disabled={!partnerCollections?.collections?.length}
              className="gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Download className="w-4 h-4" />
              Export to Excel
            </Button>
            <Button variant="outline" onClick={() => setShowPartnerCollections(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* Simple Debit Entry Dialog - OUTSIDE loading condition */}

      {/* Account Print Column Selection Dialog - Must be OUTSIDE mainView conditionals */}
      <Dialog open={showAccountPrintDialog} onOpenChange={setShowAccountPrintDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Printer className="w-5 h-5 text-emerald-600" />
              Print - Select Columns
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Choose columns and options for print view
            </p>
          </DialogHeader>
          <div className="py-2">
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { key: 'date', label: 'Date' },
                { key: 'tracking', label: 'Particulars' },
                { key: 'customer', label: 'Customer' },
                { key: 'ref', label: 'Payment Ref' },
                { key: 'sale', label: 'Sale' },
                { key: 'gp', label: 'GP' },
                { key: 'gp_self', label: `Self (${accountViewData?.gp_columns?.find(c => c.key === 'gp_self')?.percentage || 50}%)` },
                { key: 'gp_partner', label: `${accountViewData?.gp_columns?.find(c => c.key === 'gp_to_partner')?.name || 'Partner'} (${accountViewData?.gp_columns?.find(c => c.key === 'gp_to_partner')?.percentage || 50}%)` },
                { key: 'debit', label: 'Debit' },
                { key: 'credit', label: 'Credit' },
                { key: 'coll', label: 'Collection' },
                { key: 'balance', label: 'Balance' }
              ].map(col => (
                <label 
                  key={col.key} 
                  className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1.5 rounded-md border border-slate-100"
                  data-testid={`print-col-${col.key}`}
                >
                  <Checkbox
                    checked={accountPrintColumns[col.key]}
                    onCheckedChange={(checked) => setAccountPrintColumns(prev => ({ ...prev, [col.key]: checked }))}
                  />
                  <span className="text-xs text-slate-700">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
              <Button 
                size="sm" variant="ghost" className="text-xs flex-1 h-8"
                onClick={() => setAccountPrintColumns({
                  date: true, tracking: true, customer: true, ref: true, sale: true,
                  gp: true, gp_self: true, gp_partner: true, debit: true, credit: true, coll: true, balance: true
                })}
              >
                Select All
              </Button>
              <Button 
                size="sm" variant="ghost" className="text-xs flex-1 h-8"
                onClick={() => setAccountPrintColumns({
                  date: false, tracking: false, customer: false, ref: false, sale: false,
                  gp: false, gp_self: false, gp_partner: false, debit: false, credit: false, coll: false, balance: false
                })}
              >
                Deselect All
              </Button>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Print Options</p>
              <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1.5 rounded-md border border-slate-100" data-testid="print-hide-details">
                <Checkbox
                  checked={accountPrintHideDetails}
                  onCheckedChange={(checked) => setAccountPrintHideDetails(checked)}
                />
                <span className="text-xs text-slate-700">Hide Opening / Closing / Percentages</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1.5 rounded-md border border-slate-100" data-testid="print-hide-manual">
                <Checkbox
                  checked={accountPrintHideManual}
                  onCheckedChange={(checked) => setAccountPrintHideManual(checked)}
                />
                <span className="text-xs text-slate-700">Hide Manual Entries</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-200 sticky bottom-0 bg-white pb-1">
            <Button 
              variant="outline" 
              className="flex-1 h-10"
              onClick={() => setShowAccountPrintDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleAccountPrint}
              disabled={!Object.values(accountPrintColumns).some(v => v)}
              data-testid="print-confirm-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Open Print View
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDebitDialog} onOpenChange={setShowDebitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <DollarSign className="w-5 h-5" />
              Add Debit Entry
            </DialogTitle>
            <DialogDescription>
              Record amount to collect from client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client Code *</Label>
              <Select value={debitClientCode} onValueChange={setDebitClientCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client code" />
                </SelectTrigger>
                <SelectContent>
                  {summaryData?.clients?.map((client) => (
                    <SelectItem key={client.client_code} value={client.client_code}>
                      {client.client_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={debitAmount}
                onChange={(e) => setDebitAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date</Label>
              <DMYDateInput value={debitDate} onChange={(e) => setDebitDate(e.target.value)} />
            </div>
            
            <div className="space-y-2">
              <Label>Narration</Label>
              <Input
                value={debitNarration}
                onChange={(e) => setDebitNarration(e.target.value)}
                placeholder="Enter description"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDebitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDebit} className="bg-red-600 hover:bg-red-700 text-white">
              Add Debit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LedgerReconciliationPage;
